import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Button, Tabs, Tab, Spinner, Dropdown, Form } from 'react-bootstrap';
import axios from 'axios';
import useTheme from '../hooks/useTheme.ts';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import mqtt from 'mqtt';
import { toast } from 'react-toastify';
import ConcentrationModal from './ConcentrationModal.tsx';
import CopyToStagesModal from './CopyToStagesModal.tsx';
import styles from './TreatmentModal.module.css';

interface TreatmentModalProps {
  show: boolean;
  onHide: () => void;
  stageId: number | null;
  wellId: number | null;
  stageNumber: number | null;
  isRealTimeStreaming?: boolean;
  currentStep?: number | null;
  onTreatmentScheduleUpdate?: (optimisticUpdate?: any[]) => void;
  useFakeData?: boolean;
}

// Treatment type options for dropdown
const treatmentTypeOptions = [
  'Acid',
  'Acid Spearhead',
  'Breakdown',
  'Displacement',
  'Diverter',
  'FET',
  'Flush',
  'Load',
  'Overflush',
  'Pad',
  'Pre-Flush',
  'Pre-Pad',
  'Proppant',
  'Pump-in',
  'Pumpdown',
  'Rate Drop',
  'Rate Drop-Proppant',
  'Rate Drop-Sand Ramp',
  'Sand Ramp',
  'Shutdown',
  'Spacer',
  'Sweep'
];

// Description dictionary for proppant types and their corresponding values
const descriptionDictionary: { [key: string]: number } = {
  '30/70 White': 22.1,
  '30/80 FracBlack HT': 22.6,
  '35/140H Interprop': 26.688,
  '40/70 Black Pro': 22.3,
  '40/70 CarboLite': 22.601,
  '40/70 CarboProp': 27.272,
  '40/70 CRC': 20.767,
  '40/70 CRC - C': 21.017,
  '40/70 EconoProp': 22.518,
  '40/70 El-Prop': 22.6,
  '40/70 Interprop': 26.688,
  '40/70 MG Light': 22.601,
  '40/70 OilPlus': 21.7,
  '40/70 Pearl CRC': 22.6,
  '40/70 PowerProp': 20.683,
  '40/70 PRC': 20.767,
  '40/70 PRC Premium': 21.267,
  '40/70 Prime Plus': 21.7,
  '40/70 Super DC': 21.267,
  '40/70 Super LC': 21.35,
  '40/70 Tempered HS': 21.568,
  '40/70 TG': 22.1,
  '40/70 ValuBond': 21.767,
  '40/70 White': 22.1,
  '40/80 Bauxite (St. Gobain)': 29.19,
  '40/80 BauxLite': 26.688,
  '40/80 HydroProp': 21.267,
  '40/80 Interprop': 26.688,
  '40/80 SinterLite': 27.772,
  '20/40 GGL G2': 25.02,
  'GreenProp': 20.93,
  'SinterBall Lock': 27.939,
  'SinterBlast': 30.2,
  'SpearProp': 20.93,
  'Ultraprop': 29.19,
  'VersaLite': 25.02,
  'Versaprop': 26.688,
  '12/20 Brady': 22.1,
  '16/30 Brady': 22.1,
  '16/30 PC': 21.43,
  '20/40 Brady': 22.1,
  '20/40 CRC - E': 21.434,
  '20/40 PC': 21.43,
  '20/40 PPC': 21.43,
  '30/50 Brady': 22.1,
  '30/50 CRC - E': 21.434,
  '30/50 PC': 21.43,
  '30/50 PPC': 21.43,
  '30/70 Brady': 22.1,
  '40/70 Brady': 22.1,
  '40/70 Chieftain': 22.1,
  '40/70 CRC - E': 21.434,
  '40/70 PC': 21.43,
  '40/70 PPC': 21.43,
  '40/70 Optiprop-G2': 21.184,
  '16/30 OilPlus': 21.8,
  '20/40 Black Pro': 21.3,
  '30/50 Black Ultra': 21.3,
  '30/50 LW Manticore': 22.685,
  '30/50 Optiprop-G2': 21.184,
  '40/70 Garnet CRC': 22.6,
  '20/40 INNOPROP CR': 21.35,
  '20/40 INNOPROP PR': 21.35,
  '30/50 SSP': 22.1,
  'CarboNRT': 22.518,
  '100 SSP': 20.85,
  '40/70 SSP': 20.85,
  '16/30 CoolSet': 21.433,
  '20/40 CoolSet': 21.433,
  '30/50 CoolSet': 21.433,
  '40/70 CoolSet': 21.433,
  '30/50 P-1000': 22.018,
  '100 Mesh Native': 21.684,
  '40/70 Native': 21.767,
  '30/50 Native': 21.934,
  '20/40 Native': 21.767,
  '20/40 Trueset': 21.684,
  '30/50 Trueset': 21.684,
  '40/70 Trueset': 21.684,
  '30/70 Native': 21.767,
  '100 Mesh Arizona': 21.684,
  '20/40 Arizona': 21.767,
  '30/50 Arizona': 21.934,
  '40/70 Arizona': 21.767,
  '200 Mesh': 22.101,
  '40/70 TEMPERED LC': 21.434,
  '40/70 CRC-LT': 21.295,
  '30/50 CRC-LT': 21.267,
  '40/70 FloPro': 22.101,
  '40/70 Genoa': 21.851,
  '100 MESH FLO PRO': 20.85,
  '100 Mesh Brady': 22.101,
  '100 MESH TURBOPROP': 22.1,
  '100 Mesh Chrome': 21.35,
  '100 Super LC': 21.267,
  '40/70 Durasand': 21.934,
  '40/140 NATIVE': 21.35,
  'CS - 100 Mesh TG': 22.1,
  '100 Mesh TG': 22.1,
  '100 Mesh White': 22.1,
  '12/18 CarboHSP': 29.69,
  '12/18 CarboLite': 22.601,
  '12/18 El-Prop': 22.6,
  '12/18 Interprop': 26.688,
  '12/18 SinterLite': 27.021,
  '12/20 TG': 22.1,
  '12/20 White': 22.1,
  '14/20 SinterBall': 29.774,
  '14/40 FracBlack HT': 22.6,
  '14/40 XRT Ceramax V': 25.1,
  '16/20 CarboHSP': 29.69,
  '16/20 CarboLite': 22.601,
  '16/20 El-Prop': 22.6,
  '16/30 Bauxite (St. Gobain)': 29.19,
  '16/30 BauxLite Plus': 25.02,
  '16/30 CarboHSP': 29.69,
  '16/30 CarboProp': 27.272,
  '16/30 CRC': 20.767,
  '16/30 CRC - C': 21.017,
  '16/30 Garnet CRC': 22.6,
  '16/30 Hyperprop-G2': 25.02,
  '16/30 Interprop': 26.688,
  '16/30 Optiprop-G2': 21.184,
  '16/30 SiberProp': 21.267,
  '16/30 SinterBall': 29.774,
  '16/30 SinterLite': 27.021,
  '16/30 Super LC': 21.35,
  '16/30 TG': 22.1,
  '16/30 White': 22.1,
  '16/50 Interprop': 26.688,
  '20/40 Bauxite (St. Gobain)': 29.19,
  '20/40 BauxLite': 26.688,
  '20/40 BauxLite Plus': 25.02,
  '20/40 Black Ultra': 22.27,
  '20/40 CarboBond Lite': 21.684,
  '20/40 CarboHSP': 29.69,
  '20/40 CarboLite': 22.601,
  '20/40 CarboProp': 27.272,
  '20/40 CRC': 21.434,
  '20/40 CRC - C': 21.017,
  '20/40 CRC-LT': 21.434,
  '20/40 EconoProp': 22.518,
  '20/40 El-Prop': 22.6,
  '20/40 Garnet CRC': 22.6,
  '20/40 Hyperprop-G2': 25.02,
  '20/40 Interprop': 26.688,
  '20/40 MG Light': 22.601,
  '20/40 MG Light RCP': 22.601,
  '20/40 OilPlus': 21.7,
  '20/40 Optiprop-G2': 21.184,
  '20/40 Pearl CRC': 22.6,
  '20/40 PowerProp': 20.683,
  '20/40 PR6000': 21.571,
  '20/40 PRC': 21.434,
  '20/40 PRC Premium': 21.267,
  '20/40 SB Excel': 21.6,
  '20/40 SB Prime': 21.9,
  '20/40 SiberProp': 21.267,
  '20/40 SinterBall': 29.857,
  '20/40 SinterLite': 27.522,
  '20/40 SinterLite Lock': 27.939,
  '20/40 Super DC': 21.267,
  '20/40 Super LC': 20.933,
  '20/40 Tempered HS': 20.433,
  '20/40 Tempered LC': 20.433,
  '20/40 TG': 22.1,
  '20/40 ValuBond': 21.767,
  '20/40 White': 22.1,
  '20/40 XRT Ceramax E': 20.9,
  '20/40 XRT Ceramax P': 28.6,
  '30/50 Bauxite (St. Gobain)': 29.19,
  '30/50 BauxLite': 26.688,
  '30/50 CarboBond Lite': 21.684,
  '30/50 CarboLite': 22.601,
  '30/50 CRC - C': 21.017,
  '30/50 EconoProp': 22.518,
  '30/50 El-Prop': 22.6,
  '30/50 Garnet CRC': 22.6,
  '30/50 Interprop': 26.688,
  '30/50 MG Light': 22.601,
  '30/50 OilPlus': 21.7,
  '30/50 Pearl CRC': 22.6,
  '30/50 PowerProp': 20.683,
  '30/50 PRC': 20.767,
  '30/50 PRC Premium': 21.267,
  '30/50 Prime Plus': 21.7,
  '30/50 Super DC': 21.267,
  '30/50 Super LC': 21.35,
  '30/50 Tempered HS': 20.266,
  '30/50 Tempered LC': 20.433,
  '30/50 TG': 22.1,
  '30/50 White': 22.1,
  '30/50 XRT Ceramax V': 25.1,
  '30/60 CarboHSP': 29.69,
  '30/60 CarboProp': 27.272,
  '30/60 SinterBall': 29.941,
  '30/60 SinterLite': 27.772,
  '30/70 TG': 22.1,
  'CS - 100 Mesh White': 22.1,
  'CS - 12/18 CarboHSP': 29.69,
  'CS - 12/18 CarboLite': 22.601,
  'CS - 12/18 El-Prop': 22.6,
  'CS - 12/18 Interprop': 26.688,
  'CS - 12/18 SinterLite': 27.021,
  'CS - 12/20 TG': 22.1,
  'CS - 12/20 White': 22.1,
  'CS - 14/20 SinterBall': 29.774,
  'CS - 14/40 FracBlack HT': 22.6,
  'CS - 14/40 XRT Ceramax V': 25.1,
  'CS - 16/20 CarboHSP': 29.69,
  'CS - 16/20 CarboLite': 22.601,
  'CS - 16/20 El-Prop': 22.6,
  'CS - 16/30 Bauxite (St. Gobain)': 29.19,
  'CS - 16/30 BauxLite Plus': 25.02,
  'CS - 16/30 CarboHSP': 29.69,
  'CS - 16/30 CarboProp': 27.272,
  'CS - 16/30 CRC': 20.767,
  'CS - 16/30 CRC - C': 21.017,
  'CS - 16/30 Garnet CRC': 22.6,
  'CS - 16/30 Hyperprop-G2': 25.02,
  'CS - 16/30 Interprop': 26.688,
  'CS - 16/30 Optiprop-G2': 21.184,
  'CS - 16/30 SiberProp': 21.267,
  'CS - 16/30 SinterBall': 29.774,
  'CS - 16/30 SinterLite': 27.021,
  'CS - 16/30 Super LC': 21.35,
  'CS - 16/30 TG': 22.1,
  'CS - 16/30 White': 22.1,
  'CS - 16/50 Interprop': 26.688,
  'CS - 20/40 Bauxite (St. Gobain)': 29.19,
  'CS - 20/40 BauxLite': 26.688,
  'CS - 20/40 BauxLite Plus': 25.02,
  'CS - 20/40 Black Ultra': 22.27,
  'CS - 20/40 CarboBond Lite': 21.684,
  'CS - 20/40 CarboHSP': 29.69,
  'CS - 20/40 CarboLite': 22.601,
  'CS - 20/40 CarboProp': 27.272,
  'CS - 20/40 CRC': 21.434,
  'CS - 20/40 CRC - C': 21.017,
  'CS - 20/40 CRC-LT': 21.017,
  'CS - 20/40 EconoProp': 22.518,
  'CS - 20/40 El-Prop': 22.6,
  'CS - 20/40 Garnet CRC': 22.6,
  'CS - 20/40 Hyperprop-G2': 25.02,
  'CS - 20/40 Interprop': 26.688,
  'CS - 20/40 MG Light': 22.601,
  'CS - 20/40 MG Light RCP': 22.601,
  'CS - 20/40 OilPlus': 21.7,
  'CS - 20/40 Optiprop-G2': 21.184,
  'CS - 20/40 Pearl CRC': 22.6,
  'CS - 20/40 PowerProp': 20.683,
  'CS - 20/40 PR6000': 21.571,
  'CS - 20/40 PRC': 21.434,
  'CS - 20/40 PRC Premium': 21.267,
  'CS - 20/40 SB Excel': 21.6,
  'CS - 20/40 SB Prime': 21.9,
  'CS - 20/40 SiberProp': 21.267,
  'CS - 20/40 SinterBall': 29.857,
  'CS - 20/40 SinterLite': 27.522,
  'CS - 20/40 SinterLite Lock': 27.939,
  'CS - 20/40 Super DC': 21.267,
  'CS - 20/40 Super LC': 20.933,
  'CS - 20/40 Tempered HS': 20.433,
  'CS - 20/40 Tempered LC': 20.433,
  'CS - 20/40 TG': 22.1,
  'CS - 20/40 ValuBond': 21.767,
  'CS - 20/40 White': 22.1,
  'CS - 20/40 XRT Ceramax E': 20.9,
  'CS - 20/40 XRT Ceramax P': 28.6,
  'CS - 30/50 Bauxite (St. Gobain)': 29.19,
  'CS - 30/50 BauxLite': 26.688,
  'CS - 30/50 CarboBond Lite': 21.684,
  'CS - 30/50 CarboLite': 22.601,
  'CS - 30/50 CRC - C': 21.017,
  'CS - 30/50 EconoProp': 22.518,
  'CS - 30/50 El-Prop': 22.6,
  'CS - 30/50 Garnet CRC': 22.6,
  'CS - 30/50 Interprop': 26.688,
  'CS - 30/50 MG Light': 22.601,
  'CS - 30/50 OilPlus': 21.7,
  'CS - 30/50 Pearl CRC': 22.6,
  'CS - 30/50 PowerProp': 20.683,
  'CS - 30/50 PRC': 20.767,
  'CS - 30/50 PRC Premium': 21.267,
  'CS - 30/50 Prime Plus': 21.7,
  'CS - 30/50 Super DC': 21.267,
  'CS - 30/50 Super LC': 21.35,
  'CS - 30/50 Tempered HS': 20.266,
  'CS - 30/50 Tempered LC': 20.433,
  'CS - 30/50 TG': 22.1,
  'CS - 30/50 White': 22.1,
  'CS - 30/50 XRT Ceramax V': 25.1,
  'CS - 30/60 CarboHSP': 29.69,
  'CS - 30/60 CarboProp': 27.272,
  'CS - 30/60 SinterBall': 29.941,
  'CS - 30/60 SinterLite': 27.772,
  'CS - 30/70 TG': 22.1,
  'CS - 30/70 White': 22.1,
  'CS - 30/80 FracBlack HT': 22.6,
  'CS - 35/140H Interprop': 26.688,
  'CS - 40/70 Black Pro': 22.3,
  'CS - 40/70 CarboLite': 22.601,
  'CS - 40/70 CarboProp': 27.272,
  'CS - 40/70 CRC': 20.767,
  'CS - 40/70 CRC - C': 21.017,
  'CS - 40/70 EconoProp': 22.518,
  'CS - 40/70 El-Prop': 22.6,
  'CS - 40/70 Interprop': 26.688,
  'CS - 40/70 MG Light': 22.601,
  'CS - 40/70 OilPlus': 21.7,
  'CS - 40/70 Pearl CRC': 22.6,
  'CS - 40/70 PowerProp': 20.683,
  'CS - 40/70 PRC': 20.767,
  'CS - 40/70 PRC Premium': 21.267,
  'CS - 40/70 Prime Plus': 21.7,
  'CS - 40/70 Super DC': 21.267,
  'CS - 40/70 Super LC': 21.35,
  'CS - 40/70 Tempered HS': 21.568,
  'CS - 40/70 TG': 22.1,
  'CS - 40/70 ValuBond': 21.767,
  'CS - 40/70 White': 22.1,
  'CS - 40/80 Bauxite (St. Gobain)': 29.19,
  'CS - 40/80 BauxLite': 26.688,
  'CS - 40/80 HydroProp': 21.267,
  'CS - 40/80 Interprop': 26.688,
  'CS - 40/80 SinterLite': 27.772,
  'CS - 20/40 GGL G2': 25.02,
  'CS - GreenProp': 20.93,
  'CS - SinterBall Lock': 27.939,
  'CS - SinterBlast': 30.2,
  'CS - SpearProp': 20.93,
  'CS - Ultraprop': 29.19,
  'CS - VersaLite': 25.02,
  'CS - Versaprop': 26.688,
  'CS - 12/20 Brady': 22.1,
  'CS - 16/30 Brady': 22.1,
  'CS - 16/30 PC': 21.43,
  'CS - 20/40 Brady': 22.1,
  'CS - 20/40 CRC - E': 21.434,
  'CS - 20/40 PC': 21.43,
  'CS - 20/40 PPC': 21.43,
  'CS - 30/50 Brady': 22.1,
  'CS - 30/50 CRC - E': 21.434,
  'CS - 30/50 PC': 21.43,
  'CS - 30/50 PPC': 21.43,
  'CS - 30/70 Brady': 22.1,
  'CS - 40/70 Brady': 22.1,
  'CS - 40/70 Chieftain': 22.1,
  'CS - 40/70 CRC - E': 21.434,
  'CS - 40/70 PC': 21.43,
  'CS - 40/70 PPC': 21.43,
  'CS - 40/70 Optiprop-G2': 21.184,
  'CS - 16/30 OilPlus': 21.8,
  'CS - 20/40 Black Pro': 21.3,
  'CS - 30/50 Black Ultra': 21.3,
  'CS - 30/50 LW Manticore': 22.685,
  'CS - 30/50 Optiprop-G2': 21.184,
  'CS - 30/50 TerraProp': 26.771,
  'CS - 30/50 ProLite': 22.935,
  'CS - 40/70 ProLite': 22.101,
  'CS - 100 Super LC': 21.267,
  'CS - 100 Mesh Native': 22.1,
  'CS - 40/70 Native': 22.1,
  'CS - 200 Mesh': 22.101
};

// Get sorted description options for dropdown
const getDescriptionOptions = (): string[] => {
  return Object.keys(descriptionDictionary).sort();
};

// Get the value for a description
const getDescriptionValue = (description: string): number => {
  return descriptionDictionary[description] || 22.101; // Default to 22.101 if not found
};

// ── Shared treatment cell renderers ──────────────────────────────────────────
// Used by both the Actual and Proposal tables so the type/description
// validation has exactly one definition. Plain functions, not components —
// calling them inlines the JSX, so no remount-on-keystroke.
type StepEdits = Record<number, any>;

// Stable identity. The Proposal table edits its rows in place and has no
// overlay, so it passes this and every `edits[id]?.x` falls through to the row.
const NO_EDITS: StepEdits = {};

const renderTypeCell = (
  row: { id: number; type: string },
  edits: StepEdits,
  onEdit: (id: number, field: string, value: any) => void,
  editable: boolean,
  theme: string
) => {
  if (!editable) return row.type;
  const currentType = edits[row.id]?.type !== undefined ? edits[row.id].type : row.type;
  const safeCurrentType = currentType || '';
  const isValidOption = treatmentTypeOptions.includes(safeCurrentType);
  const dropdownOptions = isValidOption
    ? treatmentTypeOptions
    : [safeCurrentType, ...treatmentTypeOptions];

  return (
    <select
      className={`form-control form-control-sm ${theme === 'dark' ? 'bg-dark text-light' : ''}`}
      value={safeCurrentType}
      onChange={(e) => onEdit(row.id, 'type', e.target.value)}
      style={{
        backgroundColor: theme === 'dark' ? '#1a2234' : '#ffffff',
        color: theme === 'dark' ? '#e5e5e5' : '#333333',
        borderColor: theme === 'dark' ? '#3a4564' : '#e6e6e6'
      }}
    >
      {dropdownOptions.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
};

const renderDescriptionCell = (
  row: { id: number; type: string; description: string },
  edits: StepEdits,
  onEdit: (id: number, field: string, value: any) => void,
  editable: boolean,
  theme: string,
  disabled: boolean = false
) => {
  if (!editable) return row.description;

  const currentType = edits[row.id]?.type ?? row.type;
  // Only Proppant/Sand Ramp steps get a description. Pad, Flush, Acid etc. don't.
  const showDescriptionDropdown =
    currentType === 'Proppant' || currentType === 'Rate Drop-Proppant' || currentType === 'Sand Ramp' || currentType === 'Rate Drop-Sand Ramp';

  const currentDescription = edits[row.id]?.description ?? row.description;
  const safeCurrentDescription = currentDescription || '';

  if (showDescriptionDropdown) {
    const isValidOption = getDescriptionOptions().includes(safeCurrentDescription);
    const dropdownOptions = isValidOption
      ? ['', ...getDescriptionOptions()]
      : ['', safeCurrentDescription, ...getDescriptionOptions()];

    return (
      <select
        className={`form-control form-control-sm ${theme === 'dark' ? 'bg-dark text-light' : ''}`}
        value={safeCurrentDescription}
        onChange={(e) => onEdit(row.id, 'description', e.target.value)}
        disabled={disabled}
        style={{
          backgroundColor: theme === 'dark' ? '#1a2234' : '#ffffff',
          color: theme === 'dark' ? '#e5e5e5' : '#333333',
          borderColor: theme === 'dark' ? '#3a4564' : '#e6e6e6',
          width: '90%',
          textAlign: 'center'
        }}
      >
        {dropdownOptions.map(option => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div
      style={{
        padding: '6px 12px',
        backgroundColor: theme === 'dark' ? '#212529' : '#f8f9fa',
        border: `1px solid ${theme === 'dark' ? '#3a4564' : '#e6e6e6'}`,
        borderRadius: '4px',
        width: '90%',
        height: '31.67px',
        textAlign: 'center',
        color: theme === 'dark' ? '#e5e5e5' : '#333333',
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {safeCurrentDescription}
    </div>
  );
};

// Interface matching the MQTT payload format from PetrixDataListener
interface PetrixData {
  step: number;
  cleanRate: number;
  slurryRate: number;
  stageDensity: number;
  stp: number;
  elapsedTime: number;
  stepClean: number;
  stepSlurry: number;
  stepSand: number;
}

interface Well {
  id: number;
  pad_id: number;
  well_name: string;
  api: string;
  stage_count: number;
  start_ts: number | null;
  end_ts: number | null;
}

interface DesignTreatment {
  id: number;
  stage_id: number;
  creator: string;
  timestamp: number;
  step: number;
  type: string;
  description: string;
  rate: number;
  conc: number;
  clean_step: number;
  clean_actual: number;
  slurry_step: number;
  slurry_actual: number;
  sand_step: number;
  sand_actual: number;
}

interface ActualTreatment {
  id: number;
  stage_id: number;
  step: number;
  type: string;
  description: string;
  rate: number;
  conc: number;
  clean_step: number;
  clean_actual: number;
  slurry_step: number;
  slurry_actual: number;
  sand_step: number;
  sand_actual: number;
  start_ts: number;
  end_ts: number;
  originalStep?: number; // Optional property to store the original step number
  start_conc?: number; // Optional property for Sand Ramp start concentration
  end_conc?: number; // Optional property for Sand Ramp end concentration
}

// Proposal rows carry the same columns as Actual, plus who quoted it and when.
type ProposalTreatment = ActualTreatment & {
  creator?: string | null;
  timestamp?: number | null;
};

// ── Chem schedule types ──────────────────────────────────────────────────────
interface ChemEntry {
  design: number | null;
  actual: number | null;
}

interface ChemStep {
  step: number;
  type: string;
  clean_step: number;
  chems: Record<string, ChemEntry>; // keyed by chem_name
}

interface ChemSchedulePivoted {
  chem_names: string[];
  steps: ChemStep[];
}

const TreatmentModal: React.FC<TreatmentModalProps> = ({ 
  show, 
  onHide, 
  stageId: propStageId, 
  wellId: propWellId, 
  stageNumber: propStageNumber,
  isRealTimeStreaming: propIsRealTimeStreaming = false,
  currentStep: propCurrentStep = null,
  onTreatmentScheduleUpdate,
  useFakeData = false
}) => {
  const { theme } = useTheme();

  // ── Header well/stage navigation ────────────────────────────────────────────
  // Hop to any well's stage without closing the modal. These shadow the props,
  // so every stageId/wellId/stageNumber reference below follows the dropdowns
  // with no other changes — including the fetch effects keyed on them.
  const [navWellId, setNavWellId] = useState<number | null>(null);
  const [navStage, setNavStage]   = useState<{ id: number; number: number } | null>(null);
  const [padWells, setPadWells]   = useState<Well[]>([]);
  const [wellStages, setWellStages] = useState<{ id: number; stage_number: number }[]>([]);

  const wellId      = navWellId ?? propWellId;
  const stageId     = navStage?.id ?? propStageId;
  const stageNumber = navStage?.number ?? propStageNumber;

  // Once you navigate off the stage the modal was opened for, the parent's live
  // MQTT context describes a different stage than what's on screen. Drop it, or
  // the Actual tab would show another well's stage as live and editable.
  const isNavigated         = stageId !== propStageId;
  const isRealTimeStreaming = isNavigated ? false : propIsRealTimeStreaming;
  const currentStep         = isNavigated ? null : propCurrentStep;
  const [activeTab, setActiveTab] = useState<string>(isRealTimeStreaming ? 'actual' : 'design');
  const [activeSubTab, setActiveSubTab] = useState<string>('treatment');
  const [loading, setLoading] = useState<boolean>(false);
  const [isSavingSchedule, setIsSavingSchedule] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [editBySand, setEditBySand] = useState(false);
  const [designTreatments, setDesignTreatments] = useState<DesignTreatment[]>([]);
  const [actualTreatments, setActualTreatments] = useState<ActualTreatment[]>([]);
  const [selectedDesignTimestamp, setSelectedDesignTimestamp] = useState<number | null>(null);
  const [selectedDesignCreator, setSelectedDesignCreator] = useState<string | null>(null);
  const [uniqueDesignTimestamps, setUniqueDesignTimestamps] = useState<{timestamp: number, creator: string}[]>([]);
  const [wellData, setWellData] = useState<Well | null>(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [chartOptions, setChartOptions]     = useState<Highcharts.Options>({});
  const [chemChartOptions, setChemChartOptions] = useState<Highcharts.Options>({});
  const [editedTreatmentSteps, setEditedTreatmentSteps] = useState<{[stepId: number]: {type?: string, description?: string, rate?: number, conc?: number, clean_step?: number, sand_step?: number, start_conc?: number, end_conc?: number}, _hasChanges?: boolean}>({});
  const [realTimeData, setRealTimeData]       = useState<PetrixData | null>(null);
  const [realTimeChemData, setRealTimeChemData] = useState<any[]>([]);
  const [mqttClient, setMqttClient] = useState<mqtt.MqttClient | null>(null);
  const isViewer = JSON.parse(localStorage.getItem("roles") || "[]").includes("Viewer");
  // Store the last known values for each step
  const [lastStepValues, setLastStepValues] = useState<{[step: number]: {
    stepClean: number;
    stepSlurry: number;
    stepSand: number;
    elapsedTime: number;
    progress: number;
  }}>({}); 
  
  // Context menu state
  const [showContextMenu, setShowContextMenu] = useState<boolean>(false);
  const [contextMenuPosition, setContextMenuPosition] = useState<{x: number, y: number}>({x: 0, y: 0});
  const [selectedStepForContextMenu, setSelectedStepForContextMenu] = useState<ActualTreatment | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  // Concentration modal state — one modal instance shared by both the Actual
  // and Proposal tabs; `source` says which save handler to run.
  const [showConcentrationModal, setShowConcentrationModal] = useState<boolean>(false);
  type ConcentrationTarget =
    | { source: 'actual'; treatment: ActualTreatment }
    | { source: 'proposal'; treatment: ProposalTreatment };
  const [concentrationTarget, setConcentrationTarget] = useState<ConcentrationTarget | null>(null);
  const [hasEditLock, setHasEditLock] = useState<boolean>(false);
  const [lockHeartbeatInterval, setLockHeartbeatInterval] = useState<NodeJS.Timeout | null>(null);
  
  // Simplified change tracking state
  const [originalTreatments, setOriginalTreatments] = useState<ActualTreatment[]>([]);
  const [treatmentChanges, setTreatmentChanges]   = useState<{creator: string, timestamp: string, comment: string}[]>([]);
  const [chemicalChanges, setChemicalChanges]     = useState<{creator: string, timestamp: string | number, comment: string}[]>([]);

  // Copy to Stages modal state
  const [showCopyToStagesModal, setShowCopyToStagesModal] = useState<boolean>(false);

  // ── Chem tab state ──────────────────────────────────────────────────────────
  const [chemSchedule, setChemSchedule]   = useState<ChemSchedulePivoted | null>(null);
  const [chemLoading, setChemLoading]     = useState<boolean>(false);
  const [editingCell, setEditingCell]     = useState<{ step: number; chemName: string } | null>(null);
  const [editValue, setEditValue]         = useState<string>('');
  const [isSavingChem, setIsSavingChem]   = useState<boolean>(false);
  const [hasChemEditLock, setHasChemEditLock] = useState<boolean>(false);
  // Chem edit: track edited loadings keyed by "step::chemName"
  const [editedChemLoadings, setEditedChemLoadings] = useState<Record<string, number>>({});
  // Multi-group chem edits keyed by "step::chemName"
  const [editedChemGroupRatios, setEditedChemGroupRatios] = useState<Record<string, {group: string, ratio: number}[]>>({});
  // Raw chemicals array from the schedule MQTT topic, keyed by step number
  const [rawScheduleChemicals, setRawScheduleChemicals] = useState<Record<number, {name: string, group: string, ratio: number}[]>>({});
  // Group ratio modal state
  const [chemGroupModal, setChemGroupModal] = useState<{step: number, chemName: string, groups: {group: string, ratio: number}[]} | null>(null);
  // true = Petrix 8.0 (chemicals array in schedule topic) — direct chem save
  // false = Petrix 7.0.1 (no chemicals array) — falls back to posting a plain message
  const [petrixHasChemSupport, setPetrixHasChemSupport] = useState<boolean>(true);

  // ── Proposal tab ────────────────────────────────────────────────────────────
  // The quoted schedule. No MQTT, no edit lock, no Petrix round-trip — so rows
  // are edited in place and Cancel just refetches. That's why there's no
  // editedProposalSteps overlay to mirror editedTreatmentSteps.
  const [proposalTreatments, setProposalTreatments] = useState<ProposalTreatment[]>([]);
  const [proposalLoading, setProposalLoading]     = useState<boolean>(false);
  const [isProposalEditing, setIsProposalEditing] = useState<boolean>(false);
  const [isSavingProposal, setIsSavingProposal]   = useState<boolean>(false);
  const [proposalDirty, setProposalDirty]         = useState<boolean>(false);
  const [selectedProposalStep, setSelectedProposalStep] = useState<ProposalTreatment | null>(null);
  const proposalTempIdRef      = useRef<number>(-1);
  const proposalLoadedForStage = useRef<number | null>(null);

  // Fetch treatment changes from backend
  const fetchTreatmentChanges = async (stage_id: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${backendUrl}/stages/${stage_id}/treatment_changes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTreatmentChanges(response.data);
    } catch (error) {
      console.error('Error fetching treatment changes:', error);
    }
  };

  // Fetch chemical changes from dedicated table
  const fetchChemicalChanges = async (stage_id: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${backendUrl}/stages/${stage_id}/chemical_changes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChemicalChanges(response.data);
    } catch (error) {
      console.error('Error fetching chemical changes:', error);
    }
  };

  // Helper function to compare treatments and generate change descriptions
  const compareAndGenerateChanges = (currentTreatments: ActualTreatment[], originalTreatments: ActualTreatment[]): string => {
    const changeDescriptions: string[] = [];
    
    // Create maps for easier lookup by ID
    const currentById = new Map(currentTreatments.map(t => [t.id, t]));
    const originalById = new Map(originalTreatments.map(t => [t.id, t]));
    
    // Track added steps (exist in current but not in original)
    const addedSteps = currentTreatments.filter(current => !originalById.has(current.id));
    
    // Track removed steps (exist in original but not in current)
    const removedSteps = originalTreatments.filter(original => !currentById.has(original.id));
    
    // Track steps that exist in both (potential moves or field changes)
    const existingSteps = currentTreatments.filter(current => originalById.has(current.id));
    
    // Process adds first
    if (addedSteps.length > 0) {
      if (addedSteps.length === 1) {
        changeDescriptions.push(`Added step ${addedSteps[0].step}`);
      } else {
        const stepNumbers = addedSteps.map(s => s.step).sort((a, b) => a - b);
        changeDescriptions.push(`Added steps ${stepNumbers.join(', ')}`);
      }
    }
    
    // Process removes
    if (removedSteps.length > 0) {
      if (removedSteps.length === 1) {
        changeDescriptions.push(`Removed step ${removedSteps[0].step}`);
      } else {
        const stepNumbers = removedSteps.map(s => s.step).sort((a, b) => a - b);
        changeDescriptions.push(`Removed steps ${stepNumbers.join(', ')}`);
      }
    }
    
    // For remaining steps, detect meaningful moves (not cascading renumbers)
    // We need to distinguish intentional moves from automatic renumbering caused by adds/removes
    const meaningfulMoves: Array<{fromStep: number, toStep: number, treatmentId: number, isIntentional: boolean}> = [];
    
    existingSteps.forEach(current => {
      const original = originalById.get(current.id);
      if (!original) return;
      
      if (original.step !== current.step) {
        // Calculate the natural cascading effect for this specific step
        let expectedNewStep = original.step;
        
        // Account for steps removed before this step's original position
        const removedBefore = removedSteps.filter(removed => removed.step < original.step).length;
        expectedNewStep -= removedBefore;
        
        // Account for steps added before this step's expected new position
        // We need to be careful here - only count adds that would naturally push this step down
        const addsBeforeExpected = addedSteps.filter(added => added.step <= expectedNewStep).length;
        expectedNewStep += addsBeforeExpected;
        
        // Check if this step's current position matches the naturally cascaded position
        const isNaturalCascade = current.step === expectedNewStep;
        
        // Check originalStep to determine if this was an intentional move
        const currentOriginalStep = (current as any).originalStep;
        const hasPreservingOriginalStep = currentOriginalStep !== undefined && currentOriginalStep === original.step;
        
        // Determine if this is an intentional move vs automatic displacement
        // An intentional move is when originalStep doesn't match the original step
        const isIntentionalMove = currentOriginalStep !== undefined && currentOriginalStep !== original.step;
        
        // Only treat as cascading if BOTH conditions are true:
        // 1. The position matches natural cascading AND
        // 2. The originalStep suggests this wasn't an intentional move AND
        // 3. It's not marked as an intentional move
        const isCascadingRenumber = isNaturalCascade && hasPreservingOriginalStep && !isIntentionalMove;
        
        // If it's not cascading renumbering, it's a move (but we'll track if it's intentional)
        if (!isCascadingRenumber) {
          meaningfulMoves.push({
            fromStep: original.step,
            toStep: current.step,
            treatmentId: current.id,
            isIntentional: isIntentionalMove
          });
        }
      }
    });
    
    // Process meaningful moves, but prioritize intentional ones
    if (meaningfulMoves.length > 0) {
      // For moves that form circular patterns (like 4→5 and 5→4), 
      // only report the intentional one
      const reportedMoves = new Set<string>();
      
      meaningfulMoves.forEach(move => {
        // Check if this move has a reverse counterpart
        const reverseMove = meaningfulMoves.find(m => 
          m.fromStep === move.toStep && 
          m.toStep === move.fromStep && 
          m.treatmentId !== move.treatmentId
        );
        
        if (reverseMove) {
          // This is part of a circular move pattern
          const moveKey = `${Math.min(move.fromStep, move.toStep)}-${Math.max(move.fromStep, move.toStep)}`;
          
          if (!reportedMoves.has(moveKey)) {
            // Report the intentional move, or if both/neither are intentional, report the first one
            if (move.isIntentional && !reverseMove.isIntentional) {
              changeDescriptions.push(`Moved step ${move.fromStep} to ${move.toStep}`);
            } else if (!move.isIntentional && reverseMove.isIntentional) {
              changeDescriptions.push(`Moved step ${reverseMove.fromStep} to ${reverseMove.toStep}`);
            } else {
              // If both or neither are marked as intentional, just report one
              changeDescriptions.push(`Moved step ${move.fromStep} to ${move.toStep}`);
            }
            reportedMoves.add(moveKey);
          }
        } else {
          // This is a standalone move
          changeDescriptions.push(`Moved step ${move.fromStep} to ${move.toStep}`);
        }
      });
    }
    
    // Track individual field changes for existing steps
    // We want to report field changes even for steps that were moved.
    // Collected per-step here so consecutive steps with identical change sets
    // can be collapsed into a range below (e.g. cascade across steps 4-8 reads
    // as "Steps 4-8: Rate: 80 -> 90" instead of five separate lines).
    const perStepChanges: { step: number; parts: string[] }[] = [];

    existingSteps.forEach(current => {
      const original = originalById.get(current.id);
      if (!original) return;

      const fieldChanges: string[] = [];
      
      if (current.type !== original.type) {
        fieldChanges.push(`Type: ${original.type} -> ${current.type}`);
      }
      if (current.description !== original.description) {
        fieldChanges.push(`Description: ${original.description || 'empty'} -> ${current.description || 'empty'}`);
      }
      if (Math.abs(current.rate - original.rate) > 0.001) { // Use small epsilon for float comparison
        fieldChanges.push(`Rate: ${original.rate} -> ${current.rate}`);
      }
      if (Math.abs(current.clean_step - original.clean_step) > 0.001) {
        fieldChanges.push(`Clean Step: ${original.clean_step} -> ${current.clean_step}`);
      }

      // In editBySand mode only sand_step is written — detect and report as Sand Step
      const editedStep = editedTreatmentSteps[current.id];
      if (editedStep?.sand_step !== undefined && editedStep?.clean_step === undefined) {
        const originalSand = Math.round((original.clean_step || 0) * 42 * (original.conc || 0));
        const newSand = Math.round(editedStep.sand_step as number);
        if (newSand !== originalSand) {
          fieldChanges.push(`Sand Step: ${originalSand} -> ${newSand}`);
        }
      }
      
      // Compare concentrations with proper decimal handling (round to 4 decimal places for comparison)
      const originalConc = Math.round((original.conc || 0) * 10000) / 10000;
      const currentConc = Math.round((current.conc || 0) * 10000) / 10000;
      
      if (Math.abs(currentConc - originalConc) > 0.0001) {
        fieldChanges.push(`Conc: ${originalConc} -> ${currentConc}`);
      }
      
      // Compare start and end concentrations for Sand Ramp steps
      const originalStartConc = Math.round((original.start_conc || 0) * 10000) / 10000;
      const currentStartConc = Math.round((current.start_conc || 0) * 10000) / 10000;
      const originalEndConc = Math.round((original.end_conc || 0) * 10000) / 10000;
      const currentEndConc = Math.round((current.end_conc || 0) * 10000) / 10000;
      
      if (Math.abs(currentStartConc - originalStartConc) > 0.0001) {
        fieldChanges.push(`Start Target Conc: ${originalStartConc} -> ${currentStartConc}`);
      }
      if (Math.abs(currentEndConc - originalEndConc) > 0.0001) {
        fieldChanges.push(`End Target Conc: ${originalEndConc} -> ${currentEndConc}`);
      }
      
      if (fieldChanges.length > 0) {
        perStepChanges.push({ step: current.step, parts: fieldChanges });
      }
    });

    perStepChanges.sort((a, b) => a.step - b.step);

    const groups: { start: number; end: number; parts: string[] }[] = [];
    for (const entry of perStepChanges) {
      const last = groups[groups.length - 1];
      const sig = [...entry.parts].sort().join('|');
      const lastSig = last ? [...last.parts].sort().join('|') : '';
      if (last && last.end + 1 === entry.step && sig === lastSig) {
        last.end = entry.step;
      } else {
        groups.push({ start: entry.step, end: entry.step, parts: entry.parts });
      }
    }

    groups.forEach(g => {
      const label = g.start === g.end ? `Step ${g.start}` : `Steps ${g.start}-${g.end}`;
      changeDescriptions.push(`${label}: ${g.parts.join(', ')}`);
    });

    return changeDescriptions.join(', ');
  };

  // Fetch latest design schedule from endpoint
  const fetchLatestDesignSchedule = async (): Promise<ActualTreatment[]> => {
    if (!stageId) return [];
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return [];

      const response = await axios.get(`${backendUrl}/stages/${stageId}/latest_design_schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = Array.isArray(response.data) ? response.data : [response.data];
      
      // Transform to ActualTreatment format and assign originalStep to current step
      return data.map(treatment => ({
        ...treatment,
        conc: typeof treatment.conc === 'string' ? parseFloat(treatment.conc) || 0 : (treatment.conc || 0),
        rate: typeof treatment.rate === 'string' ? parseFloat(treatment.rate) || 0 : (treatment.rate || 0),
        clean_step: typeof treatment.clean_step === 'string' ? parseFloat(treatment.clean_step) || 0 : (treatment.clean_step || 0),
        // Always set originalStep to the current step when loading latest design schedule
        originalStep: treatment.step
      }));
    } catch (error) {
      console.error('Error fetching latest design schedule:', error);
      return [];
    }
  };

  // Lock mechanism functions
  const requestEditLock = async (): Promise<boolean> => {
    if (!stageId) return false;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token not found');
        return false;
      }

      // Extract the van_id from localStorage or use fallback
      const vanId = localStorage.getItem('currentVanId') || "DV1017";

      const response = await axios.post(
        `${backendUrl}/stages/mqtt/${stageId}/request_edit_lock`,
        { vanId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.lockAcquired) {
        setHasEditLock(true);
        // Start heartbeat to keep lock alive
        startLockHeartbeat();
        toast.success(response.data.message || "Edit lock acquired");
        return true;
      } else {
        toast.error(response.data.message || "Could not acquire edit lock");
        return false;
      }
    } catch (error) {
      console.error('Error requesting edit lock:', error);
      toast.error('Failed to request edit lock');
      return false;
    }
  };

  const releaseEditLock = async (): Promise<void> => {
    if (!stageId) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      // Extract the van_id from localStorage or use fallback
      const vanId = localStorage.getItem('currentVanId') || "DV1017";

      await axios.post(
        `${backendUrl}/stages/mqtt/${stageId}/release_lock`,
        { vanId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setHasEditLock(false);
      setEditedTreatmentSteps({});  // Discard unsaved edits
      setEditBySand(false);         // Reset toggle to "Editing by Clean Step"
      stopLockHeartbeat();
      toast.success("Edit lock released");
    } catch (error) {
      console.error('Error releasing edit lock:', error);
      toast.error('Failed to release edit lock');
    }
  };

  const startLockHeartbeat = () => {
    // Clear any existing heartbeat
    if (lockHeartbeatInterval) {
      clearInterval(lockHeartbeatInterval);
    }

    // Start new heartbeat every 15 seconds
    const interval = setInterval(async () => {
      // Only send heartbeat if we still have the lock
      if (hasEditLock && stageId) {
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            setHasEditLock(false);
            stopLockHeartbeat();
            return;
          }

          const vanId = localStorage.getItem('currentVanId') || "DV1017";

          // Send heartbeat to maintain lock (not acquire new one)
          const response = await axios.post(
            `${backendUrl}/stages/mqtt/${stageId}/heartbeat_lock`,
            { vanId },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (!response.data.lockMaintained) {
            // Lock was lost, stop heartbeat and disable editing
            setHasEditLock(false);
            setEditedTreatmentSteps({});
            setEditBySand(false);
            stopLockHeartbeat();
            toast.error("Edit lock was lost");
          }
        } catch (error) {
          console.error('Error sending lock heartbeat:', error);
          // On error, assume lock is lost
          setHasEditLock(false);
          setEditedTreatmentSteps({});
          setEditBySand(false);
          stopLockHeartbeat();
        }
      } else {
        // No lock to maintain, stop heartbeat
        stopLockHeartbeat();
      }
    }, 15000);

    setLockHeartbeatInterval(interval);
  };

  const stopLockHeartbeat = () => {
    if (lockHeartbeatInterval) {
      clearInterval(lockHeartbeatInterval);
      setLockHeartbeatInterval(null);
    }
  };

  // Clean up heartbeat on unmount or when modal closes
  useEffect(() => {
    return () => {
      stopLockHeartbeat();
      if (hasEditLock && stageId) {
        releaseEditLock();
      }
    };
  }, []);

  // Release lock when modal closes
  useEffect(() => {
    if (!show && hasEditLock && stageId) {
      releaseEditLock();
    }
  }, [show]);

  // Handle concentration click for Sand Ramp types
  const handleConcentrationClick = (treatment: ActualTreatment) => {
    const currentType = editedTreatmentSteps[treatment.id]?.type !== undefined 
      ? editedTreatmentSteps[treatment.id].type 
      : treatment.type;
    
    if (currentType === 'Sand Ramp' || currentType === 'Rate Drop-Sand Ramp') {
      setConcentrationTarget({ source: 'actual', treatment });
      setShowConcentrationModal(true);
    }
  };

  // Handle concentration modal save — dispatches to the Actual or Proposal
  // state bucket depending on which tab opened the modal.
  const handleConcentrationSave = (startConc: number, endConc: number) => {
    if (!concentrationTarget) return;
    const averageConc = (startConc + endConc) / 2;

    if (concentrationTarget.source === 'actual') {
      const id = concentrationTarget.treatment.id;
      setEditedTreatmentSteps(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          start_conc: startConc,
          end_conc: endConc,
          conc: averageConc // Update the main conc field to the average
        }
      }));
    } else {
      const id = concentrationTarget.treatment.id;
      setProposalTreatments(prev => prev.map(t =>
        t.id === id
          ? { ...t, start_conc: startConc, end_conc: endConc, conc: averageConc }
          : t
      ));
      setProposalDirty(true);
    }
  };

  // ── Cascade helpers ──────────────────────────────────────────────
  // Copy a value from `fromStepNum` to that step plus every later step in
  // `actualTreatments`. The "C" button next to each edited numeric input
  // uses these to bulk-apply a design change to the remainder of the stage.
  // Edits land in the same `editedTreatmentSteps` / `editedChemLoadings`
  // buckets the Save button already reads.
  const cascadeTreatmentField = (
    fromStepNum: number,
    field: 'rate' | 'clean_step' | 'sand_step' | 'conc',
    value: number,
  ) => {
    const future = actualTreatments.filter(t => t.step >= fromStepNum);
    if (future.length <= 1) return;
    setEditedTreatmentSteps(prev => {
      const next = { ...prev };
      future.forEach(t => {
        next[t.id] = { ...next[t.id], [field]: value };
      });
      return next;
    });
    toast.info(`Cascaded ${field.replace('_', ' ')} to ${future.length} steps`, {
      position: 'top-right', autoClose: 2000,
    });
  };

  // Sand Ramp conc is the average of start_conc/end_conc, so cascade all three
  // together to keep derived values consistent on later steps.
  const cascadeSandRampConc = (fromStepNum: number) => {
    const fromStep = actualTreatments.find(t => t.step === fromStepNum);
    if (!fromStep) return;
    const edited = editedTreatmentSteps[fromStep.id] || {};
    const start = edited.start_conc ?? fromStep.start_conc ?? fromStep.conc;
    const end   = edited.end_conc   ?? fromStep.end_conc   ?? fromStep.conc;
    const conc  = edited.conc       ?? fromStep.conc;
    const future = actualTreatments.filter(t => t.step >= fromStepNum);
    if (future.length <= 1) return;
    setEditedTreatmentSteps(prev => {
      const next = { ...prev };
      future.forEach(t => {
        next[t.id] = { ...next[t.id], start_conc: start, end_conc: end, conc };
      });
      return next;
    });
    toast.info(`Cascaded conc to ${future.length} steps`, {
      position: 'top-right', autoClose: 2000,
    });
  };

  const cascadeChemRatio = (fromStepNum: number, chemName: string) => {
    const future = actualTreatments.filter(t => t.step >= fromStepNum);
    if (future.length <= 1) return;
    const fromKey = `${fromStepNum}::${chemName}`;
    const editedGroups = editedChemGroupRatios[fromKey];
    const editedVal = editedChemLoadings[fromKey];
    if (editedGroups) {
      setEditedChemGroupRatios(prev => {
        const next = { ...prev };
        future.forEach(t => { next[`${t.step}::${chemName}`] = editedGroups; });
        return next;
      });
    } else if (editedVal !== undefined) {
      setEditedChemLoadings(prev => {
        const next = { ...prev };
        future.forEach(t => { next[`${t.step}::${chemName}`] = editedVal; });
        return next;
      });
    } else {
      return;
    }
    toast.info(`Cascaded ${chemName} ratio to ${future.length} steps`, {
      position: 'top-right', autoClose: 2000,
    });
  };

  // Small "C" button rendered next to an edited cell. Visibility is decided
  // by the caller — render only when an edit exists AND there are later
  // steps to cascade into.
  const renderCascadeBtn = (onClick: () => void, title = 'Cascade this value to current + all future steps') => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        marginLeft: '4px',
        width: '18px',
        height: '18px',
        borderRadius: '50%',
        border: `1px solid ${theme === 'dark' ? '#8CC540' : '#5a9620'}`,
        backgroundColor: theme === 'dark' ? '#2d4a18' : '#e8f5d8',
        color: theme === 'dark' ? '#a4d36a' : '#3d6611',
        fontSize: '0.7rem',
        fontWeight: 700,
        lineHeight: 1,
        padding: 0,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: 'middle',
        flexShrink: 0,
      }}
    >
      C
    </button>
  );

  const rateDropConstraints = useMemo(() => {
    const sorted = [...actualTreatments].sort((a, b) => a.step - b.step);
    const map: Record<number, { rateMin: number; rateMax: number; rateOutOfRange: boolean; cleanSeconds: number; cleanOutOfRange: boolean; consecutiveRateDrop: boolean }> = {};
    let hasViolation = false;
    sorted.forEach((t, i) => {
      const type = editedTreatmentSteps[t.id]?.type ?? t.type;
      if (type !== 'Rate Drop' && type !== 'Rate Drop-Proppant' && type !== 'Rate Drop-Sand Ramp') return;
      const prevType = i > 0 ? (editedTreatmentSteps[sorted[i - 1].id]?.type ?? sorted[i - 1].type) : '';
      const consecutiveRateDrop = prevType === 'Rate Drop' || prevType === 'Rate Drop-Proppant' || prevType === 'Rate Drop-Sand Ramp';
      const prevRate = i > 0 ? (editedTreatmentSteps[sorted[i - 1].id]?.rate ?? sorted[i - 1].rate) : 0;
      const rateMin = +(prevRate - 10).toFixed(4);
      const rateMax = +(prevRate - 2).toFixed(4);
      const currentRate = (editedTreatmentSteps[t.id]?.rate ?? t.rate) / (1 + (editedTreatmentSteps[t.id]?.conc ?? t.conc) / 42);
      const rateOutOfRange = currentRate < rateMin || currentRate > rateMax;
      const cleanStep = editedTreatmentSteps[t.id]?.clean_step ?? t.clean_step;
      const cleanSeconds = currentRate > 0 ? (cleanStep / currentRate) * 60 : 0;
      const cleanOutOfRange = cleanSeconds < 5 || cleanSeconds > 90;
      if (rateOutOfRange || cleanOutOfRange || consecutiveRateDrop) hasViolation = true;
      map[t.id] = { rateMin, rateMax, rateOutOfRange, cleanSeconds, cleanOutOfRange, consecutiveRateDrop };
    });
    return { map, hasViolation };
  }, [actualTreatments, editedTreatmentSteps]);

  // Handle concentration modal close without releasing edit lock
  const handleConcentrationModalClose = () => {
    setShowConcentrationModal(false);
    setConcentrationTarget(null);
  };

  // Reset state when modal is closed
  useEffect(() => {
    if (!show) {
      setDesignTreatments([]);
      setActualTreatments([]);
      setSelectedDesignTimestamp(null);
      setSelectedDesignCreator(null);
      setUniqueDesignTimestamps([]);
      setError(null);
      setEditedTreatmentSteps({});
      setChemSchedule(null);
      setEditingCell(null);
      setHasChemEditLock(false);
      setEditedChemLoadings({});
      setEditedChemGroupRatios({});
      setRawScheduleChemicals({});
      setChemGroupModal(null);
      setChemicalChanges([]);
      
      // Disconnect MQTT client when modal is closed
      if (mqttClient) {
        mqttClient.end();
        setMqttClient(null);
      }
    }
  }, [show, mqttClient]);

  // Connect to MQTT broker
  const connectToMqtt = () => {
    if (mqttClient) {
      // Already connected
      return;
    }

    // Make sure we have actualTreatments data before connecting
    if (actualTreatments.length === 0) {
      console.log('Waiting for treatment data before connecting to MQTT');
      return; // Don't connect yet, wait for data
    }

    console.log('Connecting to MQTT with actualTreatments:', actualTreatments);

    // WebSocket URL for MQTT broker
    const brokerUrl = process.env.REACT_APP_BROKER_URL;
    const brokerUser = process.env.REACT_APP_BROKER_USER;
    const brokerPass = process.env.REACT_APP_BROKER_PASS;
    
    // MQTT connection options
    const options = {
      username: brokerUser,
      password: brokerPass,
      keepalive: 60,
      reconnectPeriod: 1000,
    };
    
    // Connect to MQTT broker
    const client = mqtt.connect(brokerUrl, options);
    setMqttClient(client);
    
    client.on('connect', () => {
      console.log('MQTT connected successfully in TreatmentModal');
      
      // Get the van_id from localStorage (set by TreatmentDataGraph)
      const vanId = localStorage.getItem('currentVanId') || 'DV1017'; // Fallback to default
      const dataTopic = `propilot/${vanId}/petrix/data`;
      const scheduleTopic = `propilot/${vanId}/petrix/schedule`;
      
      // Store the topic in localStorage for later use
      localStorage.setItem('mqttTopic', dataTopic);
      
      client.subscribe(dataTopic, (err) => {
        if (err) {
          console.error('Subscribe error for data topic:', err);
        } else {
          console.log('Successfully subscribed to data topic:', dataTopic);
        }
      });

      // Subscribe to schedule topic for real-time schedule updates
      client.subscribe(scheduleTopic, (err) => {
        if (err) {
          console.error('Subscribe error for schedule topic:', err);
        } else {
          console.log('Successfully subscribed to schedule topic:', scheduleTopic);
        }
      });

      // Subscribe to chem topic for real-time loading actual values
      const chemTopic = `propilot/${vanId}/petrix/chem`;
      client.subscribe(chemTopic, (err) => {
        if (!err) console.log('Subscribed to chem topic:', chemTopic);
      });
    });
    
    client.on('message', (topic, payload) => {
      try {
        const payloadStr = payload.toString();
        
        // Check if this is a schedule update message
        if (topic.endsWith('/petrix/schedule')) {
          console.log('Received schedule update via MQTT:', payloadStr);
          
          // Parse the schedule update to get the new data
          try {
            const scheduleUpdate = JSON.parse(payloadStr);
            
            // Update actualTreatments state directly with the new schedule data
            if (scheduleUpdate && Array.isArray(scheduleUpdate)) {
              console.log('Updating treatment schedule with MQTT data:', scheduleUpdate);

              // Detect Petrix version from chemicals array presence
              // Detect Petrix version: 8.0 sends a "chemicals" key on every step (may be
              // empty array). 7.0.1 omits the key entirely. Check for presence, not length.
              const hasPetrix8Chemicals = scheduleUpdate.some(
                (step: any) => Object.prototype.hasOwnProperty.call(step, 'chemicals') && Array.isArray(step.chemicals)
              );
              setPetrixHasChemSupport(hasPetrix8Chemicals);
              console.log(`Petrix version: ${hasPetrix8Chemicals ? '8.0 (chemicals supported)' : '7.0.1 (no chemicals array)'}`);

              // Populate rawScheduleChemicals — source of truth for group info
              const rawChems: Record<number, {name: string, group: string, ratio: number}[]> = {};
              scheduleUpdate.forEach((step: any) => {
                if (Array.isArray(step.chemicals)) {
                  rawChems[step.step] = step.chemicals.map((c: any) => ({
                    name:  (c.name  || ''),
                    group: (c.group || ''),
                    ratio: typeof c.ratio === 'number' ? c.ratio : 0,
                  }));
                }
              });
              setRawScheduleChemicals(rawChems);

              // The new MQTT broadcast is authoritative — an unsupervised rule (or
              // any external change) has shifted the schedule baseline, so any
              // pending edits are stale. Drop them so the fresh values render
              // instead of being masked by the user's prior edit (rate, conc,
              // clean/sand step on the treatment side; ratios on the chem side).
              setEditedChemLoadings({});
              setEditedChemGroupRatios({});
              setEditedTreatmentSteps({});

              // Transform the MQTT schedule data to match our ActualTreatment interface
              const updatedTreatments = scheduleUpdate.map((step: any) => {
                // Find existing treatment to preserve certain values
                const existingTreatment = actualTreatments.find(t => t.step === step.step);
                
                // Calculate concentration from start_conc and end_conc average
                let processedConc = 0;
                
                if (step.targetStartConc !== undefined && step.targetEndConc !== undefined) {
                  // Calculate average of start and end concentrations
                  processedConc = (step.targetStartConc + step.targetEndConc) / 2;
                  console.log(`Calculated conc for step ${step.step}: (${step.targetStartConc} + ${step.targetEndConc}) / 2 = ${processedConc}`);
                } else if (step.start_conc !== undefined && step.end_conc !== undefined) {
                  // Alternative field names
                  processedConc = (step.start_conc + step.end_conc) / 2;
                  console.log(`Calculated conc for step ${step.step}: (${step.start_conc} + ${step.end_conc}) / 2 = ${processedConc}`);
                } else if (step.trueDensity !== undefined) {
                  // Fallback to trueDensity if available
                  processedConc = step.trueDensity;
                  console.log(`Using trueDensity for step ${step.step}: ${processedConc}`);
                } else if (step.conc !== undefined) {
                  // Fallback to conc if available
                  processedConc = step.conc;
                  console.log(`Using conc for step ${step.step}: ${processedConc}`);
                } else if (existingTreatment && existingTreatment.conc > 0) {
                  // Preserve existing concentration if no new data
                  processedConc = existingTreatment.conc;
                  console.log(`Preserving existing conc for step ${step.step}: ${processedConc}`);
                }
                
                return {
                  id: step.id || (existingTreatment?.id) || Date.now() + Math.random(), // Use existing ID or generate one
                  stage_id: stageId || 0, // Ensure stage_id is never null
                  step: step.step,
                  type: step.type || '',
                  description: step.description || '',
                  rate: step.targetRate || step.rate || 0,
                  conc: processedConc,
                  clean_step: step.clean || step.clean_step || 0,
                  // PRESERVE ACTUAL VALUES - Don't overwrite with 0 if not provided in MQTT update
                  clean_actual: step.clean_actual !== undefined ? step.clean_actual : (existingTreatment?.clean_actual || 0),
                  slurry_step: step.slurry_step || 0,
                  slurry_actual: step.slurry_actual !== undefined ? step.slurry_actual : (existingTreatment?.slurry_actual || 0),
                  sand_step: (() => {
                    // Derive sand_step from clean * 42 * conc so Editing by Sand Step shows correct values
                    const concVal = step.targetStartConc !== step.targetEndConc
                      ? (step.targetStartConc + step.targetEndConc) / 2
                      : (step.targetStartConc ?? 0);
                    return Math.round((step.clean || step.clean_step || 0) * 42 * concVal);
                  })(),
                  sand_actual: step.sand_actual !== undefined ? step.sand_actual : (existingTreatment?.sand_actual || 0),
                  start_ts: step.start_ts || Date.now(),
                  end_ts: step.end_ts || Date.now(),
                  originalStep: step.originalStep !== undefined ? step.originalStep : (existingTreatment?.originalStep !== undefined ? existingTreatment.originalStep : step.step),
                  // Store start_conc and end_conc for Sand Ramp steps, preserving existing values if not provided
                  start_conc: step.targetStartConc !== undefined ? step.targetStartConc : (step.start_conc !== undefined ? step.start_conc : existingTreatment?.start_conc),
                  end_conc: step.targetEndConc !== undefined ? step.targetEndConc : (step.end_conc !== undefined ? step.end_conc : existingTreatment?.end_conc)
                };
              });
              
              setActualTreatments(updatedTreatments);
              
              // Update originalTreatments to the new schedule for proper change tracking
              setOriginalTreatments(updatedTreatments);
              
              // Also notify parent component of the update
              if (onTreatmentScheduleUpdate) {
                const optimisticUpdateData = updatedTreatments.map(treatment => ({
                  id: treatment.id,
                  stage_id: treatment.stage_id,
                  step: treatment.step,
                  type: treatment.type,
                  description: treatment.description,
                  rate: treatment.rate,
                  conc: treatment.conc,
                  clean_step: treatment.clean_step,
                  clean_actual: treatment.clean_actual,
                  slurry_step: treatment.slurry_step,
                  slurry_actual: treatment.slurry_actual,
                  sand_step: treatment.sand_step,
                  sand_actual: treatment.sand_actual,
                  elapsed_time: 0
                }));
                
                onTreatmentScheduleUpdate(optimisticUpdateData);
              }
              
              // Reload design schedule and treatment changes after a brief delay
              // to allow the microservice to post the data to the backend
              setTimeout(() => {
                console.log('Reloading design schedule and treatment changes after MQTT schedule update');
                
                // Reload design treatment schedule
                if (stageId) {
                  const token = localStorage.getItem('token');
                  if (token) {
                    axios.get(`${backendUrl}/stages/${stageId}/design_treatment_schedule`, {
                      headers: { Authorization: `Bearer ${token}` }
                    })
                    .then(designResponse => {
                      const designData = Array.isArray(designResponse.data) 
                        ? designResponse.data 
                        : [designResponse.data];
                      
                      setDesignTreatments(designData);
                      console.log('Design treatment schedule reloaded after MQTT schedule update:', designData);
                    })
                    .catch(error => {
                      console.error('Error reloading design treatment schedule after MQTT update:', error);
                    });
                    
                    // Reload treatment and chemical changes
                    fetchTreatmentChanges(stageId);
                    fetchChemicalChanges(stageId);
                  }
                }
              }, 2000); // 2 second delay to allow microservice to process
            }
          } catch (parseError) {
            console.error('Error parsing schedule update:', parseError);
            // Fallback to refreshing data from database
            setTimeout(() => {
              console.log('Fallback: Refreshing treatment schedules after MQTT schedule update');
              fetchTreatmentData();
            }, 1000);
          }
          
          return; // Don't process as PetrixData
        }
        
        // Handle chem messages
        if (topic.endsWith('/petrix/chem')) {
          try {
            const chemParsed = JSON.parse(payloadStr);
            if (Array.isArray(chemParsed)) {
              setRealTimeChemData(chemParsed);
            }
          } catch (e) {
            console.error('Error parsing chem message:', e);
          }
          return;
        }

        // Handle regular data messages
        const parsed = JSON.parse(payloadStr) as PetrixData;
        
        // Update real-time data
        setRealTimeData(parsed);
        
        // Store the last known values for the current step
        if (parsed.step !== undefined) {
          setLastStepValues(prev => {
            // Find the treatment step to get the clean_step value for progress calculation
            const treatment = actualTreatments.find(t => t.step === parsed.step);
            const cleanStepTarget = treatment?.clean_step || 1; // Default to 1 to avoid division by zero
            
            return {
              ...prev,
              [parsed.step]: {
                stepClean: parsed.stepClean,
                stepSlurry: parsed.stepSlurry,
                stepSand: parsed.stepSand,
                elapsedTime: parsed.elapsedTime,
                progress: (parsed.stepClean / cleanStepTarget) * 100
              }
            };
          });
        }
      } catch (error) {
        console.error('Error processing MQTT message:', error);
      }
    });
    
    client.on('error', (error) => {
      console.error('MQTT connection error:', error);
    });
    
    client.on('close', () => {
      console.log('MQTT connection closed');
    });
  };

  // Generate fake treatment data for demo/prototype purposes
  const generateFakeTreatmentData = () => {
    const now = Date.now();
    const fakeTimestamp = Math.floor(now / 1000);
    
    // Fake design treatments matching the reference screenshot
    const fakeDesignData: DesignTreatment[] = [
      { id: 1, stage_id: 0, creator: 'System', timestamp: fakeTimestamp, step: 1, type: 'Breakdown', description: '', rate: 15, conc: 0, clean_step: 20, clean_actual: 21, slurry_step: 20, slurry_actual: 21, sand_step: 0, sand_actual: 0 },
      { id: 2, stage_id: 0, creator: 'System', timestamp: fakeTimestamp, step: 2, type: 'Acid', description: '', rate: 10, conc: 0, clean_step: 24, clean_actual: 24, slurry_step: 24, slurry_actual: 24, sand_step: 0, sand_actual: 0 },
      { id: 3, stage_id: 0, creator: 'System', timestamp: fakeTimestamp, step: 3, type: 'Pad', description: '', rate: 95, conc: 0, clean_step: 700, clean_actual: 700, slurry_step: 700, slurry_actual: 700, sand_step: 0, sand_actual: 0 },
      { id: 4, stage_id: 0, creator: 'System', timestamp: fakeTimestamp, step: 4, type: 'Pad', description: '', rate: 100, conc: 0, clean_step: 495, clean_actual: 495, slurry_step: 497, slurry_actual: 497, sand_step: 1328, sand_actual: 0 },
      { id: 5, stage_id: 0, creator: 'System', timestamp: fakeTimestamp, step: 5, type: 'Proppant', description: '100 Mesh Native', rate: 105, conc: 0.20, clean_step: 500, clean_actual: 500, slurry_step: 505, slurry_actual: 504, sand_step: 3028, sand_actual: 4200 },
      { id: 6, stage_id: 0, creator: 'System', timestamp: fakeTimestamp, step: 6, type: 'Proppant', description: '100 Mesh Native', rate: 95, conc: 0.50, clean_step: 810, clean_actual: 810, slurry_step: 829, slurry_actual: 828, sand_step: 16999, sand_actual: 17010 },
      { id: 7, stage_id: 0, creator: 'System', timestamp: fakeTimestamp, step: 7, type: 'Proppant', description: '100 Mesh Native', rate: 95, conc: 0.75, clean_step: 500, clean_actual: 133, slurry_step: 517, slurry_actual: 137, sand_step: 4185, sand_actual: 15750 },
      { id: 8, stage_id: 0, creator: 'System', timestamp: fakeTimestamp, step: 8, type: 'Proppant', description: '100 Mesh Native', rate: 90, conc: 1.00, clean_step: 500, clean_actual: 0, slurry_step: 523, slurry_actual: 0, sand_step: 0, sand_actual: 21042 },
    ];
    
    // Fake actual treatments (similar but with actuals filled in)
    const fakeActualData: ActualTreatment[] = [
      { id: 1, stage_id: 0, step: 1, type: 'Breakdown', description: '', rate: 15, conc: 0, clean_step: 20, clean_actual: 21, slurry_step: 20, slurry_actual: 21, sand_step: 0, sand_actual: 0, start_ts: now - 5000000, end_ts: now - 4900000, originalStep: 1 },
      { id: 2, stage_id: 0, step: 2, type: 'Acid', description: '', rate: 10, conc: 0, clean_step: 24, clean_actual: 24, slurry_step: 24, slurry_actual: 24, sand_step: 0, sand_actual: 0, start_ts: now - 4900000, end_ts: now - 4700000, originalStep: 2 },
      { id: 3, stage_id: 0, step: 3, type: 'Pad', description: '', rate: 95, conc: 0, clean_step: 700, clean_actual: 700, slurry_step: 700, slurry_actual: 700, sand_step: 0, sand_actual: 0, start_ts: now - 4700000, end_ts: now - 4200000, originalStep: 3 },
      { id: 4, stage_id: 0, step: 4, type: 'Pad', description: '', rate: 100, conc: 0, clean_step: 495, clean_actual: 495, slurry_step: 497, slurry_actual: 497, sand_step: 1328, sand_actual: 0, start_ts: now - 4200000, end_ts: now - 3900000, originalStep: 4 },
      { id: 5, stage_id: 0, step: 5, type: 'Proppant', description: '100 Mesh Native', rate: 105, conc: 0.20, clean_step: 500, clean_actual: 500, slurry_step: 505, slurry_actual: 504, sand_step: 3028, sand_actual: 4200, start_ts: now - 3900000, end_ts: now - 3500000, originalStep: 5 },
      { id: 6, stage_id: 0, step: 6, type: 'Proppant', description: '100 Mesh Native', rate: 95, conc: 0.50, clean_step: 810, clean_actual: 810, slurry_step: 829, slurry_actual: 828, sand_step: 16999, sand_actual: 17010, start_ts: now - 3500000, end_ts: now - 2800000, originalStep: 6 },
      { id: 7, stage_id: 0, step: 7, type: 'Proppant', description: '100 Mesh Native', rate: 95, conc: 0.75, clean_step: 500, clean_actual: 133, slurry_step: 517, slurry_actual: 137, sand_step: 4185, sand_actual: 15750, start_ts: now - 2800000, end_ts: now - 2200000, originalStep: 7 },
      { id: 8, stage_id: 0, step: 8, type: 'Proppant', description: '100 Mesh Native', rate: 90, conc: 1.00, clean_step: 500, clean_actual: 0, slurry_step: 523, slurry_actual: 0, sand_step: 0, sand_actual: 21042, start_ts: now - 2200000, end_ts: now, originalStep: 8 },
    ];
    
    setDesignTreatments(fakeDesignData);
    setActualTreatments(fakeActualData);
    setOriginalTreatments(fakeActualData);
    setSelectedDesignTimestamp(fakeTimestamp);
    setSelectedDesignCreator('System');
    setUniqueDesignTimestamps([{ timestamp: fakeTimestamp, creator: 'System' }]);
    setLoading(false);
  };

  // Fetch treatment data when stageId changes or modal is shown
  useEffect(() => {
    if (show && !loading) {
      if (useFakeData) {
        // Load fake data for prototype/demo
        setLoading(true);
        setTimeout(() => {
          generateFakeTreatmentData();
        }, 300); // Small delay to show loading state
      } else if (stageId) {
        fetchTreatmentData();
      }
    }
  }, [stageId, show, useFakeData]);

  // Fetch well data separately
  useEffect(() => {
    if (wellId && show) {
      fetchWellData();
    }
  }, [wellId, show]);

  // Wells on this pad, for the header dropdown. pad_id only becomes known once
  // wellData lands, so this chains off it rather than off wellId.
  useEffect(() => {
    if (!show || useFakeData || !wellData?.pad_id) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${backendUrl}/pads/${wellData.pad_id}/wells`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => setPadWells(Array.isArray(res.data) ? res.data : []))
      .catch(err => console.error('Error fetching pad wells:', err));
  }, [show, wellData?.pad_id, useFakeData]);

  // Stages for whichever well is selected
  useEffect(() => {
    if (!show || useFakeData || !wellId) return;
    fetchStagesForWell(wellId).then(setWellStages);
  }, [show, wellId, useFakeData]);

  // Drop navigation when the modal closes or the parent points at a new stage
  useEffect(() => {
    setNavWellId(null);
    setNavStage(null);
  }, [show, propStageId, propWellId]);
  
  // Connect to MQTT after actualTreatments are loaded (only once)
  useEffect(() => {
    if (show && isRealTimeStreaming && actualTreatments.length > 0 && !mqttClient) {
      console.log('Treatment data loaded, now connecting to MQTT');
      connectToMqtt();
    }
  }, [actualTreatments, show, isRealTimeStreaming, mqttClient]);

  // Lazy-load chem schedule when Chemicals sub-tab is opened (re-fetches on design/actual/timestamp change)
  useEffect(() => {
    if (activeSubTab !== 'chemicals' || activeTab === 'proposal' || !stageId) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setChemSchedule(null);
    setChemLoading(true);
    // Pass the selected design timestamp so the backend can scope to the right design version
    const params = selectedDesignTimestamp ? `?timestamp=${selectedDesignTimestamp}` : '';
    axios
      .get(`${backendUrl}/stages/${stageId}/chem_schedule_pivoted${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => setChemSchedule(res.data))
      .catch(err => console.error('Error loading chem schedule:', err))
      .finally(() => setChemLoading(false));
  }, [activeSubTab, activeTab, stageId, selectedDesignTimestamp]);

  // Lazy-load the proposal on first visit per stage; ref guard so tabbing away
  // mid-edit and back doesn't clobber unsaved rows.
  useEffect(() => {
    if (!show || activeTab !== 'proposal' || !stageId || useFakeData) return;
    if (proposalLoadedForStage.current === stageId) return;
    proposalLoadedForStage.current = stageId;
    fetchProposalData();
  }, [show, activeTab, stageId, useFakeData]);

  useEffect(() => {
    proposalLoadedForStage.current = null;
    setIsProposalEditing(false);
    setProposalDirty(false);
    setSelectedProposalStep(null);
  }, [stageId, show]);
  
  // Fetch well data to get the well name
  const fetchWellData = async () => {
    if (!wellId) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        return;
      }
      
      const wellResponse = await axios.get(`${backendUrl}/wells/${wellId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setWellData(wellResponse.data);
    } catch (err) {
      console.error('Error fetching well data:', err);
    }
  };

  // Update chart when treatments or selected design changes
  useEffect(() => {
    if (activeTab === 'design' && designTreatments.length > 0) {
      const filteredTreatments = selectedDesignTimestamp 
        ? designTreatments.filter(t => t.timestamp === selectedDesignTimestamp)
        : designTreatments;
      
      prepareChartData(filteredTreatments, 'design');
    } else if (activeTab === 'actual' && actualTreatments.length > 0) {
      prepareChartData(actualTreatments, 'actual');
    }
  }, [activeTab, designTreatments, actualTreatments, selectedDesignTimestamp]);

  // Build chem chart options only when chemSchedule, tab, subTab, or theme changes.
  // NOT dependent on editedChemLoadings or realTimeData — prevents per-second rerenders.
  useEffect(() => {
    if (!chemSchedule || chemSchedule.chem_names.length === 0 || activeSubTab !== 'chemicals') return;

    const isDark       = theme === 'dark';
    const bgColor      = isDark ? '#303335' : '#ffffff';
    const textColor    = isDark ? '#ffffff' : '#303335';
    const gridColor    = '#5C646B';
    const seriesColors = ['#3E76FE','#8CC540','#FF5F4D','#BD9EFF','#F9C844','#FF9F43','#54A0FF'];
    const stepsData    = chemSchedule.steps;
    const isDesign     = activeTab === 'design';

    const chemSeries: Highcharts.SeriesOptionsType[] = chemSchedule.chem_names.map((name, i) => ({
      type:   'line' as const,
      name:   `${name} — Design (gpt)`,
      data:   stepsData.map(s => s.chems[name]?.design ?? null),
      yAxis:  1,
      color:  seriesColors[i % seriesColors.length],
      marker: { enabled: true, radius: 4 },
    }));

    if (!isDesign) {
      chemSchedule.chem_names.forEach((name, i) => {
        chemSeries.push({
          type:      'line' as const,
          name:      `${name} — Actual (gpt)`,
          data:      stepsData.map(s => s.chems[name]?.actual ?? null),
          yAxis:     1,
          color:     seriesColors[i % seriesColors.length],
          dashStyle: 'ShortDash' as const,
          marker:    { enabled: true, radius: 3 },
        });
      });
    }

    setChemChartOptions({
      title: {
        text:  `Chemical Schedule — ${isDesign ? 'Design' : 'Actual'}`,
        style: { color: textColor, fontWeight: '600' },
      },
      xAxis: {
        categories: stepsData.map(s => `Step ${s.step}`),
        labels:     { style: { color: textColor } },
        lineColor:  gridColor,
        tickColor:  gridColor,
        gridLineColor: gridColor,
      },
      yAxis: [
        {
          title:  { text: 'Clean Volume (gals)', style: { color: textColor } },
          labels: { style: { color: textColor } },
          min:    0,
          gridLineColor: gridColor,
        },
        {
          title:    { text: 'Loading (gpt)', style: { color: textColor } },
          labels:   { style: { color: textColor } },
          opposite: true,
          min:      0,
          gridLineColor: gridColor,
        },
      ],
      series: [
        {
          type:        'column' as const,
          name:        'Clean Volume (gals)',
          data:        stepsData.map(s => s.clean_step * 42),
          yAxis:       0,
          color:       '#3E76FE',
          opacity:     0.35,
          groupPadding: 0.05,
          pointPadding: 0.05,
          borderWidth:  0,
        } as Highcharts.SeriesOptionsType,
        ...chemSeries,
      ],
      credits:  { enabled: false },
      tooltip:  {
        shared:          true,
        valueDecimals:   3,
        backgroundColor: isDark ? '#141b2d' : '#ffffff',
        borderColor:     gridColor,
        style:           { color: textColor },
      },
      chart: {
        height:          320,
        backgroundColor: bgColor,
        borderRadius:    8,
        style:           { fontFamily: 'Inter, Space Grotesk, sans-serif' },
        animation:       false,
      } as Highcharts.ChartOptions,
      legend: {
        enabled:       true,
        align:         'center' as Highcharts.AlignValue,
        verticalAlign: 'top'   as Highcharts.VerticalAlignValue,
        itemStyle:     { color: textColor },
      },
      plotOptions: {
        column: { borderWidth: 0 },
        series: {
          animation: false,
          states:    { hover: { lineWidth: 3 } },
        },
      },
    });
  }, [chemSchedule, activeTab, activeSubTab, theme]);

  // Extract unique timestamps and creators when design treatments change
  useEffect(() => {
    if (designTreatments.length > 0) {
      const uniqueTimestamps = Array.from(
        new Set(designTreatments.map(t => t.timestamp))
      ).map(timestamp => {
        const treatment = designTreatments.find(t => t.timestamp === timestamp);
        return {
          timestamp,
          creator: treatment?.creator || 'Unknown'
        };
      });
      
      setUniqueDesignTimestamps(uniqueTimestamps);
      
      // Always set the most recent timestamp as selected by default
      if (uniqueTimestamps.length > 0) {
        const mostRecent = uniqueTimestamps.reduce((prev, current) => 
          prev.timestamp > current.timestamp ? prev : current
        );
        setSelectedDesignTimestamp(mostRecent.timestamp);
        setSelectedDesignCreator(mostRecent.creator);
      }
    }
  }, [designTreatments]);

  const fetchTreatmentData = async () => {
    if (!stageId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      // Fetch design treatment schedule
      const designResponse = await axios.get(`${backendUrl}/stages/${stageId}/design_treatment_schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Check if the response is an array or a single object
      const designData = Array.isArray(designResponse.data) 
        ? designResponse.data 
        : [designResponse.data]; // Convert single object to array if needed
      
      setDesignTreatments(designData);
      console.log('design data:', designData)

      // Fetch actual treatment schedule
      const actualResponse = await axios.get(`${backendUrl}/stages/${stageId}/actual_treatment_schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Raw actual response:', actualResponse.data);
      
      // Check if the response is an array or a single object
      const actualData = Array.isArray(actualResponse.data) 
        ? actualResponse.data 
        : [actualResponse.data]; // Convert single object to array if needed
      
      // Add originalStep property to each treatment step when first loaded
      // OriginalStep should be the initial step number when first loaded, not updated when steps are added/deleted
      const actualDataWithOriginalStep = actualData.map(treatment => {
        // Ensure conc is properly converted to number if it comes as string, but preserve original value
        const originalConc = treatment.conc;
        let processedConc = originalConc;
        
        // Only convert if it's actually a string and not already a valid number
        if (typeof originalConc === 'string' && originalConc.trim() !== '') {
          const parsed = parseFloat(originalConc);
          processedConc = isNaN(parsed) ? 0 : parsed;
        } else if (typeof originalConc === 'number') {
          processedConc = originalConc;
        } else if (originalConc === null || originalConc === undefined) {
          processedConc = 0;
        }
        
        const processedTreatment = {
          ...treatment,
          conc: processedConc,
          rate: typeof treatment.rate === 'string' ? parseFloat(treatment.rate) || 0 : (treatment.rate || 0),
          clean_step: typeof treatment.clean_step === 'string' ? parseFloat(treatment.clean_step) || 0 : (treatment.clean_step || 0),
          // Set originalStep to current step when loading from latest_design_schedule
          originalStep: treatment.step
        };
        
        console.log(`Processing treatment step ${treatment.step}:`, {
          original_conc: originalConc,
          original_conc_type: typeof originalConc,
          processed_conc: processedTreatment.conc,
          type: treatment.type,
          description: treatment.description,
          originalStep: processedTreatment.originalStep
        });
        
        return processedTreatment;
      });
      
      setActualTreatments(actualDataWithOriginalStep);
      console.log('actual data with processed values:', actualDataWithOriginalStep)
      
      // Store original treatments for change tracking
      setOriginalTreatments(actualDataWithOriginalStep);
      
      // Fetch treatment and chemical changes for this stage
      fetchTreatmentChanges(stageId);
      fetchChemicalChanges(stageId);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching treatment data:', err);
      setError('Failed to fetch treatment data. Please try again later.');
      setLoading(false);
    }
  };

  // ── Header navigation data ──────────────────────────────────────────────────
  const fetchStagesForWell = async (targetWellId: number) => {
    const token = localStorage.getItem('token');
    if (!token) return [];
    try {
      const res = await axios.get(`${backendUrl}/wells/${targetWellId}/stages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return (Array.isArray(res.data) ? res.data : [])
        .map((s: any) => ({ id: s.id, stage_number: Number(s.stage_number) }))
        .sort((a: any, b: any) => a.stage_number - b.stage_number);
    } catch (err) {
      console.error('Error fetching stages for well:', err);
      return [];
    }
  };

  const handleSelectWell = async (w: Well) => {
    if (w.id === wellId) return;
    const stages = await fetchStagesForWell(w.id);
    if (stages.length === 0) {
      toast.warning(`${w.well_name} has no stages`);
      return;
    }
    // Land on the same stage number where possible, otherwise the first one
    const match = stages.find((s: any) => s.stage_number === stageNumber) ?? stages[0];
    setWellStages(stages);
    setNavWellId(w.id);
    setNavStage({ id: match.id, number: match.stage_number });
  };

  const handleSelectStage = (s: { id: number; stage_number: number }) => {
    if (s.id === stageId) return;
    setNavStage({ id: s.id, number: s.stage_number });
  };

  // ── Proposal data ───────────────────────────────────────────────────────────
  const fetchProposalData = async () => {
    if (!stageId) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    setProposalLoading(true);
    try {
      const res = await axios.get(
        `${backendUrl}/stages/${stageId}/proposal_treatment_schedule`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const rows: ProposalTreatment[] = (Array.isArray(res.data) ? res.data : [])
        .map((r: any) => ({
          ...r,
          step:        Number(r.step) || 0,
          type:        r.type ?? '',
          description: r.description ?? '',
          rate:        r.rate == null ? 0 : Number(r.rate),
          conc:        r.conc == null ? 0 : Number(r.conc),
          clean_step:  r.clean_step == null ? 0 : Number(r.clean_step),
          start_conc:  r.start_conc == null ? undefined : Number(r.start_conc),
          end_conc:    r.end_conc == null ? undefined : Number(r.end_conc),
        }))
        .sort((a: ProposalTreatment, b: ProposalTreatment) => a.step - b.step);
      setProposalTreatments(rows);
    } catch (err) {
      console.error('Error fetching proposal treatment schedule:', err);
      setProposalTreatments([]);
    } finally {
      setProposalLoading(false);
    }
  };

  const handleSaveProposal = async () => {
    if (!stageId) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    setIsSavingProposal(true);
    try {
      const payload = [...proposalTreatments]
        .sort((a, b) => a.step - b.step)
        .map((t, i) => {
          // slurry/sand are derived here rather than server-side so the stored
          // numbers are exactly the ones the table shows. Same formula as the
          // Slurry Step / Sand Step columns, using the descriptionDictionary
          // lookup — NOT the 22.101 the Design tab hardcodes, and not the 42
          // Petrix divides by in design_treatment_schedule.
          const descriptionValue = getDescriptionValue(t.description || '');
          // Sand Ramp carries an explicit start/end split from the concentration
          // modal. Proppant/Rate Drop-Proppant have a single conc value, but the
          // backend/actual-schedule convention is start_conc === end_conc === conc
          // for those types — everything else has no concentration at all.
          const isProppant = t.type === 'Proppant' || t.type === 'Rate Drop-Proppant';
          const startConc = isProppant ? t.conc : (t.start_conc ?? 0);
          const endConc   = isProppant ? t.conc : (t.end_conc ?? 0);
          return {
            step:        i + 1,
            type:        t.type || null,
            description: t.description || null,
            rate:        t.rate,
            conc:        t.conc,
            clean_step:  t.clean_step,
            slurry_step: t.clean_step + (t.clean_step * t.conc / descriptionValue),
            sand_step:   t.clean_step * 42 * t.conc,
            start_conc:  startConc,
            end_conc:    endConc,
          };
        });

      await axios.post(
        `${backendUrl}/stages/${stageId}/proposal_treatment_schedule`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Proposal schedule saved');
      setIsProposalEditing(false);
      setProposalDirty(false);
      await fetchProposalData();
    } catch (err: any) {
      console.error('Error saving proposal treatment schedule:', err);
      toast.error(err?.response?.data?.message || 'Failed to save proposal schedule');
    } finally {
      setIsSavingProposal(false);
    }
  };

  const handleCancelProposalEdit = () => {
    setIsProposalEditing(false);
    setProposalDirty(false);
    setShowContextMenu(false);
    fetchProposalData();
  };

  // ── Proposal step handlers ──────────────────────────────────────────────────
  // Separate from handleAddStep/handleMoveUp/etc: those carry originalStep
  // bookkeeping, currentStep gating and edit-lock state the proposal has none of.
  const renumberProposal = (rows: ProposalTreatment[]): ProposalTreatment[] =>
    rows.map((r, i) => ({ ...r, step: i + 1 }));

  const blankProposalRow = (): ProposalTreatment => ({
    id: proposalTempIdRef.current--,
    stage_id: stageId as number,
    step: 0,
    type: '',
    description: '',
    rate: 0,
    conc: 0,
    clean_step: 0,
    clean_actual: 0,
    slurry_step: 0,
    slurry_actual: 0,
    sand_step: 0,
    sand_actual: 0,
    start_ts: 0,
    end_ts: 0,
  });

  // Signature matches handleEditTreatmentStep so the shared cell renderers
  // can take either one.
  const handleEditProposalStep = (id: number, field: string, value: any) => {
    setProposalTreatments(prev => prev.map(t => {
      if (t.id !== id) return t;
      const next: any = { ...t };
      next[field] = value;
      if (field === 'type') {
        // Leaving a proppant/sand-ramp type strands the description and
        // concentration — clear both. Only these four types (and their
        // Rate Drop variants) are ever allowed a nonzero conc.
        if (value !== 'Proppant' && value !== 'Rate Drop-Proppant' && value !== 'Sand Ramp' && value !== 'Rate Drop-Sand Ramp') {
          next.description = '';
          next.conc = 0;
          next.start_conc = 0;
          next.end_conc = 0;
        }
      }
      return next;
    }));
    setProposalDirty(true);
  };

  // Sand Ramp / Rate Drop-Sand Ramp steps get the pop-out concentration modal.
  const isProposalSandRamp = (type: string | undefined): boolean =>
    type === 'Sand Ramp' || type === 'Rate Drop-Sand Ramp';

  // Handle concentration click for Sand Ramp proposal steps — opens the same
  // shared ConcentrationModal instance the Actual tab uses, tagged so
  // handleConcentrationSave knows to write into proposalTreatments.
  const handleProposalConcentrationClick = (treatment: ProposalTreatment) => {
    if (isProposalSandRamp(treatment.type)) {
      setConcentrationTarget({ source: 'proposal', treatment });
      setShowConcentrationModal(true);
    }
  };

  const handleProposalRightClick = (e: React.MouseEvent, t: ProposalTreatment) => {
    if (activeTab !== 'proposal' || !isProposalEditing) return;
    e.preventDefault();
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setSelectedProposalStep(t);
    setShowContextMenu(true);
  };

  const handleProposalAddFirstStep = () => {
    setProposalTreatments(renumberProposal([blankProposalRow()]));
    setProposalDirty(true);
  };

  const handleProposalAddStep = () => {
    const idx = proposalTreatments.findIndex(t => t.id === selectedProposalStep?.id);
    if (idx === -1) return;
    const next = [...proposalTreatments];
    next.splice(idx + 1, 0, blankProposalRow());
    setProposalTreatments(renumberProposal(next));
    setProposalDirty(true);
    setShowContextMenu(false);
  };

  const handleProposalCopyStep = () => {
    const idx = proposalTreatments.findIndex(t => t.id === selectedProposalStep?.id);
    if (idx === -1 || !selectedProposalStep) return;
    const next = [...proposalTreatments];
    next.splice(idx + 1, 0, { ...selectedProposalStep, id: proposalTempIdRef.current-- });
    setProposalTreatments(renumberProposal(next));
    setProposalDirty(true);
    setShowContextMenu(false);
  };

  const handleProposalDeleteStep = () => {
    if (!selectedProposalStep) return;
    const removedId = selectedProposalStep.id;
    setProposalTreatments(prev => renumberProposal(prev.filter(t => t.id !== removedId)));
    setProposalDirty(true);
    setShowContextMenu(false);
  };

  const moveProposalStep = (delta: number) => {
    const idx = proposalTreatments.findIndex(t => t.id === selectedProposalStep?.id);
    const target = idx + delta;
    if (idx === -1 || target < 0 || target >= proposalTreatments.length) {
      setShowContextMenu(false);
      return;
    }
    const next = [...proposalTreatments];
    [next[idx], next[target]] = [next[target], next[idx]];
    setProposalTreatments(renumberProposal(next));
    setProposalDirty(true);
    setShowContextMenu(false);
  };

  const handleProposalMoveUp   = () => moveProposalStep(-1);
  const handleProposalMoveDown = () => moveProposalStep(1);

  const prepareChartData = (treatments: DesignTreatment[] | ActualTreatment[], type: 'design' | 'actual') => {
    // Sort treatments by step
    const sortedTreatments = [...treatments].sort((a, b) => a.step - b.step);
    
    // Prepare series data
    const cleanStepData: [number, number][] = [];
    const slurryStepData: [number, number][] = [];
    const sandStepData: [number, number][] = [];
    const rateData: [number, number][] = [];
    const concData: [number, number][] = [];
    
    // Calculate time values (commented out as not currently used in chart)
    sortedTreatments.forEach((treatment, index) => {
      // let timeValue: number;
      // 
      // if (type === 'design') {
      //   // For design: calculate time as clean_step / clean_rate * 60
      //   timeValue = treatment.clean_step / treatment.rate * 60;
      // } else {
      //   // For actual: calculate time as end_ts - start_ts
      //   const actualTreatment = treatment as ActualTreatment;
      //   timeValue = actualTreatment.end_ts && actualTreatment.start_ts 
      //     ? actualTreatment.end_ts - actualTreatment.start_ts 
      //     : treatment.clean_step / treatment.rate * 60;
      // }
      
      cleanStepData.push([index, treatment.clean_step]);
      slurryStepData.push([index, treatment.slurry_step]);
      sandStepData.push([index, treatment.sand_step]);
      rateData.push([index, treatment.rate]);
      concData.push([index, treatment.conc]);
    });

    // Get theme-based colors using Michael's color scheme
    const isDarkTheme = theme === 'dark';
    const backgroundColor = isDarkTheme ? '#303335' : '#ffffff'; // Black for dark theme, white for light
    const textColor = isDarkTheme ? '#ffffff' : '#303335'; // White text for dark theme, black for light
    const gridLineColor = '#5C646B'; // Light Gray for bounding boxes and borders
    
    // Series colors using Michael's palette
    const seriesColors = [
      '#3E76FE', // Primary blue
      '#8CC540', // Eye-friendly green
      '#FF5F4D', // Red for error states
      '#BD9EFF', // Purple accent
      '#F9C844', // Yellow for warnings
    ];
    
    // Update chart options
    setChartOptions({
      title: { 
        text: `Treatment ${type === 'design' ? 'Design' : 'Actual'}`,
        style: { color: textColor, fontWeight: '600' }
      },
      xAxis: { 
        categories: sortedTreatments.map(t => `Step ${t.step}`),
        title: { 
          text: 'Step',
          style: { color: textColor }
        },
        labels: { style: { color: textColor } },
        lineColor: gridLineColor,
        tickColor: gridLineColor,
        gridLineColor: gridLineColor,
      },
      yAxis: [
        { 
          // Left y-axis for steps
          title: { 
            text: 'Volume',
            style: { color: textColor }
          },
          labels: { 
            style: { color: textColor }
          },
          min: 0,
          gridLineColor: gridLineColor
        },
        { 
          // Right y-axis for rate and concentration
          title: { 
            text: 'Rate / Concentration',
            style: { color: textColor }
          },
          labels: { 
            style: { color: textColor }
          },
          opposite: true,
          min: 0,
          gridLineColor: gridLineColor
        }
      ],
      series: [
        {
          type: 'column',
          name: 'Clean Step',
          data: cleanStepData,
          yAxis: 0,
          color: seriesColors[0]
        },
        {
          type: 'column',
          name: 'Slurry Step',
          data: slurryStepData,
          yAxis: 0,
          color: seriesColors[1]
        },
        {
          type: 'column',
          name: 'Sand Step',
          data: sandStepData,
          yAxis: 0,
          color: seriesColors[2]
        },
        {
          type: 'line',
          name: 'Rate',
          data: rateData,
          yAxis: 1,
          color: seriesColors[3],
          marker: {
            enabled: true
          }
        },
        {
          type: 'line',
          name: 'Concentration',
          data: concData,
          yAxis: 1,
          color: seriesColors[4],
          marker: {
            enabled: true
          }
        }
      ],
      credits: { enabled: false },
      tooltip: {
        shared: true,
        valueDecimals: 2,
        backgroundColor: isDarkTheme ? '#141b2d' : '#ffffff',
        borderColor: gridLineColor,
        style: { color: textColor }
      },
      chart: { 
        height: 400,
        backgroundColor: backgroundColor,
        borderRadius: 8,
        style: { fontFamily: 'Inter, Space Grotesk, sans-serif' }
      } as Highcharts.ChartOptions,
      legend: {
        enabled: true,
        align: 'center' as Highcharts.AlignValue,
        verticalAlign: 'top' as Highcharts.VerticalAlignValue,
        layout: 'horizontal',
        itemStyle: { color: textColor },
        itemHoverStyle: { color: seriesColors[0] }
      },
      plotOptions: {
        column: {
          grouping: false,
          pointPadding: 0.1,
          borderWidth: 0
        },
        series: {
          states: {
            hover: {
              lineWidth: 3
            }
          }
        }
      },
      colors: seriesColors
    });
  };

  const handleDesignSelect = (timestamp: number, creator: string) => {
    setSelectedDesignTimestamp(timestamp);
    setSelectedDesignCreator(creator);
  };

  const formatTimestamp = (timestamp: number): string => {
    // Check if timestamp is in seconds (Unix timestamp) and convert to milliseconds if needed
    const timestampInMs = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
    return new Date(timestampInMs).toLocaleString();
  };

  // Handle editing of treatment steps
  const handleEditTreatmentStep = (id: number, field: string, value: string | number) => {
    setEditedTreatmentSteps(prev => {
      const newState = {
        ...prev,
        [id]: {
          ...prev[id],
          [field]: value
        }
      };
      
      // If changing type field, check if we need to clear the description
      if (field === 'type') {
        const treatment = actualTreatments.find(t => t.id === id);
        const currentType = prev[id]?.type ?? treatment?.type;
        const newType = value as string;
        
        // If moving FROM Proppant/Sand Ramp TO another type, clear description
        if ((currentType === 'Proppant' || currentType === 'Rate Drop-Proppant' || currentType === 'Sand Ramp' || currentType === 'Rate Drop-Sand Ramp') && 
            newType !== 'Proppant' && newType !== 'Rate Drop-Proppant' && newType !== 'Sand Ramp' && newType !== 'Rate Drop-Sand Ramp') {
          newState[id].description = '';
        }
      }
      
      return newState;
    });
  };

  // Check if a step is editable (only when user has edit lock and only current steps and onward)
  const isStepEditable = (step: number): boolean => {
    if (!hasEditLock) return false;
    if (!isRealTimeStreaming) return false;
    return currentStep === null || step >= currentStep;
  };
  
  // Handle right click on a treatment step
  const handleRightClick = (e: React.MouseEvent, treatment: ActualTreatment) => {
    if (!isRealTimeStreaming || activeTab !== 'actual') return;
    
    // Only allow context menu when user has edit lock
    if (!hasEditLock) return;
    
    // Only allow context menu for steps after the current step + 2
    if (currentStep !== null && treatment.step <= currentStep + 2) {
      return;
    }
    
    e.preventDefault();
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setSelectedStepForContextMenu(treatment);
    setShowContextMenu(true);
  };
  
  // Handle click outside context menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setShowContextMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Add a new blank step below the selected step
  const handleAddStep = () => {
    if (!selectedStepForContextMenu || !stageId) return;
    
    // Find the index of the selected step
    const selectedIndex = actualTreatments.findIndex(t => t.id === selectedStepForContextMenu.id);
    if (selectedIndex === -1) return;
    
    // Create a new step with blank values
    const newStep: ActualTreatment & { originalStep: number } = {
      id: Date.now(), // Temporary ID until saved to backend
      stage_id: stageId,
      step: selectedStepForContextMenu.step + 1, // Step number is one more than the selected step
      type: '',
      description: '',
      rate: 0,
      conc: 0,
      clean_step: 0,
      clean_actual: 0,
      slurry_step: 0,
      slurry_actual: 0,
      sand_step: 0,
      sand_actual: 0,
      start_ts: Date.now(),
      end_ts: Date.now(),
      originalStep: 0 // New steps should have originalStep = 0
    };
    
    // Create a deep copy of the current treatments to avoid mutating the original
    const updatedTreatments = actualTreatments.map(treatment => ({ ...treatment }));
    
    // Increment step numbers for all steps after the inserted one
    for (let i = 0; i < updatedTreatments.length; i++) {
      if (updatedTreatments[i].step > selectedStepForContextMenu.step) {
        updatedTreatments[i].step += 1;
      }
    }
    
    // Insert the new step
    updatedTreatments.splice(selectedIndex + 1, 0, newStep);
    
    // Update state
    setActualTreatments(updatedTreatments);
    setShowContextMenu(false);
    
    // Mark the new step as edited so it will be saved
    setEditedTreatmentSteps(prev => ({
      ...prev,
      [newStep.id]: {
        type: newStep.type,
        description: newStep.description,
        rate: newStep.rate,
        conc: newStep.conc,
        clean_step: newStep.clean_step
      }
    }));
    
    // Show success toast
    toast.success("New step added. Don't forget to save your changes.", {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };
  
  // Copy the selected step and add it below
  const handleCopyStep = () => {
    if (!selectedStepForContextMenu || !stageId) return;
    
    // Find the index of the selected step
    const selectedIndex = actualTreatments.findIndex(t => t.id === selectedStepForContextMenu.id);
    if (selectedIndex === -1) return;
    
    // Create a copy of the selected step
    const copiedStep: ActualTreatment & { originalStep: number } = {
      ...selectedStepForContextMenu,
      id: Date.now(), // Temporary ID until saved to backend
      step: selectedStepForContextMenu.step + 1, // Step number is one more than the selected step
      originalStep: 0 // Copied steps should have originalStep = 0
    };
    
    // Create a deep copy of the current treatments to avoid mutating the original
    const updatedTreatments = actualTreatments.map(treatment => ({ ...treatment }));
    
    // Increment step numbers for all steps after the inserted one
    for (let i = 0; i < updatedTreatments.length; i++) {
      if (updatedTreatments[i].step > selectedStepForContextMenu.step) {
        updatedTreatments[i].step += 1;
      }
    }
    
    // Insert the copied step
    updatedTreatments.splice(selectedIndex + 1, 0, copiedStep);
    
    // Update state
    setActualTreatments(updatedTreatments);
    setShowContextMenu(false);
    
    // Mark the copied step as edited so it will be saved
    setEditedTreatmentSteps(prev => ({
      ...prev,
      [copiedStep.id]: {
        type: copiedStep.type,
        description: copiedStep.description,
        rate: copiedStep.rate,
        conc: copiedStep.conc,
        clean_step: copiedStep.clean_step
      }
    }));
    
    // Show success toast
    toast.success("Step copied. Don't forget to save your changes.", {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };
  
  // Delete the selected step
  const handleDeleteStep = () => {
    if (!selectedStepForContextMenu) return;
    
    // Create a deep copy of the current treatments to avoid mutating the original
    const updatedTreatments = actualTreatments
      .filter(t => t.id !== selectedStepForContextMenu.id)
      .map(treatment => ({ ...treatment })); // Create a copy of each treatment object
    
    // Decrement step numbers for all steps after the deleted one
    for (let i = 0; i < updatedTreatments.length; i++) {
      if (updatedTreatments[i].step > selectedStepForContextMenu.step) {
        updatedTreatments[i].step -= 1;
      }
    }
    
    // Update state
    setActualTreatments(updatedTreatments);
    setShowContextMenu(false);
    
    // Mark that changes have been made by adding a flag to editedTreatmentSteps
    // This ensures the Save button remains enabled after deletion
    setEditedTreatmentSteps(prev => ({
      ...prev,
      _hasChanges: true // Add a flag to indicate changes have been made
    }));
  };
  
  // Move the selected step up one position
  const handleMoveUp = () => {
    if (!selectedStepForContextMenu) return;
    
    // Find the index of the selected step
    const selectedIndex = actualTreatments.findIndex(t => t.id === selectedStepForContextMenu.id);
    if (selectedIndex === -1 || selectedIndex === 0) return; // Can't move up if it's the first step
    
    // Create a deep copy of the treatments array to avoid mutation
    const updatedTreatments = actualTreatments.map(treatment => ({ ...treatment }));
    
    // Get the step above and the current step
    const stepAbove = updatedTreatments[selectedIndex - 1];
    const currentStep = updatedTreatments[selectedIndex];
    
    // Swap their step numbers
    const tempStepNumber = stepAbove.step;
    stepAbove.step = currentStep.step;
    currentStep.step = tempStepNumber;
    
    // Swap their positions in the array
    updatedTreatments[selectedIndex - 1] = currentStep;
    updatedTreatments[selectedIndex] = stepAbove;
    
    // Update state
    setActualTreatments(updatedTreatments);
    setShowContextMenu(false);
    
    // Mark that changes have been made to enable the Save button
    setEditedTreatmentSteps(prev => ({
      ...prev,
      _hasChanges: true
    }));
    
    // Show success toast
    toast.success("Step moved up. Don't forget to save your changes.", {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };
  
  // Move the selected step down one position
  const handleMoveDown = () => {
    if (!selectedStepForContextMenu) return;
    
    // Find the index of the selected step
    const selectedIndex = actualTreatments.findIndex(t => t.id === selectedStepForContextMenu.id);
    if (selectedIndex === -1 || selectedIndex === actualTreatments.length - 1) return; // Can't move down if it's the last step
    
    // Create a deep copy of the treatments array to avoid mutation
    const updatedTreatments = actualTreatments.map(treatment => ({ ...treatment }));
    
    // Get the step below and the current step
    const stepBelow = updatedTreatments[selectedIndex + 1];
    const currentStep = updatedTreatments[selectedIndex];
    
    // Swap their step numbers
    const tempStepNumber = stepBelow.step;
    stepBelow.step = currentStep.step;
    currentStep.step = tempStepNumber;
    
    // Swap their positions in the array
    updatedTreatments[selectedIndex + 1] = currentStep;
    updatedTreatments[selectedIndex] = stepBelow;
    
    // Update state
    setActualTreatments(updatedTreatments);
    setShowContextMenu(false);
    
    // Mark that changes have been made to enable the Save button
    setEditedTreatmentSteps(prev => ({
      ...prev,
      _hasChanges: true
    }));
    
    // Show success toast
    toast.success("Step moved down. Don't forget to save your changes.", {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  // ── buildScheduleWithChems ────────────────────────────────────────────────
  // Shared helper used by both treatment saves and chem saves.
  // chemOverride lets a chem edit inject its updated loading before the DB echo.
  const buildScheduleWithChems = (
    treatments: ActualTreatment[],
    currentChemSchedule: ChemSchedulePivoted | null,
    chemOverride?: { step: number; chemName: string; loading: number }
  ) => {
    return treatments.map(t => {
      // Use rawScheduleChemicals first (preserves groups from MQTT echo).
      // Fall back to DB pivot (chemSchedule) only if no MQTT data for this step.
      const stepRawChems = rawScheduleChemicals[t.step] || [];
      const chemicals: {name: string, group: string, ratio: number}[] = [];

      if (stepRawChems.length > 0) {
        const chemsByName: Record<string, {name: string, group: string, ratio: number}[]> = {};
        stepRawChems.forEach(c => {
          if (!chemsByName[c.name]) chemsByName[c.name] = [];
          chemsByName[c.name].push(c);
        });
        Object.entries(chemsByName).forEach(([chemName, groups]) => {
          groups.forEach(g => {
            const isOverride = chemOverride?.step === t.step && chemOverride?.chemName === chemName;
            chemicals.push({ name: chemName, group: g.group, ratio: isOverride ? chemOverride!.loading : g.ratio });
          });
        });
      } else if (currentChemSchedule) {
        const chemStep = currentChemSchedule.steps.find(s => s.step === t.step);
        currentChemSchedule.chem_names
          .filter(name => {
            const entry = chemStep?.chems[name];
            return (entry?.design != null) || (chemOverride?.step === t.step && chemOverride?.chemName === name);
          })
          .forEach(name => {
            const isOverride = chemOverride?.step === t.step && chemOverride?.chemName === name;
            const loading = isOverride ? chemOverride!.loading : (chemStep?.chems[name]?.design ?? 0);
            chemicals.push({ name, group: '', ratio: loading });
          });
      }

      // Determine originalStep
      let originalStep: string | number;
      if ((t as any).originalStep !== undefined) {
        originalStep = (t as any).originalStep;
      } else {
        originalStep = t.id > 1000000000000 ? 0 : 0;
      }

      // Handle Sand Ramp concentrations
      const editedStep = editedTreatmentSteps[t.id] || {};
      const rate = editedStep.rate !== undefined ? editedStep.rate : t.rate;
      const conc = editedStep.conc !== undefined ? editedStep.conc : t.conc;
      const type = editedStep.type !== undefined ? editedStep.type : t.type;
      const description = editedStep.description !== undefined ? editedStep.description : (t.description || '');

      // In editBySand mode, clean_step is never written — back-calculate from sand_step
      const isProppantOrSandRamp = type === 'Proppant' || type === 'Rate Drop-Proppant' || type === 'Sand Ramp' || type === 'Rate Drop-Sand Ramp';
      const clean = (editBySand && isProppantOrSandRamp && editedStep.sand_step !== undefined)
        ? (conc > 0 ? editedStep.sand_step / (42 * conc) : 0)
        : (editedStep.clean_step !== undefined ? editedStep.clean_step : t.clean_step);

      let targetStartConc: number;
      let targetEndConc: number;

      if (type === 'Sand Ramp' || type === 'Rate Drop-Sand Ramp') {
        // Only Sand Ramp steps have a split start/end conc — Proppant always has matching values.
        // Priority: explicitly edited value > original step value > conc fallback.
        targetStartConc = editedStep.start_conc !== undefined
          ? editedStep.start_conc
          : (t.start_conc !== undefined ? t.start_conc : conc);
        targetEndConc = editedStep.end_conc !== undefined
          ? editedStep.end_conc
          : (t.end_conc !== undefined ? t.end_conc : conc);
      } else {
        targetStartConc = conc;
        targetEndConc = conc;
      }

      return {
        step: t.step,
        type,
        description,
        targetRate: rate,
        targetStartConc,
        targetEndConc,
        clean,
        trueDensity: 0,
        originalStep,
        chemicals,
      };
    });
  };

  // ── Chem Edit Lock helpers ──────────────────────────────────────────────────
  const requestChemEditLock = async (): Promise<boolean> => {
    if (!stageId) return false;
    try {
      const token = localStorage.getItem('token');
      if (!token) { toast.error('Authentication token not found'); return false; }
      const vanId = localStorage.getItem('currentVanId') || 'DV1017';
      const response = await axios.post(
        `${backendUrl}/stages/mqtt/${stageId}/request_edit_lock`,
        { vanId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.lockAcquired) {
        setHasChemEditLock(true);
        startLockHeartbeat();
        toast.success(response.data.message || 'Chemical edit lock acquired');
        return true;
      } else {
        toast.error(response.data.message || 'Could not acquire edit lock');
        return false;
      }
    } catch (error) {
      toast.error('Failed to request edit lock');
      return false;
    }
  };

  const releaseChemEditLock = async (): Promise<void> => {
    if (!stageId) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const vanId = localStorage.getItem('currentVanId') || 'DV1017';
      await axios.post(
        `${backendUrl}/stages/mqtt/${stageId}/release_lock`,
        { vanId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error releasing chem edit lock:', error);
    }
    setHasChemEditLock(false);
    setEditedChemLoadings({});
    setEditedChemGroupRatios({});
    stopLockHeartbeat();
  };

  // ── handleSaveChemSchedule ────────────────────────────────────────────────
  // Sends the full treatment schedule + updated chemical loadings to Petrix via MQTT.
  // Poster-worker persists both tables after the echo — same pattern as treatment save.
  const handleSaveChemSchedule = async () => {
    if (!stageId || !chemSchedule) return;

    const token = localStorage.getItem('token');
    if (!token) { toast.error('Authentication token not found'); return; }

    let vanId = '';
    if (mqttClient) {
      const topic = localStorage.getItem('mqttTopic') || '';
      const match = topic.match(/propilot\/([^\/]+)\//);
      if (match) vanId = match[1];
    }
    if (!vanId) vanId = localStorage.getItem('currentVanId') || '';
    if (!vanId) { toast.error('Could not determine Van ID'); return; }

    setIsSavingChem(true);
    try {
      // Build per-step change map — only report values that actually changed,
      // strip trailing zeros, group by step number (mirrors TreatmentDataGraph).
      const changesByStep: Record<number, string[]> = {};
      const fmtVal = (v: number) => parseFloat(v.toFixed(3)).toString();
      const addChange = (step: number, label: string, oldVal: number, newVal: number) => {
        if (oldVal === newVal) return; // skip unchanged
        if (!changesByStep[step]) changesByStep[step] = [];
        changesByStep[step].push(`${label}: ${fmtVal(oldVal)} -> ${fmtVal(newVal)} gpt`);
      };

      Object.entries(editedChemLoadings).forEach(([key, newVal]) => {
        const [stepStr, name] = key.split('::');
        const step = Number(stepStr);
        const chemStep = chemSchedule.steps.find(s => s.step === step);
        const oldVal = chemStep?.chems?.[name]?.design
          ?? (rawScheduleChemicals[step] || []).find(c => c.name === name && c.group === '')?.ratio
          ?? 0;
        addChange(step, name, oldVal, newVal);
      });

      Object.entries(editedChemGroupRatios).forEach(([key, groups]) => {
        const [stepStr, name] = key.split('::');
        const step = Number(stepStr);
        const origGroups = (rawScheduleChemicals[step] || []).filter(c => c.name === name);
        groups.forEach(g => {
          const oldVal = origGroups.find(o => o.group === g.group)?.ratio ?? 0;
          const label = g.group ? `${name} (${g.group})` : name;
          addChange(step, label, oldVal, g.ratio);
        });
      });

      // Collapse consecutive steps with identical change sets into a range so a
      // cascade across many steps reads as one block, e.g.
      // "Steps 4-8:\n  AG-1 (Backup): 5 -> 4 gpt\n  FR: 7 -> 5 gpt"
      const sortedChemEntries = Object.entries(changesByStep)
        .map(([step, parts]) => ({ step: Number(step), parts }))
        .sort((a, b) => a.step - b.step);
      const chemGroups: { start: number; end: number; parts: string[] }[] = [];
      for (const entry of sortedChemEntries) {
        const last = chemGroups[chemGroups.length - 1];
        const sig = [...entry.parts].sort().join('|');
        const lastSig = last ? [...last.parts].sort().join('|') : '';
        if (last && last.end + 1 === entry.step && sig === lastSig) {
          last.end = entry.step;
        } else {
          chemGroups.push({ start: entry.step, end: entry.step, parts: entry.parts });
        }
      }
      const changedChems = chemGroups
        .map(g => {
          const label = g.start === g.end ? `Step ${g.start}` : `Steps ${g.start}-${g.end}`;
          return `${label}:\n  ${g.parts.join('\n  ')}`;
        })
        .join('\n');

      // ── Petrix 7.0.1 ── No chemical array support — post as a plain message
      if (!petrixHasChemSupport) {
        const message = `Chemical loading update requested:\n${
          Object.entries(editedChemLoadings).map(([key, val]) => {
            const [step, name] = key.split('::');
            return `Step ${step} - ${name}: ${val.toFixed(3)} gpt`;
          }).join('\n')
        }`;
        const resp = await axios.post(
          `${backendUrl}/mqtt/${vanId}/post_message`,
          { message },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (resp.data === true) {
          setEditedChemLoadings({});
          setEditedChemGroupRatios({});
          if (stageId) fetchChemicalChanges(stageId);
          toast.success(`Chemical update sent as message to field operator (Petrix 7.0.1): ${changedChems}`, {
            position: 'top-right', autoClose: 5000,
          });
        } else {
          toast.error('Failed to send chemical update message', { position: 'top-right' });
        }
        return;
      }

      // ── Petrix 8.0 ── Full chemical array support — send via actual_treatment_schedule
      // Every step now has all chemicals+groups in rawScheduleChemicals — use it directly.

      const scheduleWithChems = actualTreatments.map(t => {
        const editedStep = editedTreatmentSteps[t.id] || {};
        const rate  = editedStep.rate  ?? t.rate;
        const conc  = editedStep.conc  ?? t.conc;
        const clean = editedStep.clean_step ?? t.clean_step;
        const type  = editedStep.type  ?? t.type;
        const desc  = editedStep.description ?? (t.description || '');
        let targetStartConc = conc, targetEndConc = conc;
        if (type === 'Sand Ramp' || type === 'Rate Drop-Sand Ramp') {
          targetStartConc = (t as any).start_conc ?? conc;
          targetEndConc   = (t as any).end_conc   ?? conc;
        }
        const origStep = (t as any).originalStep ?? t.step;

        const stepRawChems = rawScheduleChemicals[t.step] || [];
        const chemicals: {name: string, group: string, ratio: number}[] = [];

        if (stepRawChems.length > 0) {
          const chemsByName: Record<string, {name: string, group: string, ratio: number}[]> = {};
          stepRawChems.forEach(c => {
            if (!chemsByName[c.name]) chemsByName[c.name] = [];
            chemsByName[c.name].push(c);
          });
          Object.entries(chemsByName).forEach(([chemName, groups]) => {
            const k = `${t.step}::${chemName}`;
            if (groups.length > 1) {
              // Multi-group: use edited ratios if available, else originals from schedule
              const editedGroups = editedChemGroupRatios[k];
              const source = editedGroups ?? groups.map(g => ({ group: g.group, ratio: g.ratio }));
              source.forEach(g => chemicals.push({ name: chemName, group: g.group, ratio: g.ratio }));
            } else {
              const loading = editedChemLoadings[k] !== undefined
                ? editedChemLoadings[k]
                : groups[0].ratio;
              chemicals.push({ name: chemName, group: groups[0].group, ratio: loading });
            }
          });
        } else {
          // No schedule topic data — fall back to DB chem schedule
          const chemStep = chemSchedule.steps.find(s => s.step === t.step);
          chemSchedule.chem_names
            .filter(name => {
              const entry = chemStep?.chems?.[name];
              return (entry?.design != null) || (`${t.step}::${name}` in editedChemLoadings);
            })
            .forEach(name => {
              const k = `${t.step}::${name}`;
              const loading = editedChemLoadings[k] !== undefined
                ? editedChemLoadings[k]
                : (chemStep?.chems?.[name]?.design ?? 0);
              chemicals.push({ name, group: '', ratio: loading });
            });
        }

        return {
          step: t.step, type, description: desc,
          targetRate: rate, targetStartConc, targetEndConc,
          clean, trueDensity: 0, originalStep: origStep,
          chemicals,
        };
      });

      const response = await axios.post(
        `${backendUrl}/stages/mqtt/${stageId}/actual_treatment_schedule`,
        {
          vanId,
          schedule:       scheduleWithChems,
          changes:        `Chemical loadings updated: ${changedChems}`,
          is_chem_change: true,   // flags backend to write to chemical_changes table
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // rawScheduleChemicals is updated automatically when Petrix echoes the saved
        // schedule back on the MQTT schedule topic — no manual update needed here.
        setEditedChemLoadings({});
        setEditedChemGroupRatios({});
        toast.success(`Success - Chemical loadings updated: ${changedChems}`, {
          position: 'top-right', autoClose: 5000,
        });
        // Refresh chemical changes from the dedicated table
        if (stageId) fetchChemicalChanges(stageId);
        // NOTE: lock is NOT released — user can continue editing without clicking Edit again
      } else {
        toast.error(`Update rejected: ${response.data.message}`, { position: 'top-right' });
      }
    } catch (err: any) {
      console.error('Chem save failed:', err);
      toast.error(
        `Error - ${err.response?.data?.message || err.message || 'Failed to save chemical schedule'}`,
        { position: 'top-right' }
      );
    } finally {
      setIsSavingChem(false);
    }
  };


  // Save edited treatment steps
  const handleSaveTreatmentSteps = async () => {
    console.log('=== SAVE FUNCTION CALLED ===');
    console.log('stageId:', stageId);
    console.log('editedTreatmentSteps:', editedTreatmentSteps);
    console.log('isRealTimeStreaming:', isRealTimeStreaming);
    
    if (!stageId) {
      console.log('No stageId, checking if should return early...');
      // If no changes and not in real-time streaming mode, don't do anything
      if (Object.keys(editedTreatmentSteps).length === 0 && !isRealTimeStreaming) {
        console.log('Returning early - no changes and not real-time streaming');
        return;
      }
    }

    try {
      setIsSavingSchedule(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        setIsSavingSchedule(false);
        return;
      }

      // Clean up editedTreatmentSteps to remove entries for steps that no longer exist
      const currentStepIds = new Set(actualTreatments.map(t => t.id));
      const cleanedEditedSteps: {[stepId: number]: {type?: string, description?: string, rate?: number, conc?: number, clean_step?: number, start_conc?: number, end_conc?: number}, _hasChanges?: boolean} = {};
      
      // Only keep entries for steps that still exist, excluding the _hasChanges flag
      Object.entries(editedTreatmentSteps).forEach(([stepId, value]) => {
        if (stepId === '_hasChanges') {
          // Keep the _hasChanges flag if it exists
          cleanedEditedSteps._hasChanges = value as boolean;
        } else {
          const numericStepId = parseInt(stepId);
          if (currentStepIds.has(numericStepId)) {
            cleanedEditedSteps[numericStepId] = value as {type?: string, description?: string, rate?: number, conc?: number, clean_step?: number, start_conc?: number, end_conc?: number};
          }
        }
      });
      
      // Update the editedTreatmentSteps state with cleaned data
      setEditedTreatmentSteps(cleanedEditedSteps);

      // Create updated treatment steps with edited values applied
      const updatedTreatments = actualTreatments.map(treatment => {
        const editedStep = cleanedEditedSteps[treatment.id];
        if (editedStep) {
          return {
            ...treatment,
            type: editedStep.type !== undefined ? editedStep.type : treatment.type,
            description: editedStep.description !== undefined ? editedStep.description : treatment.description,
            rate: editedStep.rate !== undefined ? editedStep.rate : treatment.rate,
            conc: editedStep.conc !== undefined ? editedStep.conc : treatment.conc,
            clean_step: editedStep.clean_step !== undefined ? editedStep.clean_step : treatment.clean_step,
            start_conc: editedStep.start_conc !== undefined ? editedStep.start_conc : treatment.start_conc,
            end_conc: editedStep.end_conc !== undefined ? editedStep.end_conc : treatment.end_conc
          };
        }
        return treatment;
      });

      if (isRealTimeStreaming) {
        // For real-time streaming, use the same endpoint as in TreatmentDataGraph
        // Log edited treatment steps for debugging
        console.log('Edited treatment steps before transformation:', editedTreatmentSteps);
        console.log('Updated treatments before transformation:', updatedTreatments);
        
        // Transform the data to match the expected format, embedding chemicals
        const transformedSchedule = buildScheduleWithChems(updatedTreatments, chemSchedule);
        
        // Log the final transformed schedule
        console.log('Final transformed schedule:', transformedSchedule);

        // Extract the van_id from the MQTT subscription topic
        // The topic format is "propilot/{vanId}/petrix/data"
        let vanId = "DEFAULT_VAN";
        
        // Check if we have an active MQTT client
        if (mqttClient) {
          // Since we can't directly access subscriptions in the MQTT client,
          // we'll use the topic we know we subscribed to in the connectToMqtt function
          const topic = localStorage.getItem('mqttTopic');
          if (topic && topic.includes('/petrix/data')) {
            // Extract the van_id from the topic
            const parts = topic.split('/');
            if (parts.length >= 3) {
              vanId = parts[1]; // The van_id is the second part of the topic
              console.log('Extracted vanId from MQTT topic:', vanId);
            }
          }
        } else {
          // Fallback to localStorage if no MQTT client
          const vanIdFromStorage = localStorage.getItem('currentVanId');
          if (vanIdFromStorage) {
            vanId = vanIdFromStorage;
            console.log('Using vanId from localStorage:', vanId);
          }
        }

        // Generate change description by comparing updated treatments with original treatments
        const changeDescription = compareAndGenerateChanges(updatedTreatments, originalTreatments);
        
        // Prepare the payload
        const payload = {
          vanId: vanId,
          schedule: transformedSchedule,
          changes: changeDescription
        };
        
        // Log the final payload being sent to the backend
        console.log('Payload being sent to backend:', payload);
        
        // Send the request to the backend
        const response = await axios.post(
          `${backendUrl}/stages/mqtt/${stageId}/actual_treatment_schedule`,
          payload,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        console.log('Treatment schedule update response:', response.data);
        
        // Handle the response and show appropriate toast message
        if (response.data.success) {
          // Show success toast with the format "Success - Changes"
          // If the backend message is empty or just whitespace, use our change description
          const successMessage = response.data.message && response.data.message.trim() 
            ? response.data.message 
            : changeDescription || "Treatment schedule updated successfully";
          
          toast.success(`Success - ${successMessage}`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        } else {
          // Revert the treatment schedule by fetching latest design schedule
          try {
            const latestDesignSchedule = await fetchLatestDesignSchedule();
            if (latestDesignSchedule.length > 0) {
              setActualTreatments(latestDesignSchedule);
              setOriginalTreatments(latestDesignSchedule);
            } else {
              // Fallback to original treatments if fetch fails
              setActualTreatments([...originalTreatments]);
            }
          } catch (fetchError) {
            console.error('Error fetching latest design schedule for revert:', fetchError);
            // Fallback to original treatments if fetch fails
            setActualTreatments([...originalTreatments]);
          }
          
          // Clear any edited steps since we're reverting
          setEditedTreatmentSteps({});
          
          // Show error toast with the format "Error - Message"
          toast.error(`Error - ${response.data.message}`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          setIsSavingSchedule(false);
          return; // Don't proceed with optimistic updates if the request failed
        }
      }


      // OPTIMISTIC UPDATE: Immediately update the parent component with the new data
      if (onTreatmentScheduleUpdate) {
        // Convert the updated treatments back to the format expected by TreatmentDataGraph
        const optimisticUpdateData = updatedTreatments.map(treatment => ({
          id: treatment.id,
          stage_id: treatment.stage_id,
          step: treatment.step,
          type: treatment.type,
          description: treatment.description,
          rate: treatment.rate,
          conc: treatment.conc,
          clean_step: treatment.clean_step,
          clean_actual: treatment.clean_actual,
          slurry_step: treatment.slurry_step,
          slurry_actual: treatment.slurry_actual,
          sand_step: treatment.sand_step,
          sand_actual: treatment.sand_actual,
          elapsed_time: 0
        }));
        
        console.log('Sending optimistic update to parent:', optimisticUpdateData);
        onTreatmentScheduleUpdate(optimisticUpdateData);
      }

      // Update the actualTreatments state with the new values, preserving start_conc and end_conc
      const finalUpdatedTreatments = actualTreatments.map(treatment => {
        const editedStep = editedTreatmentSteps[treatment.id];
        if (editedStep) {
          const type = editedStep.type !== undefined ? editedStep.type : treatment.type;
          const conc = editedStep.conc !== undefined ? editedStep.conc : treatment.conc;
          const isProppantOrSandRamp = type === 'Proppant' || type === 'Rate Drop-Proppant' || type === 'Sand Ramp' || type === 'Rate Drop-Sand Ramp';
          // In editBySand mode, clean_step is back-calculated from sand_step.
          // In editByClean mode, clean_step is set directly.
          const effectiveCleanStep = (editBySand && isProppantOrSandRamp && editedStep.sand_step !== undefined)
            ? (conc > 0 ? editedStep.sand_step / (42 * conc) : 0)
            : (editedStep.clean_step !== undefined ? editedStep.clean_step : treatment.clean_step);
          // Keep sand_step in sync — use edited value in sand mode, re-derive otherwise.
          const effectiveSandStep = (editBySand && isProppantOrSandRamp && editedStep.sand_step !== undefined)
            ? Math.round(editedStep.sand_step)
            : Math.round(effectiveCleanStep * 42 * conc);
          return {
            ...treatment,
            type,
            description: editedStep.description !== undefined ? editedStep.description : treatment.description,
            rate: editedStep.rate !== undefined ? editedStep.rate : treatment.rate,
            conc,
            clean_step: effectiveCleanStep,
            sand_step: effectiveSandStep,
            // Preserve start_conc and end_conc for Sand Ramp steps
            start_conc: editedStep.start_conc !== undefined ? editedStep.start_conc : treatment.start_conc,
            end_conc: editedStep.end_conc !== undefined ? editedStep.end_conc : treatment.end_conc,
            // Keep the original originalStep value, don't overwrite it
            originalStep: treatment.originalStep
          };
        }
        return treatment;
      });
      
      setActualTreatments(finalUpdatedTreatments);
      
      // Reload design treatment schedule to ensure it reflects any updates from the database/service worker
      // This ensures that when switching to the "Design" tab, the updated data is visible
      try {
        const token = localStorage.getItem('token');
        if (token && stageId) {
          const designResponse = await axios.get(`${backendUrl}/stages/${stageId}/design_treatment_schedule`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const designData = Array.isArray(designResponse.data) 
            ? designResponse.data 
            : [designResponse.data];
          
          setDesignTreatments(designData);
          console.log('Design treatment schedule reloaded after save:', designData);
        }
      } catch (designError) {
        console.error('Error reloading design treatment schedule:', designError);
        // Don't fail the entire save operation if design reload fails
      }
      
      // After successful save, query latest_design_schedule and update originalTreatments
      try {
        const latestDesignSchedule = await fetchLatestDesignSchedule();
        if (latestDesignSchedule.length > 0) {
          // Update originalTreatments to the latest design schedule for future comparisons
          // This ensures that originalStep values are reset to current step values after save
          setOriginalTreatments(latestDesignSchedule);
          
          // Also update the current actualTreatments to match the latest design schedule
          // This ensures the UI reflects the saved state with proper originalStep values
          setActualTreatments(latestDesignSchedule);
          
          console.log('Updated treatments and originalTreatments with latest design schedule after save');
          console.log('Latest design schedule with reset originalSteps:', latestDesignSchedule);
        } else {
          // Fallback: Reset originalTreatments with recalculated originalSteps
          const recalculatedTreatments = finalUpdatedTreatments.map(treatment => ({
            ...treatment,
            originalStep: treatment.step // Reset originalStep to current step
          }));
          setOriginalTreatments(recalculatedTreatments);
          setActualTreatments(recalculatedTreatments);
          console.log('Fallback: Reset originalSteps to current step values');
        }
      } catch (error) {
        console.error('Error fetching latest design schedule after save:', error);
        // Fallback: Reset originalTreatments with recalculated originalSteps
        const recalculatedTreatments = finalUpdatedTreatments.map(treatment => ({
          ...treatment,
          originalStep: treatment.step // Reset originalStep to current step
        }));
        setOriginalTreatments(recalculatedTreatments);
        setActualTreatments(recalculatedTreatments);
        console.log('Error fallback: Reset originalSteps to current step values');
      }
      
      // Clear the edited steps after successful update - this is crucial for preventing stale entries
      setEditedTreatmentSteps({});
      
      setIsSavingSchedule(false);
    } catch (err: any) {
      console.error('Error saving treatment steps:', err);
      setIsSavingSchedule(false);
      
      // Revert the treatment schedule to the original state (before any edits)
      setActualTreatments([...originalTreatments]);
      
      // Clear any edited steps since we're reverting
      setEditedTreatmentSteps({});
      
      // Extract error message from response if available
      let errorMessage = "Failed to save treatment steps. Please try again.";
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Show error toast notification with the format "Error - Message"
      toast.error(`Error - ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
};

  // ── Table cell styles for Chemicals tab (matches treatment table) ────────────
  const chemThStyle: React.CSSProperties = {
    padding: '4px 8px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  };
  const chemTdStyle: React.CSSProperties = {
    padding: '4px 8px',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  };

  return (
    <>
      {/* Full Screen Loading Overlay - Only for save operations */}
      {isSavingSchedule && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(4px)'
        }}>
          <Spinner
            animation="border"
            variant="light"
            style={{ 
              width: '4rem', 
              height: '4rem',
              marginBottom: '1rem'
            }}
          />
          <h4 style={{ 
            color: 'white',
            fontWeight: '600',
            textAlign: 'center',
            margin: 0
          }}>
            Waiting for field approval
          </h4>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.8)',
            textAlign: 'center',
            margin: '0.5rem 0 0 0',
            fontSize: '1rem'
          }}>
            Please wait while your changes are being processed...
          </p>
        </div>
      )}
      
      <Modal 
        show={show} 
        onHide={onHide}
        centered
        dialogClassName={styles.treatmentModalWide}
      >
      <Modal.Header closeButton style={{
        backgroundColor: theme === 'dark' ? '#303335' : '#ffffff', // Black for dark theme, white for light
        borderBottom: `1px solid #5C646B` // Light Gray for borders
      }}>
        <Modal.Title style={{ color: theme === 'dark' ? '#8CC540' : '#303335' }}> {/* White for dark theme, black for light */}
          {wellData && stageNumber 
            ? `Well ${wellData.well_name} - Stage ${stageNumber} Treatment` 
            : wellId && stageNumber 
              ? `Well #${wellId} - Stage ${stageNumber} Treatment` 
              : 'Treatment Details'}
        </Modal.Title>

        {/* Well / Stage pickers — jump to any schedule without reopening.
            Navigating off the opened stage drops real-time streaming, so the
            Actual tab can't show another well's stage as live. */}
        <div className="d-flex align-items-center gap-2" style={{ marginLeft: 'auto', marginRight: '1rem' }}>
          {isNavigated && (
            <Button
              size="sm"
              variant="link"
              onClick={() => { setNavWellId(null); setNavStage(null); }}
              style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', textDecoration: 'none', fontSize: '0.8rem' }}
              title="Back to the stage this modal was opened for"
            >
              Reset
            </Button>
          )}
          <Dropdown>
            <Dropdown.Toggle
              variant={theme === 'dark' ? 'outline-light' : 'outline-secondary'}
              id="dropdown-nav-well"
              size="sm"
              disabled={padWells.length === 0}
            >
              {wellData?.well_name ?? 'Well'}
            </Dropdown.Toggle>
            <Dropdown.Menu
              className={theme === 'dark' ? 'dropdown-menu-dark' : ''}
              style={{ maxHeight: '320px', overflowY: 'auto' }}
            >
              {padWells.map(w => (
                <Dropdown.Item
                  key={w.id}
                  onClick={() => handleSelectWell(w)}
                  active={w.id === wellId}
                >
                  {w.well_name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Dropdown>
            <Dropdown.Toggle
              variant={theme === 'dark' ? 'outline-light' : 'outline-secondary'}
              id="dropdown-nav-stage"
              size="sm"
              disabled={wellStages.length === 0}
            >
              {stageNumber != null ? `Stage ${stageNumber}` : 'Stage'}
            </Dropdown.Toggle>
            <Dropdown.Menu
              className={theme === 'dark' ? 'dropdown-menu-dark' : ''}
              style={{ maxHeight: '320px', overflowY: 'auto' }}
            >
              {wellStages.map(s => (
                <Dropdown.Item
                  key={s.id}
                  onClick={() => handleSelectStage(s)}
                  active={s.id === stageId}
                >
                  Stage {s.stage_number}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </Modal.Header>
      <Modal.Body style={{ 
        backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
        color: theme === 'dark' ? '#ffffff' : '#303335',
        overflowX: 'auto'  // allows the chem table horizontal scrollbar to surface through the modal
      }}>

        {/* ── Two-sided tab header: Design/Actual (left) | Treatment/Chemicals (right) ── */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `2px solid ${theme === 'dark' ? '#5C646B' : '#dee2e6'}`,
          marginBottom: '1rem',
        }}>
          {/* Left: Design | Actual */}
          <div style={{ display: 'flex' }}>
            {(['proposal', 'design', 'actual'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '8px 18px',
                  border: 'none',
                  borderBottom: activeTab === tab ? '3px solid #8CC540' : '3px solid transparent',
                  backgroundColor: 'transparent',
                  color: activeTab === tab
                    ? (theme === 'dark' ? '#e5e5e5' : '#303335')
                    : (theme === 'dark' ? '#8b9299' : '#999999'),
                  cursor: 'pointer',
                  fontWeight: activeTab === tab ? 600 : 400,
                  marginBottom: '-2px',
                  fontSize: '0.9rem',
                  transition: 'all 0.15s',
                  textTransform: 'capitalize',
                }}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
          {/* Right: Treatment | Chemicals */}
          <div style={{ display: 'flex' }}>
            {(['treatment', 'chemicals'] as const).map(sub => (
              <button
                key={sub}
                onClick={() => setActiveSubTab(sub)}
                style={{
                  padding: '8px 18px',
                  border: 'none',
                  borderBottom: activeSubTab === sub ? '3px solid #3E76FE' : '3px solid transparent',
                  backgroundColor: 'transparent',
                  color: activeSubTab === sub
                    ? (theme === 'dark' ? '#e5e5e5' : '#303335')
                    : (theme === 'dark' ? '#8b9299' : '#999999'),
                  cursor: 'pointer',
                  fontWeight: activeSubTab === sub ? 600 : 400,
                  marginBottom: '-2px',
                  fontSize: '0.9rem',
                  transition: 'all 0.15s',
                  textTransform: 'capitalize',
                }}
              >
                {sub.charAt(0).toUpperCase() + sub.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* ── Proposal – Treatment ───────────────────────────────────────── */}
        {/* Design's layout, Actual's validation. Type/Description cells come from
            the shared renderers, so the proppant rules stay in one place. No
            chart, no Progress column, no Actual columns — there's no MQTT here. */}
        {activeSubTab === 'treatment' && activeTab === 'proposal' && (
          <>
            {proposalLoading ? (
              <div className="text-center p-5">
                <Spinner animation="border" variant={theme === 'dark' ? 'light' : 'primary'} />
                <p className="mt-3">Loading proposal treatment data...</p>
              </div>
            ) : proposalTreatments.length === 0 ? (
              <div className="alert alert-info d-flex justify-content-between align-items-center">
                <span>No proposal treatment data available.</span>
                {isProposalEditing && (
                  <Button size="sm" onClick={handleProposalAddFirstStep}
                    style={{ backgroundColor: '#8CC540', borderColor: '#8CC540' }}>
                    Add Step
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="mt-4">
                  <div className="d-flex justify-content-center align-items-center mb-3">
                    <h5 className="mb-0">Proposal Treatment Steps</h5>
                    {proposalTreatments[0]?.creator && proposalTreatments[0]?.timestamp && (
                      <>
                        <span className="mx-3" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: '1.2em' }}>|</span>
                        <small className="text-muted">
                          Last updated by {proposalTreatments[0].creator} on {formatTimestamp(proposalTreatments[0].timestamp as number)}
                        </small>
                      </>
                    )}
                  </div>
                  <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <table className={`table ${theme === 'dark' ? 'table-dark' : 'table-light'} ${styles.treatmentTable}`} style={{ lineHeight: '2.5' }}>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                        <tr>
                          <th colSpan={3}>Step</th>
                          <th colSpan={2}>Target</th>
                          <th>Clean</th>
                          <th colSpan={2}>Slurry</th>
                          <th colSpan={2}>Sand</th>
                          <th>Time</th>
                        </tr>
                        <tr>
                          <th>Step</th>
                          <th>Type</th>
                          <th>Description</th>
                          <th>Rate</th>
                          <th>Conc</th>
                          <th>Step</th>
                          <th>Step</th>
                          <th>Total</th>
                          <th>Step</th>
                          <th>Total</th>
                          <th>Step</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const totals: { [id: number]: { slurry: number; sand: number } } = {};
                          let slurryRunning = 0;
                          let sandRunning = 0;
                          const rows = [...proposalTreatments].sort((a, b) => a.step - b.step);
                          rows.forEach(t => {
                            const slurryStep = t.clean_step + (t.clean_step * t.conc / getDescriptionValue(t.description || ''));
                            const sandStep = t.clean_step * 42 * t.conc;
                            slurryRunning += slurryStep;
                            sandRunning += sandStep;
                            totals[t.id] = { slurry: slurryRunning, sand: sandRunning };
                          });

                          return rows.map(treatment => {
                            const editable = isProposalEditing;
                            const slurryStep = treatment.clean_step + (treatment.clean_step * treatment.conc / getDescriptionValue(treatment.description || ''));
                            return (
                              <tr
                                key={treatment.id}
                                onContextMenu={(e) => handleProposalRightClick(e, treatment)}
                                style={{ cursor: editable ? 'context-menu' : 'default' }}
                              >
                                <td>{treatment.step}</td>
                                <td>
                                  {renderTypeCell(treatment, NO_EDITS, handleEditProposalStep, editable, theme)}
                                </td>
                                <td>
                                  {renderDescriptionCell(treatment, NO_EDITS, handleEditProposalStep, editable, theme)}
                                </td>
                                <td>
                                  {editable ? (
                                    <input
                                      type="number"
                                      step="any"
                                      onMouseDown={e => { e.preventDefault(); (e.target as HTMLInputElement).focus(); }}
                                      onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'a') e.preventDefault(); }}
                                      className={`form-control form-control-sm ${theme === 'dark' ? 'bg-dark text-light' : ''}`}
                                      value={treatment.rate}
                                      onChange={(e) => { const v = parseFloat(e.target.value); handleEditProposalStep(treatment.id, 'rate', isNaN(v) ? 0 : v); }}
                                      style={{
                                        width: '80px',
                                        textAlign: 'center',
                                        backgroundColor: theme === 'dark' ? '#1a2234' : '#ffffff',
                                        color: theme === 'dark' ? '#e5e5e5' : '#333333',
                                        borderColor: theme === 'dark' ? '#3a4564' : '#e6e6e6'
                                      }}
                                    />
                                  ) : treatment.rate}
                                </td>
                                <td>
                                  {(() => {
                                    const concInputStyle = {
                                      width: '80px',
                                      textAlign: 'center' as const,
                                      backgroundColor: theme === 'dark' ? '#1a2234' : '#ffffff',
                                      color: theme === 'dark' ? '#e5e5e5' : '#333333',
                                      borderColor: theme === 'dark' ? '#3a4564' : '#e6e6e6'
                                    };

                                    if (isProposalSandRamp(treatment.type)) {
                                      // Sand Ramp / Rate Drop-Sand Ramp: same pop-out modal as Actual —
                                      // conc field itself is read-only and only editable via the modal.
                                      const startConc = treatment.start_conc ?? treatment.conc ?? 0;
                                      const endConc = treatment.end_conc ?? treatment.conc ?? 0;
                                      return (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                          <input
                                            type="number"
                                            readOnly
                                            className={`form-control form-control-sm ${theme === 'dark' ? 'bg-dark text-light' : ''}`}
                                            value={Number(treatment.conc ?? 0).toFixed(2)}
                                            onClick={() => editable && handleProposalConcentrationClick(treatment)}
                                            style={{ ...concInputStyle, cursor: editable ? 'pointer' : 'default' }}
                                          />
                                          <span>
                                            {`- (${Number(startConc).toFixed(2)}/${Number(endConc).toFixed(2)})`}
                                          </span>
                                        </div>
                                      );
                                    } else if (treatment.type === 'Proppant' || treatment.type === 'Rate Drop-Proppant') {
                                      return editable ? (
                                        <input
                                          type="number"
                                          step="any"
                                          onMouseDown={e => { e.preventDefault(); (e.target as HTMLInputElement).focus(); }}
                                          onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'a') e.preventDefault(); }}
                                          className={`form-control form-control-sm ${theme === 'dark' ? 'bg-dark text-light' : ''}`}
                                          value={treatment.conc}
                                          onChange={(e) => { const v = parseFloat(e.target.value); handleEditProposalStep(treatment.id, 'conc', isNaN(v) ? 0 : v); }}
                                          style={concInputStyle}
                                        />
                                      ) : treatment.conc;
                                    } else {
                                      // Every other step type is forced to 0 concentration — not editable.
                                      return Number(0).toFixed(2);
                                    }
                                  })()}
                                </td>
                                <td>
                                  {editable ? (
                                    <input
                                      type="number"
                                      step="any"
                                      onMouseDown={e => { e.preventDefault(); (e.target as HTMLInputElement).focus(); }}
                                      onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'a') e.preventDefault(); }}
                                      className={`form-control form-control-sm ${theme === 'dark' ? 'bg-dark text-light' : ''}`}
                                      value={treatment.clean_step}
                                      onChange={(e) => { const v = parseFloat(e.target.value); handleEditProposalStep(treatment.id, 'clean_step', isNaN(v) ? 0 : v); }}
                                      style={{
                                        width: '80px',
                                        textAlign: 'center',
                                        backgroundColor: theme === 'dark' ? '#1a2234' : '#ffffff',
                                        color: theme === 'dark' ? '#e5e5e5' : '#333333',
                                        borderColor: theme === 'dark' ? '#3a4564' : '#e6e6e6'
                                      }}
                                    />
                                  ) : Number(treatment.clean_step).toFixed(2)}
                                </td>
                                <td>{slurryStep.toFixed(2)}</td>
                                <td>{totals[treatment.id]?.slurry.toFixed(2) ?? 0}</td>
                                <td>{(treatment.clean_step * 42 * treatment.conc).toFixed(2)}</td>
                                <td>{totals[treatment.id]?.sand.toFixed(2) ?? 0}</td>
                                <td>{treatment.rate ? (treatment.clean_step / treatment.rate * 60).toFixed(2) : '0.00'}</td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* ── Design – Treatment ─────────────────────────────────────────── */}
        {activeSubTab === 'treatment' && activeTab === 'design' && (
          <>
            {loading ? (
              <div className="text-center p-5">
                <Spinner animation="border" variant={theme === 'dark' ? 'light' : 'primary'} />
                <p className="mt-3">Loading design treatment data...</p>
              </div>
            ) : error ? (
              <div className="alert alert-danger">{error}</div>
            ) : designTreatments.length === 0 ? (
              <div className="alert alert-info">No design treatment data available.</div>
            ) : (
              <>
                <div className="mb-3 d-flex justify-content-between align-items-center">
                  <Dropdown>
                    <Dropdown.Toggle 
                      variant={theme === 'dark' ? 'outline-light' : 'outline-secondary'} 
                      id="dropdown-design"
                    >
                      Select Design
                    </Dropdown.Toggle>
                    <Dropdown.Menu className={theme === 'dark' ? 'dropdown-menu-dark' : ''}>
                      {uniqueDesignTimestamps.map((item) => (
                        <Dropdown.Item 
                          key={item.timestamp}
                          onClick={() => handleDesignSelect(item.timestamp, item.creator)}
                          active={selectedDesignTimestamp === item.timestamp}
                        >
                          {formatTimestamp(item.timestamp)} by {item.creator}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                  
                  {selectedDesignTimestamp && selectedDesignCreator && (
                    <div className="text-muted">
                      <small>
                        Created by: {selectedDesignCreator} on {formatTimestamp(selectedDesignTimestamp)}
                      </small>
                    </div>
                  )}
                </div>
                
                <div style={{ height: '400px', width: '100%' }}>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={chartOptions}
                  />
                </div>
                
                <div className="mt-4">
                  <div className="d-flex justify-content-center mb-3">
                    <h5 className="mb-0">Treatment Steps</h5>
                  </div>
                  <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <table className={`table ${theme === 'dark' ? 'table-dark' : 'table-light'} ${styles.treatmentTable}`} style={{ lineHeight: '2.5' }}>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                        <tr>
                          <th colSpan={4}>Step</th>
                          <th colSpan={2}>Target</th>
                          <th>Clean</th>
                          <th colSpan={2}>Slurry</th>
                          <th colSpan={2}>Sand</th>
                          <th>Time</th>
                        </tr>
                        <tr>
                          <th>Step</th>
                          <th>Progress</th>
                          <th>Type</th>
                          <th>Description</th>
                          <th>Rate</th>
                          <th>Conc</th>
                          <th>Step</th>
                          <th>Step</th>
                          <th>Total</th>
                          <th>Step</th>
                          <th>Total</th>
                          <th>Step</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const totals: { [id: number]: { slurry: number; sand: number } } = {};
                          let slurryRunning = 0;
                          let sandRunning = 0;
                          [...designTreatments]
                            .filter(t => selectedDesignTimestamp ? t.timestamp === selectedDesignTimestamp : true)
                            .sort((a, b) => a.step - b.step)
                            .forEach(t => {
                              const slurryStep = t.clean_step + (t.clean_step * t.conc / 22.101);
                              const sandStep = t.clean_step * 42 * t.conc;
                              slurryRunning += slurryStep;
                              sandRunning += sandStep;
                              totals[t.id] = { slurry: slurryRunning, sand: sandRunning };
                            });

                          return designTreatments
                            .filter(t => selectedDesignTimestamp ? t.timestamp === selectedDesignTimestamp : true)
                            .sort((a, b) => a.step - b.step)
                            .map(treatment => (
                              <tr key={treatment.id}>
                                <td>{treatment.step}</td>
                                <td>{treatment.clean_actual ? ((treatment.clean_step / treatment.clean_actual) * 100).toFixed(2) + '%' : '0.00%'}</td>
                                <td>{treatment.type}</td>
                                <td>{treatment.description}</td>
                                <td>{treatment.rate}</td>
                                <td>{treatment.conc}</td>
                                <td>{Number(treatment.clean_step).toFixed(2)}</td>
                                <td>{(treatment.clean_step + (treatment.clean_step * treatment.conc / 22.101)).toFixed(2)}</td>
                                <td>{totals[treatment.id]?.slurry.toFixed(2) ?? 0}</td>
                                <td>{(treatment.clean_step * 42 * treatment.conc).toFixed(2)}</td>
                                <td>{totals[treatment.id]?.sand.toFixed(2) ?? 0}</td>
                                <td>{(treatment.clean_step / treatment.rate * 60).toFixed(2)}</td>
                              </tr>
                            ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* ── Actual – Treatment ─────────────────────────────────────────── */}
        {activeSubTab === 'treatment' && activeTab === 'actual' && (
          <>
            {loading ? (
              <div className="text-center p-5">
                <Spinner animation="border" variant={theme === 'dark' ? 'light' : 'primary'} />
                <p className="mt-3">Loading actual treatment data...</p>
              </div>
            ) : error ? (
              <div className="alert alert-danger">{error}</div>
            ) : actualTreatments.length === 0 ? (
              <div className="alert alert-info">No actual treatment data available.</div>
            ) : (
              <>
                <div style={{ height: '400px', width: '100%' }}>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={chartOptions}
                  />
                </div>
                
                <div className="mt-4">
                  <div className="d-flex justify-content-center align-items-center mb-3">
                    <h5 className="mb-0">Treatment Steps</h5>
                    <span className="mx-3" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: '1.2em' }}>|</span>
                    <Dropdown>
                      <Dropdown.Toggle 
                        variant={theme === 'dark' ? 'outline-light' : 'outline-secondary'} 
                        id="dropdown-changes"
                        size="sm"
                      >
                        Treatment Changes ({treatmentChanges.length})
                      </Dropdown.Toggle>
                      <Dropdown.Menu 
                        className={theme === 'dark' ? 'dropdown-menu-dark' : ''} 
                        style={{ 
                          minWidth: '600px', 
                          maxWidth: '800px', 
                          maxHeight: '400px', 
                          overflowY: 'auto',
                          fontSize: '0.9em',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                        }}
                      >
                        {treatmentChanges.length > 0 ? (
                          treatmentChanges
                            .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                            .map((change, index) => (
                            <div 
                              key={index} 
                              className="px-4 py-3" 
                              style={{ 
                                borderBottom: index < treatmentChanges.length - 1 ? `1px solid ${theme === 'dark' ? '#3a4564' : '#e6e6e6'}` : 'none',
                                backgroundColor: theme === 'dark' ? '#303335' : '#ffffff'
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <span style={{ 
                                  fontWeight: 'bold', 
                                  fontSize: '1em',
                                  color: theme === 'dark' ? '#4361ee' : '#3a0ca3'
                                }}>
                                  {change.creator}
                                </span>
                                <span style={{ 
                                  fontSize: '0.85em', 
                                  color: theme === 'dark' ? '#9ca3af' : '#6b7280',
                                  whiteSpace: 'nowrap',
                                  marginLeft: '12px'
                                }}>
                                  {(() => {
                                    const timestamp = typeof change.timestamp === 'string' ? parseInt(change.timestamp) : change.timestamp;
                                    const timestampInMs = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
                                    return new Date(timestampInMs).toLocaleString();
                                  })()}
                                </span>
                              </div>
                              <p style={{ 
                                margin: 0, 
                                fontSize: '0.9em', 
                                color: theme === 'dark' ? '#e5e5e5' : '#333333',
                                lineHeight: '1.4',
                                wordWrap: 'break-word'
                              }}>
                                {change.comment}
                              </p>
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-3 text-center" style={{ 
                            color: theme === 'dark' ? '#9ca3af' : '#6b7280',
                            fontStyle: 'italic'
                          }}>
                            No recent changes for this stage
                          </div>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                    {isRealTimeStreaming && hasEditLock  && (
                    <>
                      <span className="mx-3" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: '1.2em' }}>|</span>
                      <button
                        className={`btn btn-sm ms-3 ${editBySand ? 'btn-warning' : 'btn-outline-secondary'}`}
                        onClick={() => {
                          if (!editBySand) {
                            // Switching TO sand edit mode — pre-seed sand_step for all
                            // Proppant/Sand Ramp steps not yet explicitly edited.
                            setEditedTreatmentSteps(prev => {
                              const next = { ...prev };
                              actualTreatments.forEach(t => {
                                const currentType = prev[t.id]?.type ?? t.type;
                                const isProppantOrSandRamp = currentType === 'Proppant' || currentType === 'Rate Drop-Proppant' || currentType === 'Sand Ramp' || currentType === 'Rate Drop-Sand Ramp';
                                if (isProppantOrSandRamp && prev[t.id]?.sand_step === undefined) {
                                  const conc = (prev[t.id]?.conc ?? t.conc) || 0;
                                  const cleanStep = (prev[t.id]?.clean_step ?? t.clean_step) || 0;
                                  next[t.id] = {
                                    ...(prev[t.id] || {}),
                                    sand_step: Math.round(cleanStep * 42 * conc)
                                  };
                                }
                              });
                              return next;
                            });
                          }
                          setEditBySand(prev => !prev);
                        }}
                      >
                        {editBySand ? 'Editing by Sand Step' : 'Editing by Clean Step'}
                      </button>
                    </>
                    )}
                  </div>
                  <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <table className={`table ${theme === 'dark' ? 'table-dark' : 'table-light'} ${styles.treatmentTable}`} style={{ lineHeight: '2.5' }}>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                        <tr>
                          <th colSpan={4}>Step</th>
                          <th colSpan={2}>Target</th>
                          <th colSpan={2}>Clean</th>
                          <th colSpan={3}>Slurry</th>
                          <th colSpan={3}>Sand</th>
                          <th colSpan={2}>Time</th>
                        </tr>
                        <tr>
                          <th>Step</th>
                          <th>Progress</th>
                          <th>Type</th>
                          <th>Description</th>
                          <th>Rate</th>
                          <th>Conc - (Start/End)</th>
                          <th>Step</th>
                          <th>Actual</th>
                          <th>Step</th>
                          <th>Actual</th>
                          <th>Total</th>
                          <th>Step</th>
                          <th>Actual</th>
                          <th>Total</th>
                          <th>Step</th>
                          <th>Actual</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const totals: { [id: number]: { slurry: number; sand: number } } = {};
                          let slurryRunning = 0;
                          let sandRunning = 0;
                          [...actualTreatments].sort((a, b) => a.step - b.step).forEach(t => {
                            // Use actuals for completed past steps; use step design values for current step and future steps
                            const isPastStep = currentStep !== null && t.step < currentStep;
                            const hasActuals = (t.slurry_actual && t.slurry_actual > 0) || (t.sand_actual && t.sand_actual > 0);

                            if (isPastStep && hasActuals) {
                              // Completed step — sum real actuals
                              slurryRunning += Number(t.slurry_actual) || 0;
                              sandRunning += Number(t.sand_actual) || 0;
                            } else {
                              // Current step or future step — use designed step values
                              const conc = (editedTreatmentSteps[t.id]?.conc ?? t.conc) || 0;
                              const description = (editedTreatmentSteps[t.id]?.description ?? t.description) || '';
                              const descriptionValue = getDescriptionValue(description);
                              const currentType = editedTreatmentSteps[t.id]?.type ?? t.type;
                              const isProppantOrSandRamp = currentType === 'Proppant' || currentType === 'Rate Drop-Proppant' || currentType === 'Sand Ramp' || currentType === 'Rate Drop-Sand Ramp';
                              let cleanStep: number;
                              if (editBySand && isProppantOrSandRamp && editedTreatmentSteps[t.id]?.sand_step !== undefined) {
                                cleanStep = conc > 0 ? (editedTreatmentSteps[t.id].sand_step as number) / (42 * conc) : 0;
                              } else {
                                cleanStep = (editedTreatmentSteps[t.id]?.clean_step ?? t.clean_step) || 0;
                              }
                              slurryRunning += cleanStep + (cleanStep * conc / descriptionValue);
                              sandRunning += cleanStep * 42 * conc;
                            }
                            totals[t.id] = { slurry: slurryRunning, sand: sandRunning };
                          });

                          return actualTreatments
                            .sort((a, b) => a.step - b.step)
                            .map(treatment => {
                              // Calculate time values for potential future use
                              // const timeStep = treatment.clean_step / treatment.rate * 60;
                              // const timeActual = treatment.end_ts && treatment.start_ts 
                              //   ? treatment.end_ts - treatment.start_ts 
                              //   : null;
                              const editable = isStepEditable(treatment.step);
                              
                              // Determine if this step can have context menu (steps after current step + 2)
                              const canHaveContextMenu = isRealTimeStreaming && 
                                (currentStep === null || treatment.step > currentStep + 2);
                              
                              return (
                                <tr 
                                  key={treatment.id} 
                                  className={currentStep === treatment.step ? 'table-active' : ''}
                                  onContextMenu={(e) => handleRightClick(e, treatment)}
                                  style={{ cursor: canHaveContextMenu ? 'context-menu' : 'default' }}
                                >
                                  <td>{treatment.step}</td>
                                  <td>
                                    {isRealTimeStreaming ? (
                                      // Current step - show real-time data
                                      currentStep === treatment.step && realTimeData?.stepClean !== undefined && treatment.clean_step !== undefined
                                        ? `${((realTimeData.stepClean / treatment.clean_step) * 100).toFixed(2)}%`
                                      // Previous step - show last known value
                                      : currentStep !== null && treatment.step < currentStep && lastStepValues[treatment.step]?.progress !== undefined
                                        ? `${lastStepValues[treatment.step].progress.toFixed(2)}%`
                                      // Default to database value
                                      : treatment.clean_actual 
                                        ? ((treatment.clean_actual / treatment.clean_step) * 100).toFixed(2) + '%' 
                                        : '0.00%'
                                    ) : (
                                      // When not real-time streaming, show database value
                                      treatment.clean_actual 
                                        ? ((treatment.clean_actual / treatment.clean_step) * 100).toFixed(2) + '%' 
                                        : '0.00%'
                                    )}
                                  </td>
                                  <td>
                                    {renderTypeCell(treatment, editedTreatmentSteps, handleEditTreatmentStep, editable, theme)}
                                  </td>
                                  <td>
                                    {renderDescriptionCell(treatment, editedTreatmentSteps, handleEditTreatmentStep, editable, theme, !isRealTimeStreaming)}
                                  </td>
                                  <td>
                                    {editable ? (() => {
                                      const c = rateDropConstraints.map[treatment.id];
                                      return (
                                        <div className="d-flex align-items-center justify-content-center" style={{ flexDirection: 'column' }}>
                                          <div className="d-flex align-items-center justify-content-center">
                                            <input
                                              type="number"
                                              step="any"
                                              onMouseDown={e => { e.preventDefault(); (e.target as HTMLInputElement).focus(); }} onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === "a") e.preventDefault(); }}
                                              className={`form-control form-control-sm ${theme === 'dark' ? 'bg-dark text-light' : ''}`}
                                              value={editedTreatmentSteps[treatment.id]?.rate !== undefined ? editedTreatmentSteps[treatment.id].rate : treatment.rate}
                                              onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) handleEditTreatmentStep(treatment.id, 'rate', v); }}
                                              style={{
                                                width: '80px',
                                                textAlign: 'center',
                                                backgroundColor: theme === 'dark' ? '#1a2234' : '#ffffff',
                                                color: c?.rateOutOfRange ? '#ef4444' : (theme === 'dark' ? '#e5e5e5' : '#333333'),
                                                borderColor: c?.rateOutOfRange ? '#ef4444' : (theme === 'dark' ? '#3a4564' : '#e6e6e6')
                                              }}
                                            />
                                            {editedTreatmentSteps[treatment.id]?.rate !== undefined &&
                                              editedTreatmentSteps[treatment.id].rate !== treatment.rate &&
                                              actualTreatments.some(t => t.step > treatment.step) &&
                                              renderCascadeBtn(
                                                () => cascadeTreatmentField(treatment.step, 'rate', editedTreatmentSteps[treatment.id].rate as number),
                                                `Apply this rate to step ${treatment.step} and all later steps`
                                              )}
                                          </div>
                                          {c && <div style={{ fontSize: '10px', color: c.rateOutOfRange || c.cleanOutOfRange || c.consecutiveRateDrop ? '#ef4444' : (theme === 'dark' ? '#9ca3af' : '#6b7280'), marginTop: '2px' }}>
                                            {c.consecutiveRateDrop ? 'Cannot follow another Rate Drop' : `${c.rateMax}–${c.rateMin} bpm · ${c.cleanSeconds.toFixed(1)}s`}
                                          </div>}
                                        </div>
                                      );
                                    })() : treatment.rate}
                                  </td>
                                  <td>
                                    {(() => {
                                      const currentType =
                                        editedTreatmentSteps[treatment.id]?.type ?? treatment.type;
                                      const value =
                                        editedTreatmentSteps[treatment.id]?.conc ?? treatment.conc;

                                      const inputStyle = {
                                        backgroundColor: theme === "dark" ? "#212529" : "#ffffff",
                                        color: theme === "dark" ? "#e5e5e5" : "#333333",
                                        borderColor: theme === "dark" ? "#ffffff" : "#e6e6e6",
                                        width: "80px",
                                        cursor: isRealTimeStreaming && editable ? "pointer" : "default",
                                        textAlign: "center" as const,
                                      };

                                      if (currentType === "Sand Ramp" || currentType === "Rate Drop-Sand Ramp") {
                                        // Get edited values or fall back to treatment values
                                        const displayStartConc = editedTreatmentSteps[treatment.id]?.start_conc ?? treatment.start_conc ?? 0;
                                        const displayEndConc = editedTreatmentSteps[treatment.id]?.end_conc ?? treatment.end_conc ?? 0;
                                        const sandEdited = editedTreatmentSteps[treatment.id];
                                        const sandStartChanged = sandEdited?.start_conc !== undefined && sandEdited.start_conc !== (treatment.start_conc ?? treatment.conc);
                                        const sandEndChanged   = sandEdited?.end_conc   !== undefined && sandEdited.end_conc   !== (treatment.end_conc   ?? treatment.conc);
                                        const sandConcChanged  = sandEdited?.conc       !== undefined && sandEdited.conc       !== treatment.conc;
                                        const sandRampHasFuture = actualTreatments.some(t => t.step > treatment.step);
                                        const showSandRampCascade = editable && sandRampHasFuture && (sandStartChanged || sandEndChanged || sandConcChanged);

                                        // Show start/end concentrations for all Sand Ramp types, whether editable or not
                                        if (editable) {
                                          return (
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                                              <Form.Control
                                                type="number"
                                                size="sm"
                                                readOnly
                                                value={Number(value ?? 0).toFixed(2)}
                                                onClick={() => handleConcentrationClick(treatment)}
                                                style={inputStyle}
                                                disabled={!isRealTimeStreaming}
                                              />
                                              <span>
                                                {`- (${Number(displayStartConc).toFixed(2)}/${Number(displayEndConc).toFixed(2)})`}
                                              </span>
                                              {showSandRampCascade && renderCascadeBtn(
                                                () => cascadeSandRampConc(treatment.step),
                                                `Apply this concentration ramp to step ${treatment.step} and all later steps`
                                              )}
                                            </div>
                                          );
                                        } else {
                                          // Non-editable Sand Ramp - show concentration with start/end values
                                          return (
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                                              <span>{Number(value || 0).toFixed(2)}</span>
                                              <span>
                                                {`- (${Number(displayStartConc).toFixed(2)}/${Number(displayEndConc).toFixed(2)})`}
                                              </span>
                                            </div>
                                          );
                                        }
                                      } else if (editable && (currentType === "Proppant" || currentType === "Rate Drop-Proppant")) {
                                        const proppantChanged = editedTreatmentSteps[treatment.id]?.conc !== undefined &&
                                          editedTreatmentSteps[treatment.id].conc !== treatment.conc;
                                        return (
                                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <Form.Control
                                              type="number"
                                              step="any"
                                              size="sm"
                                              onMouseDown={e => { e.preventDefault(); (e.target as HTMLInputElement).focus(); }} onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === "a") e.preventDefault(); }}
                                              value={editedTreatmentSteps[treatment.id]?.conc ?? treatment.conc ?? 0}
                                              onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) handleEditTreatmentStep(treatment.id, "conc", v); }}
                                              style={inputStyle}
                                              disabled={!isRealTimeStreaming}
                                            />
                                            {proppantChanged &&
                                              actualTreatments.some(t => t.step > treatment.step) &&
                                              renderCascadeBtn(
                                                () => cascadeTreatmentField(treatment.step, 'conc', editedTreatmentSteps[treatment.id].conc as number),
                                                `Apply this concentration to step ${treatment.step} and all later steps`
                                              )}
                                          </div>
                                        );
                                      } else {
                                        // Always show the concentration value, whether editable or not
                                        return Number(value || 0).toFixed(2);
                                      }
                                    })()}
                                  </td>
                                  {/* Clean Step */}
                                  <td>
                                    {(() => {
                                      const currentType = editedTreatmentSteps[treatment.id]?.type ?? treatment.type;
                                      const isProppantOrSandRamp = currentType === 'Proppant' || currentType === 'Rate Drop-Proppant' || currentType === 'Sand Ramp' || currentType === 'Rate Drop-Sand Ramp';

                                      if (editable && editBySand && isProppantOrSandRamp) {
                                        // editBySand mode + sand-capable type: show back-calculated clean step as readonly
                                        const conc = (editedTreatmentSteps[treatment.id]?.conc ?? treatment.conc) || 0;
                                        const sandStep = (editedTreatmentSteps[treatment.id]?.sand_step ?? treatment.sand_step) || 0;
                                        return Number(conc > 0 ? sandStep / (42 * conc) : 0).toFixed(0);
                                      }

                                      if (editable) {
                                        // Normal clean step edit
                                        const cleanChanged = editedTreatmentSteps[treatment.id]?.clean_step !== undefined &&
                                          editedTreatmentSteps[treatment.id].clean_step !== treatment.clean_step;
                                        return (
                                          <div className="d-flex align-items-center justify-content-center">
                                            <input
                                              type="number"
                                              onMouseDown={e => { e.preventDefault(); (e.target as HTMLInputElement).focus(); }} onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === "a") e.preventDefault(); }}
                                              className={`form-control form-control-sm ${theme === 'dark' ? 'bg-dark text-light' : ''}`}
                                              value={editedTreatmentSteps[treatment.id]?.clean_step !== undefined ? editedTreatmentSteps[treatment.id].clean_step : treatment.clean_step}
                                              onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) handleEditTreatmentStep(treatment.id, 'clean_step', v); }}
                                              style={{
                                                width: '80px', textAlign: 'center',
                                                backgroundColor: theme === 'dark' ? '#1a2234' : '#ffffff',
                                                color: theme === 'dark' ? '#e5e5e5' : '#333333',
                                                borderColor: theme === 'dark' ? '#3a4564' : '#e6e6e6'
                                              }}
                                            />
                                            {cleanChanged &&
                                              actualTreatments.some(t => t.step > treatment.step) &&
                                              renderCascadeBtn(
                                                () => cascadeTreatmentField(treatment.step, 'clean_step', editedTreatmentSteps[treatment.id].clean_step as number),
                                                `Apply this clean step to step ${treatment.step} and all later steps`
                                              )}
                                          </div>
                                        );
                                      }

                                      // Not editable - show value (back-calculated if editBySand + sand-capable)
                                      if (editBySand && isProppantOrSandRamp) {
                                        const conc = (editedTreatmentSteps[treatment.id]?.conc ?? treatment.conc) || 0;
                                        const sandStep = (editedTreatmentSteps[treatment.id]?.sand_step ?? treatment.sand_step) || 0;
                                        return Number(conc > 0 ? sandStep / (42 * conc) : 0).toFixed(0);
                                      }

                                      return Number(editedTreatmentSteps[treatment.id]?.clean_step ?? treatment.clean_step).toFixed(0);
                                    })()}
                                  </td>

                                  {/* Clean Actual */}
                                  <td>
                                    {isRealTimeStreaming ? (
                                      currentStep === treatment.step && realTimeData?.stepClean !== undefined
                                        ? realTimeData.stepClean.toFixed(0)
                                      : currentStep !== null && treatment.step < currentStep && lastStepValues[treatment.step]?.stepClean !== undefined
                                        ? lastStepValues[treatment.step].stepClean.toFixed(0)
                                      : treatment.clean_actual ? Number(treatment.clean_actual).toFixed(0) : '0.00'
                                    ) : (
                                      treatment.clean_actual ? Number(treatment.clean_actual).toFixed(0) : '0.00'
                                    )}
                                  </td>

                                  {/* Slurry Step - always derived, no change needed */}
                                  <td>
                                    {(() => {
                                      const currentType = editedTreatmentSteps[treatment.id]?.type ?? treatment.type;
                                      const isProppantOrSandRamp = currentType === 'Proppant' || currentType === 'Rate Drop-Proppant' || currentType === 'Sand Ramp' || currentType === 'Rate Drop-Sand Ramp';
                                      const conc = (editedTreatmentSteps[treatment.id]?.conc ?? treatment.conc) || 0;
                                      const description = (editedTreatmentSteps[treatment.id]?.description ?? treatment.description) || '';
                                      const descriptionValue = getDescriptionValue(description as string);
                                      
                                      let cleanStep: number;
                                      if (editBySand && isProppantOrSandRamp) {
                                        const sandStep = (editedTreatmentSteps[treatment.id]?.sand_step ?? treatment.sand_step) || 0;
                                        cleanStep = conc > 0 ? sandStep / (42 * conc) : 0;
                                      } else {
                                        cleanStep = (editedTreatmentSteps[treatment.id]?.clean_step ?? treatment.clean_step) || 0;
                                      }
                                      return (cleanStep + (cleanStep * conc / descriptionValue)).toFixed(0);
                                    })()}
                                  </td>

                                  {/* Slurry Actual - unchanged */}
                                  <td>
                                    {isRealTimeStreaming ? (
                                      currentStep === treatment.step && realTimeData?.stepSlurry !== undefined
                                        ? realTimeData.stepSlurry.toFixed(0)
                                      : currentStep !== null && treatment.step < currentStep && lastStepValues[treatment.step]?.stepSlurry !== undefined
                                        ? lastStepValues[treatment.step].stepSlurry.toFixed(0)
                                      : treatment.slurry_actual ? Number(treatment.slurry_actual).toFixed(0) : '0.00'
                                    ) : (
                                      treatment.slurry_actual ? Number(treatment.slurry_actual).toFixed(0) : '0.00'
                                    )}
                                  </td>

                                  {/* Slurry Total */}
                                  <td>{totals[treatment.id]?.slurry.toFixed(0) ?? 0}</td>

                                  {/* Sand Step */}
                                  <td>
                                    {(() => {
                                      const currentType = editedTreatmentSteps[treatment.id]?.type ?? treatment.type;
                                      const isProppantOrSandRamp = currentType === 'Proppant' || currentType === 'Rate Drop-Proppant' || currentType === 'Sand Ramp' || currentType === 'Rate Drop-Sand Ramp';

                                      if (editable && editBySand && isProppantOrSandRamp) {
                                        // Sand step is editable, back-calculates clean step
                                        const conc = (editedTreatmentSteps[treatment.id]?.conc ?? treatment.conc) || 0;
                                        const concIsZero = conc === 0;
                                        const originalSandStep = Math.round((treatment.clean_step || 0) * 42 * (treatment.conc || 0));
                                        const editedSandStep = editedTreatmentSteps[treatment.id]?.sand_step;
                                        const sandStepChanged = editedSandStep !== undefined && Math.round(editedSandStep) !== originalSandStep;
                                        return (
                                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <div className="d-flex align-items-center">
                                              <input
                                                type="number"
                                                onMouseDown={e => { e.preventDefault(); (e.target as HTMLInputElement).focus(); }} onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === "a") e.preventDefault(); }}
                                                className={`form-control form-control-sm ${theme === 'dark' ? 'bg-dark text-light' : ''}`}
                                                value={(() => {
                                                  if (editedTreatmentSteps[treatment.id]?.sand_step !== undefined) {
                                                    return Math.round(editedTreatmentSteps[treatment.id].sand_step as number);
                                                  }
                                                  const c = (editedTreatmentSteps[treatment.id]?.conc ?? treatment.conc) || 0;
                                                  const cs = (editedTreatmentSteps[treatment.id]?.clean_step ?? treatment.clean_step) || 0;
                                                  return Math.round(cs * 42 * c);
                                                })()}
                                                onChange={(e) => handleEditTreatmentStep(treatment.id, 'sand_step', Math.round(parseFloat(e.target.value) || 0))}
                                                disabled={concIsZero}
                                                title={concIsZero ? 'Cannot edit sand step: concentration is 0' : undefined}
                                                style={{
                                                  width: '80px', textAlign: 'center',
                                                  backgroundColor: concIsZero
                                                    ? (theme === 'dark' ? '#3a1a1a' : '#ffe5e5')
                                                    : (theme === 'dark' ? '#1a2234' : '#ffffff'),
                                                  color: concIsZero
                                                    ? (theme === 'dark' ? '#ff6b6b' : '#cc0000')
                                                    : (theme === 'dark' ? '#e5e5e5' : '#333333'),
                                                  borderColor: concIsZero ? '#cc0000' : (theme === 'dark' ? '#3a4564' : '#e6e6e6'),
                                                  cursor: concIsZero ? 'not-allowed' : 'auto'
                                                }}
                                              />
                                              {sandStepChanged &&
                                                !concIsZero &&
                                                actualTreatments.some(t => t.step > treatment.step) &&
                                                renderCascadeBtn(
                                                  () => cascadeTreatmentField(treatment.step, 'sand_step', editedSandStep as number),
                                                  `Apply this sand step to step ${treatment.step} and all later steps`
                                                )}
                                            </div>
                                            {concIsZero && (
                                              <small style={{ color: '#cc0000', fontSize: '0.7em', marginTop: '2px', whiteSpace: 'nowrap' }}>
                                                Conc must be &gt; 0
                                              </small>
                                            )}
                                          </div>
                                        );
                                      }
                                      // Not editable by sand, show calculated value
                                      const conc = (editedTreatmentSteps[treatment.id]?.conc ?? treatment.conc) || 0;
                                      const cleanStep = (editedTreatmentSteps[treatment.id]?.clean_step ?? treatment.clean_step) || 0;
                                      return Math.round(cleanStep * 42 * conc).toString();
                                    })()}
                                  </td>
                                  <td>
                                    {isRealTimeStreaming ? (
                                      // Current step - show real-time data
                                      currentStep === treatment.step && realTimeData?.stepSand !== undefined
                                        ? realTimeData.stepSand.toFixed(2)
                                      // Previous step - show last known value
                                      : currentStep !== null && treatment.step < currentStep && lastStepValues[treatment.step]?.stepSand !== undefined
                                        ? lastStepValues[treatment.step].stepSand.toFixed(0)
                                      // Default to database value
                                      : treatment.sand_actual 
                                        ? Number(treatment.sand_actual).toFixed(0) 
                                        : '0.00'
                                    ) : (
                                      // When not real-time streaming, show database value
                                      treatment.sand_actual 
                                        ? Number(treatment.sand_actual).toFixed(0) 
                                        : '0.00'
                                    )}
                                  </td>
                                  {/* Sand Total */}
                                  <td>{totals[treatment.id]?.sand.toFixed(0) ?? 0}</td>
                                  <td>
                                    {(() => {
                                      // Get the current values (either edited or original)
                                      const rate = editedTreatmentSteps[treatment.id]?.rate !== undefined 
                                        ? editedTreatmentSteps[treatment.id].rate as number
                                        : treatment.rate || 1; // Default to 1 to avoid division by zero
                                      const cleanStep = editedTreatmentSteps[treatment.id]?.clean_step !== undefined 
                                        ? editedTreatmentSteps[treatment.id].clean_step as number
                                        : treatment.clean_step || 0;
                                      
                                      // Calculate time step: clean step / rate
                                      return (cleanStep / rate * 60).toFixed(2);
                                    })()}
                                  </td>
                                  <td>
                                    {isRealTimeStreaming ? (
                                      // Current step - show real-time data
                                      currentStep === treatment.step && realTimeData?.elapsedTime !== undefined
                                        ? (realTimeData.elapsedTime/1000/60).toFixed(2)
                                      // Previous step - show last known value
                                      : currentStep !== null && treatment.step < currentStep && lastStepValues[treatment.step]?.elapsedTime !== undefined
                                        ? (lastStepValues[treatment.step].elapsedTime/1000/60).toFixed(2)
                                      // Default to database value
                                      : treatment.clean_actual 
                                        ? ((treatment.clean_actual / treatment.rate) * 60).toFixed(2) 
                                        : '00:00:00'
                                    ) : (
                                      // When not real-time streaming, show database value
                                      treatment.clean_actual 
                                        ? ((treatment.clean_actual / treatment.rate) * 60).toFixed(2) 
                                        : '00:00:00'
                                    )}
                                  </td>
                                </tr>
                              );
                            });
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* ── Chemicals (Design or Actual) ─────────────────────────────── */}
        {activeSubTab === 'chemicals' && activeTab !== 'proposal' && (
          <>
            {/* Saving overlay */}
            {isSavingChem && (
              <div style={{
                position: 'fixed',
                top: 0, left: 0,
                width: '100vw', height: '100vh',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                display: 'flex', flexDirection: 'column',
                justifyContent: 'center', alignItems: 'center',
                zIndex: 9999, backdropFilter: 'blur(4px)',
              }}>
                <Spinner animation="border" variant="light" style={{ width: '4rem', height: '4rem', marginBottom: '1rem' }} />
                <h4 style={{ color: 'white', fontWeight: '600', textAlign: 'center', margin: 0 }}>Waiting for field approval</h4>
                <p style={{ color: 'rgba(255, 255, 255, 0.8)', textAlign: 'center', margin: '0.5rem 0 0 0', fontSize: '1rem' }}>
                  Please wait while your changes are being processed...
                </p>
              </div>
            )}

            {chemLoading && (
              <div className="text-center p-5">
                <Spinner animation="border" variant={theme === 'dark' ? 'light' : 'primary'} />
                <p className="mt-3">Loading chemical schedule...</p>
              </div>
            )}
            {!chemLoading && (!chemSchedule || (chemSchedule.chem_names.length === 0 && realTimeChemData.length === 0)) && (
              <div className="alert alert-info mt-2">No chemical schedule data available for this stage.</div>
            )}
            {!chemLoading && chemSchedule && (chemSchedule.chem_names.length > 0 || realTimeChemData.length > 0) && (() => {
              const isDesign = activeTab === 'design';
              const isDarkTheme = theme === 'dark';

              // Direct MQTT lookup: ratio (gpt) + total (gals) for current step
              const liveChemMap: Record<string, { ratio: number | null; total: number | null }> = {};
              if (!isDesign) {
                realTimeChemData.forEach((item: any) => {
                  if (item.name) {
                    liveChemMap[item.name] = {
                      ratio: typeof item.ratio === 'number' ? item.ratio : null,
                      total: typeof item.total === 'number' ? item.total : null,
                    };
                  }
                });
              }

              // Helper: look up live data — /chem topic always sends plain names
              const getLiveEntry = (chemName: string) => liveChemMap[chemName] ?? null;

              // Column headers (Actual tab / live):
              //   Petrix's /chem topic only includes chemicals actively pumping,
              //   so chemicals scheduled but not yet streaming would be hidden.
              //   Union /chem live names with the schedule's chemicals so the
              //   full list is always visible, anchored on /chem order.
              // Column headers (Design tab / offline):
              //   Fall back to DB chem_names which already lists all chems.
              const effectiveChemNames: string[] = (() => {
                if (isDesign || realTimeChemData.length === 0) {
                  return chemSchedule.chem_names;
                }
                const liveNames: string[] = realTimeChemData
                  .filter((item: any) => item.name)
                  .map((item: any) => item.name as string);
                const seen = new Set<string>(liveNames);
                const out = [...liveNames];
                Object.values(rawScheduleChemicals).forEach(chems => {
                  chems.forEach(c => {
                    if (c.name && !seen.has(c.name)) {
                      seen.add(c.name);
                      out.push(c.name);
                    }
                  });
                });
                return out;
              })();

              // Multi-group detection: a chem is multi-group if ANY step has >1 group for it.
              // Every step now has all groups in rawScheduleChemicals, so no master seeding needed.
              const multiGroupChems = new Set<string>();
              Object.values(rawScheduleChemicals).forEach(chems => {
                const countByName: Record<string, number> = {};
                chems.forEach(c => { countByName[c.name] = (countByName[c.name] || 0) + 1; });
                Object.entries(countByName).forEach(([name, count]) => {
                  if (count > 1) multiGroupChems.add(name);
                });
              });

              // Row data: use DB steps when available; fall back to actualTreatments for Petrix 7.0.1
              // where no chemical schedule is posted (chemSchedule.steps will be empty).
              const stepsData = chemSchedule.steps.length > 0
                ? chemSchedule.steps
                : actualTreatments.map(t => ({
                    step: t.step,
                    type: t.type,
                    clean_step: t.clean_step,
                    chems: {} as Record<string, { design: number | null; actual: number | null }>,
                  }));

              const totalGals = (loading: number | null, cleanBbls: number): number | null => {
                if (loading == null) return null;
                return (loading * cleanBbls * 42) / 1000;
              };

              const fmt = (v: number | null, dec = 3) => v == null ? '—' : v.toFixed(dec);

              const totalCleanGals = stepsData.reduce((acc, s) => acc + s.clean_step * 42, 0);
              const avgCleanGals   = totalCleanGals / (stepsData.length || 1);

              // Use dedicated chemical_changes table — no filtering needed
              const chemChanges = chemicalChanges;

              return (
                <>
                  {/* Design tab: Select Design dropdown + info */}
                  {isDesign && (
                    <div className="mb-3 d-flex justify-content-between align-items-center">
                      <Dropdown>
                        <Dropdown.Toggle
                          variant={theme === 'dark' ? 'outline-light' : 'outline-secondary'}
                          id="dropdown-chem-design"
                        >
                          Select Design
                        </Dropdown.Toggle>
                        <Dropdown.Menu className={theme === 'dark' ? 'dropdown-menu-dark' : ''}>
                          {uniqueDesignTimestamps.map(item => (
                            <Dropdown.Item
                              key={item.timestamp}
                              onClick={() => handleDesignSelect(item.timestamp, item.creator)}
                              active={selectedDesignTimestamp === item.timestamp}
                            >
                              {formatTimestamp(item.timestamp)} by {item.creator}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                      {selectedDesignTimestamp && selectedDesignCreator && (
                        <div className="text-muted">
                          <small>Created by: {selectedDesignCreator} on {formatTimestamp(selectedDesignTimestamp)}</small>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Chart */}
                  <div style={{ height: '320px', width: '100%', marginBottom: '1rem' }}>
                    <HighchartsReact highcharts={Highcharts} options={chemChartOptions} />
                  </div>

                  {/* Chemical Steps title + Chemical Changes dropdown — mirrors treatment actual layout */}
                  <div className="mt-4">
                    <div className="d-flex justify-content-center align-items-center mb-3">
                      <h5 className="mb-0">Chemical Schedule</h5>
                      {!isDesign && (
                        <>
                          <span className="mx-3" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: '1.2em' }}>|</span>
                          <Dropdown>
                            <Dropdown.Toggle
                              variant={theme === 'dark' ? 'outline-light' : 'outline-secondary'}
                              id="dropdown-chem-changes"
                              size="sm"
                            >
                              Chemical Changes ({chemChanges.length})
                            </Dropdown.Toggle>
                            <Dropdown.Menu
                              className={theme === 'dark' ? 'dropdown-menu-dark' : ''}
                              style={{
                                minWidth: '600px', maxWidth: '800px',
                                maxHeight: '400px', overflowY: 'auto',
                                fontSize: '0.9em',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              }}
                            >
                              {chemChanges.length > 0 ? (
                                [...chemChanges]
                                  .sort((a, b) => {
                                    const ta = typeof a.timestamp === 'string' ? parseInt(a.timestamp) : a.timestamp as unknown as number;
                                    const tb = typeof b.timestamp === 'string' ? parseInt(b.timestamp) : b.timestamp as unknown as number;
                                    return tb - ta;
                                  })
                                  .map((change, index) => (
                                    <div
                                      key={index}
                                      className="px-4 py-3"
                                      style={{
                                        borderBottom: index < chemChanges.length - 1 ? `1px solid ${theme === 'dark' ? '#3a4564' : '#e6e6e6'}` : 'none',
                                        backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
                                      }}
                                    >
                                      <div className="d-flex justify-content-between align-items-start mb-2">
                                        <span style={{ fontWeight: 'bold', fontSize: '1em', color: theme === 'dark' ? '#4361ee' : '#3a0ca3' }}>
                                          {change.creator}
                                        </span>
                                        <span style={{ fontSize: '0.85em', color: theme === 'dark' ? '#9ca3af' : '#6b7280', whiteSpace: 'nowrap', marginLeft: '12px' }}>
                                          {(() => {
                                            const ts = typeof change.timestamp === 'string' ? parseInt(change.timestamp) : change.timestamp as unknown as number;
                                            const tsMs = ts < 10000000000 ? ts * 1000 : ts;
                                            return new Date(tsMs).toLocaleString();
                                          })()}
                                        </span>
                                      </div>
                                      <p style={{ margin: 0, fontSize: '0.9em', color: theme === 'dark' ? '#e5e5e5' : '#333333', lineHeight: '1.4', wordWrap: 'break-word' }}>
                                        {change.comment}
                                      </p>
                                    </div>
                                  ))
                              ) : (
                                <div className="px-4 py-3 text-center" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', fontStyle: 'italic' }}>
                                  No chemical changes recorded for this stage
                                </div>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Table */}
                  <div style={{ overflowX: 'auto', overflowY: 'visible' }}>
                    <div style={{ maxHeight: '560px', overflowY: 'auto', overflowX: 'visible' }}>
                    <table
                      className={`table ${styles.treatmentTable} ${styles.chemScheduleTable}`}
                      style={{
                        lineHeight: '2.2',
                        borderCollapse: 'collapse',
                        fontSize: '0.82rem',
                        color: isDarkTheme ? '#e5e5e5' : '#303335',
                        backgroundColor: isDarkTheme ? '#303335' : '#ffffff',
                      }}
                    >
                      {/* Fixed pixel minWidths — table grows wider than container when many chems are present */}
                      <colgroup>
                        <col style={{ minWidth: '48px'  }} />  {/* Step #    */}
                        <col style={{ minWidth: '90px'  }} />  {/* Type      */}
                        <col style={{ minWidth: '88px'  }} />  {/* Clean Vol */}
                        {effectiveChemNames.map((name) => (
                          <React.Fragment key={name}>
                            <col style={{ minWidth: '78px' }} />  {/* Design Ratio  */}
                            <col style={{ minWidth: '78px' }} />  {/* Actual Ratio  */}
                            {!isDesign && (
                              <>
                                <col style={{ minWidth: '82px' }} />  {/* Total Design */}
                                <col style={{ minWidth: '82px' }} />  {/* Total Actual */}
                                <col style={{ minWidth: '72px' }} />  {/* Variance     */}
                              </>
                            )}
                          </React.Fragment>
                        ))}
                      </colgroup>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                        {/* Row 1 — grouped chem name headers */}
                        <tr>
                          <th colSpan={3} style={{
                            padding: '6px 6px', fontWeight: 600, whiteSpace: 'nowrap',
                            backgroundColor: isDarkTheme ? '#3d4144' : '#f3f4f6',
                            color: isDarkTheme ? '#e5e5e5' : '#303335',
                            border: `1px solid ${isDarkTheme ? '#5C646B' : '#dee2e6'}`,
                          }}>Step</th>
                          {effectiveChemNames.map((name) => {
                            const chemBg = isDarkTheme ? '#5a5a00' : '#fef9c3';
                            return (
                              <th key={name} colSpan={isDesign ? 2 : 5} style={{
                                textAlign: 'center', fontWeight: 700, letterSpacing: '0.02em',
                                padding: '6px 8px', whiteSpace: 'nowrap',
                                backgroundColor: chemBg,
                                color: isDarkTheme ? '#ffe066' : '#5a4000',
                                border: `1px solid ${isDarkTheme ? '#5C646B' : '#dee2e6'}`,
                              }}>
                                {name}
                              </th>
                            );
                          })}
                        </tr>
                        {/* Row 2 — column sub-headers: no background tinting, tight widths on step cols */}
                        <tr>
                          {/* Step # */}
                          <th style={{
                            padding: '5px 6px', fontWeight: 600, textAlign: 'center',
                            backgroundColor: isDarkTheme ? '#3d4144' : '#f3f4f6',
                            color: isDarkTheme ? '#e5e5e5' : '#303335',
                            border: `1px solid ${isDarkTheme ? '#5C646B' : '#dee2e6'}`,
                          }}>Step #</th>
                          {/* Type */}
                          <th style={{
                            padding: '5px 6px', fontWeight: 600,
                            backgroundColor: isDarkTheme ? '#3d4144' : '#f3f4f6',
                            color: isDarkTheme ? '#e5e5e5' : '#303335',
                            border: `1px solid ${isDarkTheme ? '#5C646B' : '#dee2e6'}`,
                          }}>Type</th>
                          {/* Clean Vol */}
                          <th style={{
                            padding: '5px 6px', fontWeight: 600, textAlign: 'right',
                            backgroundColor: isDarkTheme ? '#3d4144' : '#f3f4f6',
                            color: isDarkTheme ? '#e5e5e5' : '#303335',
                            border: `1px solid ${isDarkTheme ? '#5C646B' : '#dee2e6'}`,
                          }}>Clean Vol<br/>(gals)</th>
                          {/* Chem columns — no background, just neutral header */}
                          {effectiveChemNames.map(name => (
                            <React.Fragment key={name}>
                              <th style={{
                                textAlign: 'center', padding: '5px 6px', fontWeight: 600,
                                backgroundColor: isDarkTheme ? '#3d4144' : '#f3f4f6',
                                color: isDarkTheme ? '#e5e5e5' : '#303335',
                                border: `1px solid ${isDarkTheme ? '#5C646B' : '#dee2e6'}`,
                              }}>Design Ratio<br/>(gpt)</th>
                              {!isDesign && (
                                <th style={{
                                  textAlign: 'center', padding: '5px 6px', fontWeight: 600,
                                  backgroundColor: isDarkTheme ? '#3d4144' : '#f3f4f6',
                                  color: isDarkTheme ? '#e5e5e5' : '#303335',
                                  border: `1px solid ${isDarkTheme ? '#5C646B' : '#dee2e6'}`,
                                }}>Actual Ratio<br/>(gpt)</th>
                              )}
                              <th style={{
                                textAlign: 'right', padding: '5px 6px', fontWeight: 600,
                                backgroundColor: isDarkTheme ? '#3d4144' : '#f3f4f6',
                                color: isDarkTheme ? '#e5e5e5' : '#303335',
                                border: `1px solid ${isDarkTheme ? '#5C646B' : '#dee2e6'}`,
                              }}>Total Design<br/>(gal)</th>
                              {!isDesign && (
                                <>
                                  <th style={{
                                    textAlign: 'right', padding: '5px 6px', fontWeight: 600,
                                    backgroundColor: isDarkTheme ? '#3d4144' : '#f3f4f6',
                                    color: isDarkTheme ? '#e5e5e5' : '#303335',
                                    border: `1px solid ${isDarkTheme ? '#5C646B' : '#dee2e6'}`,
                                  }}>Total Actual<br/>(gal)</th>
                                  <th style={{
                                    textAlign: 'center', padding: '5px 6px', fontWeight: 600,
                                    backgroundColor: isDarkTheme ? '#3d4144' : '#f3f4f6',
                                    color: isDarkTheme ? '#e5e5e5' : '#303335',
                                    border: `1px solid ${isDarkTheme ? '#5C646B' : '#dee2e6'}`,
                                  }}>Variance<br/>(%)</th>
                                </>
                              )}
                            </React.Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {stepsData.map((row) => {
                          const isActiveRow = !isDesign && isRealTimeStreaming && currentStep === row.step;
                          const isFutureOrCurrent = currentStep === null || row.step >= currentStep;
                          const rowIdx = stepsData.indexOf(row);
                          const rowBg = rowIdx % 2 === 0
                            ? (isDarkTheme ? '#303335' : '#ffffff')
                            : (isDarkTheme ? '#383b3e' : '#f9fafb');
                          const activeBg = isDarkTheme ? '#f9c844' : '#fef08a';
                          const cellBg   = isActiveRow ? activeBg : rowBg;
                          const cellBase: React.CSSProperties = {
                            padding: '4px 8px', whiteSpace: 'nowrap',
                            border: `1px solid ${isDarkTheme ? '#5C646B' : '#dee2e6'}`,
                            backgroundColor: cellBg,
                            color: isDarkTheme ? '#e5e5e5' : '#303335',
                          };
                          return (
                            <tr key={row.step}
                              style={{ backgroundColor: isActiveRow ? (isDarkTheme ? '#f9c844' : '#fef08a') : rowBg }}>
                              <td style={{ ...cellBase, textAlign: 'center' }}>{row.step}</td>
                              <td style={cellBase}>{row.type}</td>
                              <td style={{ ...cellBase, textAlign: 'right' }}>
                                {Math.round(row.clean_step * 42).toLocaleString()}
                              </td>
                              {effectiveChemNames.map(name => {
                                const entry  = row.chems?.[name];
                                const cellKey = `${row.step}::${name}`;
                                const editedVal = editedChemLoadings[cellKey];
                                const editedGroups = editedChemGroupRatios[cellKey];
                                const isMultiGroup = multiGroupChems.has(name);
                                const stepGroups = (rawScheduleChemicals[row.step] || []).filter(c => c.name === name);

                                // Design value priority:
                                //   1. Edited group ratios sum (modal)
                                //   2. Inline edited value
                                //   3. Schedule topic (rawScheduleChemicals) — source of truth,
                                //      updates on every MQTT echo including current step
                                //   4. DB design value (fallback when schedule topic not yet received)
                                const designVal = editedGroups
                                  ? editedGroups.reduce((s, g) => s + g.ratio, 0)
                                  : editedVal !== undefined
                                    ? editedVal
                                    : stepGroups.length > 1
                                      ? stepGroups.reduce((s, g) => s + g.ratio, 0)
                                      : stepGroups.length === 1
                                        ? stepGroups[0].ratio
                                        : (entry?.design ?? null);
                                // Loading Actual: live chem_ratio (gpt) for current step; DB for completed steps
                                const liveEntry2 = (!isDesign && row.step === currentStep) ? getLiveEntry(name) : null;
                                const liveRatio = liveEntry2?.ratio ?? null;
                                const liveTotal = liveEntry2?.total ?? null;  // cumulative gals from Petrix
                                const actualVal = liveRatio !== null
                                  ? liveRatio
                                  : (editedVal !== undefined ? editedVal : (entry?.actual ?? null));
                                // Total Design: designRatio × live stepClean (bbls→gals) for current step.
                                // stepClean is always accurate regardless of mid-step design ratio changes.
                                const liveStepClean = (!isDesign && row.step === currentStep && realTimeData?.stepClean != null)
                                  ? realTimeData.stepClean : null;
                                const td = liveStepClean != null && designVal != null
                                  ? (designVal * liveStepClean * 42) / 1000
                                  : totalGals(designVal, row.clean_step);
                                // Total Actual: Petrix cumulative total (gals) — chem topic only.
                                const ta = (!isDesign && row.step === currentStep && liveTotal !== null)
                                  ? liveTotal
                                  : totalGals(actualVal, row.clean_step);
                                // Variance: ratio-based — both values from realTimeChemData (single topic,
                                // single render, no flicker). Shows live pump delivery vs design loading.
                                const variance = (designVal != null && designVal !== 0 && actualVal != null)
                                  ? Math.abs((designVal - actualVal) / designVal) * 100
                                  : null;

                                const varColor = variance == null ? undefined
                                  : variance <= 5  ? '#16a34a'
                                  : variance <= 15 ? '#d97706'
                                  : '#dc2626';

                                // actualColor mirrors varColor — green/amber/red when variance exists, neutral otherwise
                                const actualColor = varColor ?? (isDarkTheme ? '#86efac' : '#166534');

                                const canEdit = !isDesign && hasChemEditLock && isRealTimeStreaming && isFutureOrCurrent;

                                const rawRatio = stepGroups.length > 1
                                  ? stepGroups.reduce((s, g) => s + g.ratio, 0)
                                  : stepGroups.length === 1
                                    ? stepGroups[0].ratio
                                    : (entry?.design ?? null);
                                const groupsChanged = editedGroups !== undefined &&
                                  JSON.stringify(editedGroups) !== JSON.stringify(stepGroups.map(g => ({ group: g.group, ratio: g.ratio })));
                                const singleChanged = !isMultiGroup &&
                                  editedVal !== undefined &&
                                  editedVal !== rawRatio;
                                const chemHasFuture = actualTreatments.some(t => t.step > row.step);
                                const showChemCascade = canEdit && chemHasFuture && (groupsChanged || singleChanged);

                                return (
                                  <React.Fragment key={name}>
                                    {/* Design Ratio — multi-group uses modal, single-group uses inline input */}
                                    <td style={{ ...cellBase, textAlign: 'center' }}>
                                      {canEdit ? (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                          {isMultiGroup ? (
                                            <span
                                              onClick={() => {
                                                // Every step has all groups — use stepGroups directly
                                                const groups = editedGroups
                                                  ?? stepGroups.map(g => ({ group: g.group, ratio: g.ratio }));
                                                setChemGroupModal({ step: row.step, chemName: name, groups });
                                              }}
                                              style={{ cursor: 'pointer' }}
                                              title="Click to edit group ratios"
                                            >
                                              {fmt(designVal)}
                                            </span>
                                          ) : (
                                            <input
                                              type="number"
                                              step="any"
                                              onMouseDown={e => { e.preventDefault(); (e.target as HTMLInputElement).focus(); }} onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === "a") e.preventDefault(); }}
                                              value={designVal ?? ''}
                                              onChange={e => {
                                                const v = parseFloat(e.target.value);
                                                if (!isNaN(v)) setEditedChemLoadings(prev => ({ ...prev, [cellKey]: v }));
                                              }}
                                              style={{
                                                width: '80px', textAlign: 'center',
                                                border: `1px solid ${isDarkTheme ? '#a16207' : '#ca8a04'}`,
                                                borderRadius: '3px', padding: '1px 4px', fontSize: '0.8rem',
                                                backgroundColor: isDarkTheme ? '#1a1a00' : '#fffbeb',
                                                color: isDarkTheme ? '#ffe066' : '#5a4000',
                                              }}
                                            />
                                          )}
                                          {showChemCascade && renderCascadeBtn(
                                            () => cascadeChemRatio(row.step, name),
                                            `Apply this ${name} ratio to step ${row.step} and all later steps`
                                          )}
                                        </span>
                                      ) : (
                                        fmt(designVal)
                                      )}
                                    </td>
                                    {!isDesign && (
                                      <td style={{ ...cellBase, textAlign: 'center', color: actualColor }}>
                                        {fmt(actualVal)}
                                      </td>
                                    )}
                                    {/* Total Design */}
                                    <td style={{ ...cellBase, textAlign: 'right' }}>
                                      {fmt(td)}
                                    </td>
                                    {!isDesign && (
                                      <>
                                        {/* Total Actual */}
                                        <td style={{ ...cellBase, textAlign: 'right', color: actualColor }}>
                                          {fmt(ta)}
                                        </td>
                                        {/* Variance */}
                                        <td style={{ ...cellBase, textAlign: 'center', color: varColor, fontWeight: variance != null ? 600 : undefined }}>
                                          {variance != null ? `${variance.toFixed(1)}%` : '—'}
                                        </td>
                                      </>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{ fontWeight: 700, backgroundColor: isDarkTheme ? '#2a2d30' : '#e9ecef' }}>
                          <td colSpan={2} style={{ padding: '5px 8px', textAlign: 'right', border: `1px solid ${isDarkTheme ? '#5C646B' : '#dee2e6'}`, color: isDarkTheme ? '#e5e5e5' : '#303335', backgroundColor: isDarkTheme ? '#2a2d30' : '#e9ecef' }}>Totals</td>
                          <td style={{ padding: '5px 8px', textAlign: 'right', border: `1px solid ${isDarkTheme ? '#5C646B' : '#dee2e6'}`, color: isDarkTheme ? '#e5e5e5' : '#303335', backgroundColor: isDarkTheme ? '#2a2d30' : '#e9ecef' }}>{Math.round(totalCleanGals).toLocaleString()}</td>
                          {effectiveChemNames.map(name => {
                            const sumDL = stepsData.reduce((a, s) => a + (s.chems[name]?.design ?? 0), 0);
                            const sumDG = stepsData.reduce((a, s) => a + (totalGals(s.chems[name]?.design ?? null, s.clean_step) ?? 0), 0);
                            const sumAL = stepsData.reduce((a, s) => {
                              const k = `${s.step}::${name}`;
                              return a + (editedChemLoadings[k] !== undefined ? editedChemLoadings[k] : (s.chems[name]?.actual ?? 0));
                            }, 0);
                            const sumAG = stepsData.reduce((a, s) => {
                              const k = `${s.step}::${name}`;
                              const v = editedChemLoadings[k] !== undefined ? editedChemLoadings[k] : (s.chems[name]?.actual ?? null);
                              return a + (totalGals(v, s.clean_step) ?? 0);
                            }, 0);
                            const totalVar = sumDL !== 0 ? Math.abs((sumDL - sumAL) / sumDL) * 100 : null;
                            const tvColor = totalVar == null ? undefined : totalVar <= 5 ? '#16a34a' : totalVar <= 15 ? '#d97706' : '#dc2626';
                            const ftBase: React.CSSProperties = { padding: '5px 8px', border: `1px solid ${isDarkTheme ? '#5C646B' : '#dee2e6'}`, backgroundColor: isDarkTheme ? '#2a2d30' : '#e9ecef', color: isDarkTheme ? '#e5e5e5' : '#303335' };
                            return (
                              <React.Fragment key={name}>
                                <td style={{ ...ftBase, textAlign: 'center' }}>{fmt(sumDL)}</td>
                                {!isDesign && <td style={{ ...ftBase, textAlign: 'center' }}>{fmt(sumAL)}</td>}
                                <td style={{ ...ftBase, textAlign: 'right' }}>{fmt(sumDG)}</td>
                                {!isDesign && (
                                  <>
                                    <td style={{ ...ftBase, textAlign: 'right' }}>{fmt(sumAG)}</td>
                                    <td style={{ ...ftBase, textAlign: 'center', color: tvColor, fontWeight: 600 }}>{totalVar != null ? `${totalVar.toFixed(1)}%` : '—'}</td>
                                  </>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tr>
                        <tr style={{ fontStyle: 'italic', backgroundColor: isDarkTheme ? '#2a2d30' : '#e9ecef' }}>
                          <td colSpan={2} style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700, border: `1px solid ${isDarkTheme ? '#5C646B' : '#dee2e6'}`, color: isDarkTheme ? '#e5e5e5' : '#303335', backgroundColor: isDarkTheme ? '#2a2d30' : '#e9ecef' }}>Avgs</td>
                          <td style={{ padding: '5px 8px', textAlign: 'right', border: `1px solid ${isDarkTheme ? '#5C646B' : '#dee2e6'}`, color: isDarkTheme ? '#e5e5e5' : '#303335', backgroundColor: isDarkTheme ? '#2a2d30' : '#e9ecef' }}>{Math.round(avgCleanGals).toLocaleString()}</td>
                          {effectiveChemNames.map(name => {
                            const n = stepsData.length || 1;
                            const avgDL = stepsData.reduce((a, s) => a + (s.chems[name]?.design ?? 0), 0) / n;
                            const avgDG = stepsData.reduce((a, s) => a + (totalGals(s.chems[name]?.design ?? null, s.clean_step) ?? 0), 0) / n;
                            const avgAL = stepsData.reduce((a, s) => {
                              const k = `${s.step}::${name}`;
                              return a + (editedChemLoadings[k] !== undefined ? editedChemLoadings[k] : (s.chems[name]?.actual ?? 0));
                            }, 0) / n;
                            const avgAG = stepsData.reduce((a, s) => {
                              const k = `${s.step}::${name}`;
                              const v = editedChemLoadings[k] !== undefined ? editedChemLoadings[k] : (s.chems[name]?.actual ?? null);
                              return a + (totalGals(v, s.clean_step) ?? 0);
                            }, 0) / n;
                            const avgVar = avgDL !== 0 ? Math.abs((avgDL - avgAL) / avgDL) * 100 : null;
                            const avColor = avgVar == null ? undefined : avgVar <= 5 ? '#16a34a' : avgVar <= 15 ? '#d97706' : '#dc2626';
                            const faBase: React.CSSProperties = { padding: '5px 8px', border: `1px solid ${isDarkTheme ? '#5C646B' : '#dee2e6'}`, backgroundColor: isDarkTheme ? '#2a2d30' : '#e9ecef', color: isDarkTheme ? '#e5e5e5' : '#303335' };
                            return (
                              <React.Fragment key={name}>
                                <td style={{ ...faBase, textAlign: 'center' }}>{fmt(avgDL)}</td>
                                {!isDesign && <td style={{ ...faBase, textAlign: 'center' }}>{fmt(avgAL)}</td>}
                                <td style={{ ...faBase, textAlign: 'right' }}>{fmt(avgDG)}</td>
                                {!isDesign && (
                                  <>
                                    <td style={{ ...faBase, textAlign: 'right' }}>{fmt(avgAG)}</td>
                                    <td style={{ ...faBase, textAlign: 'center', color: avColor, fontWeight: 600 }}>{avgVar != null ? `${avgVar.toFixed(1)}%` : '—'}</td>
                                  </>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      </tfoot>
                    </table>
                    </div>  {/* end maxHeight overflowY scroll */}
                  </div>  {/* end overflowX scroll */}
                </>
              );
            })()}
          </>
        )}
      </Modal.Body>
      {/* Context Menu */}
      {showContextMenu && (
        <div 
          ref={contextMenuRef}
          className={styles.contextMenu} 
          style={{
            position: 'fixed',
            top: contextMenuPosition.y,
            left: contextMenuPosition.x,
            backgroundColor: theme === 'dark' ? '#1a2234' : '#ffffff',
            color: theme === 'dark' ? '#e5e5e5' : '#333333',
            border: `1px solid ${theme === 'dark' ? '#3a4564' : '#e6e6e6'}`,
            zIndex: 9999
          }}
        >
          <div 
            className={styles.contextMenuItem}
            onClick={activeTab === 'proposal' ? handleProposalAddStep : handleAddStep}
          >
            Add
          </div>
          <div 
            className={styles.contextMenuItem}
            onClick={activeTab === 'proposal' ? handleProposalCopyStep : handleCopyStep}
          >
            Copy
          </div>
          <div 
            className={styles.contextMenuItem}
            onClick={activeTab === 'proposal' ? handleProposalDeleteStep : handleDeleteStep}
          >
            Delete
          </div>
          <div 
            className={styles.contextMenuItem}
            onClick={activeTab === 'proposal' ? handleProposalMoveUp : handleMoveUp}
          >
            Move Up
          </div>
          <div 
            className={styles.contextMenuItem}
            onClick={activeTab === 'proposal' ? handleProposalMoveDown : handleMoveDown}
          >
            Move Down
          </div>
        </div>
      )}
      
      <Modal.Footer style={{
        backgroundColor: theme === 'dark' ? '#303335' : '#ffffff', // Black for dark theme, white for light
        borderTop: `1px solid #5C646B` // Light Gray for borders
      }}>
        {/* Copy to Stages button - only show on Proposal tab */}
        {activeTab === 'proposal' && activeSubTab === 'treatment' && (
          <div className="d-flex gap-2" style={{ marginRight: 'auto' }}>
            <Button
              onClick={() => setShowCopyToStagesModal(true)}
              disabled={isProposalEditing}
              style={{
                backgroundColor: '#3E76FE',
                borderColor: '#3E76FE',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease-in-out'
              }}
            >
              Copy to Stages
            </Button>
          </div>
        )}
        {/* Proposal Edit/Save/Cancel — no MQTT lock */}
        {!isViewer && activeTab === 'proposal' && activeSubTab === 'treatment' && (
          <div style={{ marginRight: 'auto', marginLeft: '-0.5rem' }}>
            {!isProposalEditing ? (
              <Button
                onClick={() => setIsProposalEditing(true)}
                disabled={proposalLoading}
                style={{ backgroundColor: '#8CC540', borderColor: '#8CC540', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
              >
                Edit
              </Button>
            ) : (
              <div className="d-flex gap-2">
                <Button
                  onClick={handleSaveProposal}
                  disabled={isSavingProposal || !proposalDirty}
                  style={{ backgroundColor: '#8CC540', borderColor: '#8CC540', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                >
                  {isSavingProposal ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                      Saving
                    </>
                  ) : 'Save'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleCancelProposalEdit}
                  disabled={isSavingProposal}
                  style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        )}
        {!isViewer && activeTab === 'actual' && activeSubTab === 'treatment' && isRealTimeStreaming && (
          <div style={{ marginRight: 'auto' }}>
            {!hasEditLock ? (
              <Button 
                variant="primary" 
                onClick={requestEditLock}
                disabled={loading}
                  style={{
                    backgroundColor: '#8CC540', // Michael's blue
                    borderColor: '#8CC540',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease-in-out'
                  }}
              >
                Edit
              </Button>
            ) : (
              <div className="d-flex gap-2">
                <Button 
                  variant="success" 
                  onClick={handleSaveTreatmentSteps}
                  disabled={isSavingSchedule || (Object.keys(editedTreatmentSteps).length === 0 && !editedTreatmentSteps._hasChanges) || rateDropConstraints.hasViolation}
                  style={{
                    backgroundColor: '#8CC540', // Michael's eye-friendly green
                    borderColor: '#8CC540',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  {isSavingSchedule ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Waiting for field approval
                    </>
                  ) : (
                    'Save'
                  )}
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={releaseEditLock}
                  disabled={loading}
                  style={{
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        )}
        {/* Chem Actual Edit/Save/Cancel — only when Actual + Chemicals + streaming */}
        {!isViewer && activeTab === 'actual' && activeSubTab === 'chemicals' && isRealTimeStreaming && (
          <div style={{ marginRight: 'auto' }}>
            {!hasChemEditLock ? (
              <Button
                onClick={requestChemEditLock}
                disabled={isSavingChem}
                style={{ backgroundColor: '#8CC540', borderColor: '#8CC540', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
              >
                Edit
              </Button>
            ) : (
              <div className="d-flex gap-2">
                <Button
                  onClick={handleSaveChemSchedule}
                  disabled={isSavingChem || (Object.keys(editedChemLoadings).length === 0 && Object.keys(editedChemGroupRatios).length === 0)}
                  style={{ backgroundColor: '#8CC540', borderColor: '#8CC540', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                >
                  {isSavingChem ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                      Waiting for field approval
                    </>
                  ) : 'Save'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={releaseChemEditLock}
                  disabled={isSavingChem}
                  style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        )}
        <Button variant={theme === 'dark' ? 'outline-light' : 'outline-secondary'} onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>

      {/* Concentration Modal — shared by Actual (Sand Ramp) and Proposal (Sand Ramp) tabs */}
      <ConcentrationModal
        show={showConcentrationModal}
        onHide={handleConcentrationModalClose}
        onSave={handleConcentrationSave}
        initialStartConc={(() => {
          if (!concentrationTarget) return 0;
          const { source, treatment } = concentrationTarget;
          if (source === 'actual') {
            return editedTreatmentSteps[treatment.id]?.start_conc !== undefined
              ? editedTreatmentSteps[treatment.id].start_conc as number
              : (treatment as any).start_conc !== undefined
                ? parseFloat((treatment as any).start_conc)
                : treatment.conc;
          }
          return treatment.start_conc ?? treatment.conc ?? 0;
        })()}
        initialEndConc={(() => {
          if (!concentrationTarget) return 0;
          const { source, treatment } = concentrationTarget;
          if (source === 'actual') {
            return editedTreatmentSteps[treatment.id]?.end_conc !== undefined
              ? editedTreatmentSteps[treatment.id].end_conc as number
              : (treatment as any).end_conc !== undefined
                ? parseFloat((treatment as any).end_conc)
                : treatment.conc;
          }
          return treatment.end_conc ?? treatment.conc ?? 0;
        })()}
      />

      {/* Copy to Stages Modal */}
      <CopyToStagesModal
        show={showCopyToStagesModal}
        onHide={() => setShowCopyToStagesModal(false)}
        padId={wellData?.pad_id ?? null}
        sourceStageId={stageId}
        sourceStageNumber={stageNumber}
        useFakeData={useFakeData}
      />

      {/* Chemical Group Ratios Modal */}
      {chemGroupModal && (
        <Modal show centered onHide={() => setChemGroupModal(null)} size="sm">
          <Modal.Header closeButton style={{ backgroundColor: theme === 'dark' ? '#1e2124' : '#f8f9fa', borderBottom: `1px solid ${theme === 'dark' ? '#5C646B' : '#dee2e6'}` }}>
            <Modal.Title style={{ fontSize: '0.95rem', color: theme === 'dark' ? '#e5e5e5' : '#303335' }}>
              {chemGroupModal.chemName} — Step {chemGroupModal.step} Group Ratios
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: theme === 'dark' ? '#1e2124' : '#ffffff' }}>
            <p style={{ fontSize: '0.78rem', color: theme === 'dark' ? '#9ca3af' : '#6b7280', marginBottom: '12px' }}>
              Edit individual group ratios (gpt). Groups are defined by Petrix and are read-only.
            </p>
            {chemGroupModal.groups.map((g, i) => (
              <div key={g.group} className="d-flex align-items-center gap-2 mb-2">
                <span style={{ width: '90px', fontSize: '0.82rem', color: theme === 'dark' ? '#e5e5e5' : '#303335' }}>
                  {g.group ? g.group : '(No Group)'}
                </span>
                <input
                  type="number" step="any"
                  onMouseDown={e => { e.preventDefault(); (e.target as HTMLInputElement).focus(); }} onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === "a") e.preventDefault(); }}
                  value={g.ratio}
                  onChange={e => {
                    const v = parseFloat(e.target.value);
                    if (!isNaN(v)) setChemGroupModal({ ...chemGroupModal, groups: chemGroupModal.groups.map((x, j) => j === i ? { ...x, ratio: v } : x) });
                  }}
                  style={{
                    width: '80px', textAlign: 'center',
                    border: `1px solid ${theme === 'dark' ? '#a16207' : '#ca8a04'}`,
                    borderRadius: '3px', padding: '2px 4px', fontSize: '0.82rem',
                    backgroundColor: theme === 'dark' ? '#1a1a00' : '#fffbeb',
                    color: theme === 'dark' ? '#ffe066' : '#5a4000',
                  }}
                />
                <span style={{ fontSize: '0.78rem', color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>gpt</span>
              </div>
            ))}
            <hr style={{ borderColor: theme === 'dark' ? '#5C646B' : '#dee2e6', margin: '10px 0' }} />
            <div style={{ fontSize: '0.82rem', color: theme === 'dark' ? '#e5e5e5' : '#303335' }}>
              Total: <strong style={{ color: '#8CC540' }}>
                {chemGroupModal.groups.reduce((s, g) => s + g.ratio, 0).toFixed(3)} gpt
              </strong>
            </div>
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: theme === 'dark' ? '#1e2124' : '#f8f9fa', borderTop: `1px solid ${theme === 'dark' ? '#5C646B' : '#dee2e6'}` }}>
            <Button variant="secondary" size="sm" onClick={() => setChemGroupModal(null)}>Cancel</Button>
            <Button size="sm" style={{ backgroundColor: '#8CC540', borderColor: '#8CC540' }}
              onClick={() => {
                const k = `${chemGroupModal.step}::${chemGroupModal.chemName}`;
                setEditedChemGroupRatios(prev => ({ ...prev, [k]: chemGroupModal.groups }));
                setChemGroupModal(null);
              }}
            >Apply</Button>
          </Modal.Footer>
        </Modal>
      )}
    </Modal>
    </>
  );
};

export default TreatmentModal;